(function (global) {
  "use strict";

  const DEFAULT_CONFIG = {
    IFRAME_SRC: "https://d1h276491ac549.cloudfront.net/dist/index.html",
    API_BASE: "https://api-dev.siena.cx",
    SCRIPT_SRC: "https://d1h276491ac549.cloudfront.net/dist/webchat.js",
    SENTRY_DSN: "https://7231bb8615fdfa25a7efcbb25b80456c@o4504989123477504.ingest.us.sentry.io/4507543096131584",
    HONEYCOMB_API_KEY: "hcaik_01k1x2cfvjepd41hya92kv2xhtthxww4nheks08097prfyj21sw8g01dr3",
    HONEYCOMB_DATASET: "siena-live-chat-FE-dev",
    NODE_ENV: "development",
    VERSION: "1.0.0"
  };

  const isDevelopment = () => DEFAULT_CONFIG.NODE_ENV === "development";

  const BLOCKED_WIDGET_EVENTS = new Set([
    "widget.span.completed",
    "widget.constructed",
    "widget.init.started",
    "widget.init.skipped",
    "widget.settings.fetched",
    "widget.product_data.fetched",
    "widget.currency.fetched",
    "widget.webchat.created",
    "widget.search_bar.created",
    "widget.embedded.created",
    "widget.init.completed",
    "widget.api.request.started"
  ]);

  /**
   * Lightweight Honeycomb tracking for widget monitoring
   * Provides observability without impacting widget performance
   *
   * CRITICAL FIX v2.0.0: Consolidated message handlers to prevent random opening
   * - Fixed duplicate message listeners causing cross-contamination
   * - Consolidated message routing with flexible source handling
   * - Removed automatic widget opening behavior on mobile
   * - Improved mobile widget closing logic
   */
  class HoneycombWidgetTracker {
    constructor() {
      this.apiKey = DEFAULT_CONFIG.HONEYCOMB_API_KEY;
      this.dataset = DEFAULT_CONFIG.HONEYCOMB_DATASET || "siena-widget";
      this.serviceName = "siena-chat-widget";
      this.environment = DEFAULT_CONFIG.NODE_ENV || "production";
      this.initialized = false;
      this.clientId = this.getOrCreateClientId();
      this.connectionId = this.generateConnectionId();
      this.eventQueue = [];
      this.maxQueueSize = 50;

      // Session-based tracing
      this.currentTraceId = null;
      this.currentSessionId = null;
      this.sessionSpan = null;
      this.spanStack = [];

      this.init();
    }

    init() {
      try {
        // Only initialize if API key is available and valid
        if (
          !this.apiKey ||
          this.apiKey === "__HONEYCOMB_API_KEY__" ||
          this.apiKey === "undefined"
        ) {
          if (isDevelopment()) {
            console.warn(
              "[Widget Honeycomb] API key not configured, monitoring disabled"
            );
          }
          return;
        }

        this.initialized = true;
        this.processEventQueue();

        if (isDevelopment()) {
          console.log("[Widget Honeycomb] Monitoring initialized", {
            dataset: this.dataset,
            environment: this.environment,
            clientId: this.clientId
          });
        }
      } catch (error) {
        if (isDevelopment()) {
          console.warn("[Widget Honeycomb] Failed to initialize:", error);
        }
        // Graceful degradation - continue without monitoring
      }
    }

    getOrCreateClientId() {
      const storageKey = "siena_client_id";
      try {
        let clientId = localStorage.getItem(storageKey);
        if (!clientId || clientId === "null" || clientId === "undefined") {
          clientId = `client_${Date.now()}_${Math.random()
            .toString(36)
            .substring(2, 10)}`;
          localStorage.setItem(storageKey, clientId);
        }
        return clientId;
      } catch (error) {
        // Fallback if localStorage is not available
        return `client_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 8)}`;
      }
    }

    generateConnectionId() {
      return `conn_${this.clientId.split("_")[1] || Date.now()}_${Date.now()}`;
    }

    addEvent(eventType, data = {}) {
      try {
        if (BLOCKED_WIDGET_EVENTS.has(eventType)) {
          return;
        }

        // Add trace context using existing working fields
        const traceContext = {};
        // CRITICAL: Use unified trace_id as the primary trace_id for cross-frame sync
        // NOTE: this.getUnifiedTraceId() doesn't exist in Honeycomb class context - use data.trace_id instead
        const unifiedTraceId = data.trace_id; // Use the trace_id passed in the event data
        if (unifiedTraceId) {
          traceContext.traceId = unifiedTraceId; // Use traceId (not trace_id) for setTraceContext
          traceContext.trace_id = unifiedTraceId; // Keep both for backward compatibility
        }
        if (this.currentTraceId && this.currentTraceId !== unifiedTraceId) {
          traceContext.honeycomb_trace_id = this.currentTraceId; // Track honeycomb-specific trace_id separately
        }
        if (this.currentSessionId) {
          traceContext.session_id = this.currentSessionId;
          traceContext.sessionId = this.currentSessionId; // Use sessionId (not session_id) for setTraceContext
        }

        // Add current span context if available
        const currentSpan = this.spanStack[this.spanStack.length - 1];
        if (currentSpan) {
          traceContext.span_id = currentSpan.spanId;
          traceContext.parent_span_id = currentSpan.parentId;
        }

        const eventData = {
          ...data,
          event_type: eventType,
          client_id: this.clientId,
          connection_id: this.connectionId,
          timestamp: Date.now(),
          service_name: this.serviceName,
          environment: this.environment,
          user_agent: navigator.userAgent,
          page_url: window.location.href,
          page_hostname: window.location.hostname,
          widget_version: DEFAULT_CONFIG.VERSION,
          ...traceContext
        };

        if (!this.initialized) {
          this.queueEvent(eventData);
          return;
        }

        this.sendEvent(eventData);
      } catch (error) {
        if (isDevelopment()) {
          console.warn("[Widget Honeycomb] Failed to add event:", error);
        }
        // Silently fail to not impact widget functionality
      }
    }

    queueEvent(eventData) {
      if (this.eventQueue.length >= this.maxQueueSize) {
        this.eventQueue.shift(); // Remove oldest event
      }
      this.eventQueue.push(eventData);
    }

    processEventQueue() {
      while (this.eventQueue.length > 0) {
        const eventData = this.eventQueue.shift();
        this.sendEvent(eventData);
      }
    }

    async sendEvent(eventData) {
      try {
        if (!this.apiKey || !this.initialized) return;

        const response = await fetch(
          `https://api.honeycomb.io/1/events/${this.dataset}`,
          {
            method: "POST",
            headers: {
              "X-Honeycomb-Team": this.apiKey,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              ...eventData,
              samplerate: 1
            })
          }
        );

        if (isDevelopment() && !response.ok) {
          console.warn(
            "[Widget Honeycomb] Failed to send event:",
            response.status,
            response.statusText
          );
        }
      } catch (error) {
        if (isDevelopment()) {
          console.warn("[Widget Honeycomb] Failed to send event:", error);
        }
        // Silently fail to not impact widget functionality
      }
    }

    startSpan(operationName) {
      const spanId = `span_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 8)}`;
      const startTime = performance.now();

      // Get parent span context
      const parentSpan = this.spanStack[this.spanStack.length - 1];
      const traceId = this.currentTraceId || spanId; // Use current trace or create new one
      const parentId = parentSpan?.spanId || null;

      const span = {
        spanId,
        traceId,
        parentId,
        startTime,
        operationName,
        fields: {
          trace_id: traceId,
          span_id: spanId,
          parent_span_id: parentId,
          service_name: this.serviceName,
          environment: this.environment,
          operation_name: operationName,
          span_kind: "internal"
        },
        addField: function (key, value) {
          this.fields[key] = value;
        },
        finish: () => {
          const spanDuration = performance.now() - startTime;
          this.addEvent("widget.span.completed", {
            span_id: spanId,
            trace_id: traceId,
            parent_span_id: parentId,
            operation_name: operationName,
            duration_ms: spanDuration,
            session_id: this.currentSessionId,
            ...this.fields
          });

          // Remove from span stack when finished
          const index = this.spanStack.indexOf(span);
          if (index > -1) {
            this.spanStack.splice(index, 1);
          }
        }
      };

      // Add to span stack for parent-child relationships
      this.spanStack.push(span);

      return span;
    }

    // Session-based tracing methods
    startSessionTrace(sessionId, sessionData = {}) {
      if (!sessionId) return null;

      this.currentSessionId = sessionId;
      this.currentTraceId = sessionId; // Use session ID as trace ID

      // Close previous session trace if exists
      if (this.sessionSpan) {
        this.sessionSpan.finish();
      }

      // Create main session span
      this.sessionSpan = this.createSessionSpan(sessionId, sessionData);
      this.spanStack = [this.sessionSpan];

      return this.sessionSpan;
    }

    finishSessionTrace() {
      if (this.sessionSpan) {
        this.sessionSpan.addField("session_end_time", Date.now());
        this.sessionSpan.finish();
        this.sessionSpan = null;
      }

      this.currentTraceId = null;
      this.currentSessionId = null;
      this.spanStack = [];
    }

    // NEW METHOD: Update trace ID while maintaining trace continuity
    updateTraceId(newTraceId) {
      if (!newTraceId) return;

      const oldTraceId = this.currentTraceId;
      this.currentTraceId = newTraceId;
      this.currentSessionId = newTraceId; // Assume session_id is the trace_id

      // Create a trace bridge span to link old trace to new trace
      if (oldTraceId && oldTraceId !== newTraceId) {
        // Start bridge span in OLD trace
        const bridgeSpan = this.startSpan("session.trace_bridge", {
          operation_name: "session_trace_bridge",
          span_kind: "internal",
          trace_id: oldTraceId, // Bridge span belongs to old trace
          session_id: oldTraceId,
          new_trace_id: newTraceId,
          old_trace_id: oldTraceId,
          bridge_reason: "session_expiry_renewal",
          bridge_type: "trace_continuity",
          timestamp: Date.now()
        });

        // Immediately finish the bridge span in old trace
        bridgeSpan.finish();

        // Create corresponding bridge event in NEW trace
        this.addEvent("session.trace_continued", {
          trace_id: newTraceId,
          session_id: newTraceId,
          previous_trace_id: oldTraceId,
          continuation_reason: "session_expiry_renewal",
          bridge_type: "trace_continuity",
          trace_bridge_created: true,
          timestamp: Date.now()
        });
      }

      // Update the session span with new trace ID
      if (this.sessionSpan) {
        this.sessionSpan.addField("trace_id", newTraceId);
        this.sessionSpan.addField("session_id", newTraceId);
        this.sessionSpan.addField("trace_updated", true);
        this.sessionSpan.addField("previous_trace_id", oldTraceId);
        this.sessionSpan.addField("has_trace_bridge", !!oldTraceId);
      }

      // Update all active spans with new trace ID
      this.spanStack.forEach((span) => {
        if (span && span.addField) {
          span.addField("trace_id", newTraceId);
          span.addField("session_id", newTraceId);
          span.addField("trace_updated_from", oldTraceId);
        }
      });

      if (isDevelopment()) {
        console.log(
          `[HoneycombWidgetTracker] Created trace bridge from ${oldTraceId} to ${newTraceId}`
        );
      }
    }

    // Get trace context headers for client communication
    getTraceHeaders() {
      const honeycombTracker = globalHoneycombTracker;
      if (!honeycombTracker.currentTraceId) return {};

      const currentSpan =
        honeycombTracker.spanStack[honeycombTracker.spanStack.length - 1];
      return {
        "X-Trace-Id": honeycombTracker.currentTraceId,
        "X-Session-Id": honeycombTracker.currentSessionId,
        "X-Span-Id": currentSpan?.spanId || honeycombTracker.currentTraceId,
        "X-Parent-Id": currentSpan?.parentId || null
      };
    }

    // Create trace context for iframe communication
    getTraceContext() {
      const honeycombTracker = globalHoneycombTracker;
      return {
        traceId: honeycombTracker.currentTraceId,
        sessionId: honeycombTracker.currentSessionId,
        spanId:
          honeycombTracker.spanStack[honeycombTracker.spanStack.length - 1]
            ?.spanId,
        parentId:
          honeycombTracker.spanStack[honeycombTracker.spanStack.length - 1]
            ?.parentId
      };
    }

    createSessionSpan(sessionId, sessionData) {
      const spanId = sessionId; // Use session ID as root span ID
      const startTime = performance.now();
      const honeycombTracker = globalHoneycombTracker; // Reference to the global tracker

      return {
        spanId: spanId,
        traceId: sessionId,
        parentId: null, // Root span has no parent
        startTime,
        fields: {
          trace_id: sessionId,
          span_id: spanId,
          service_name: honeycombTracker.serviceName,
          environment: honeycombTracker.environment,
          session_id: sessionId,
          span_kind: "server",
          operation_name: "session_lifecycle",
          ...sessionData
        },
        addField: function (key, value) {
          this.fields[key] = value;
        },
        finish: () => {
          const duration = performance.now() - startTime;
          honeycombTracker.addEvent("session.trace.completed", {
            trace_id: sessionId,
            span_id: spanId,
            duration_ms: duration,
            operation_name: "session_lifecycle",
            ...this.fields
          });
        }
      };
    }
  }

  // Initialize Honeycomb tracker globally as early as possible
  const globalHoneycombTracker = new HoneycombWidgetTracker();
  window.SienaWidgetHoneycomb = globalHoneycombTracker;

  // Track if initialization is in progress to prevent duplicate calls (declared early to avoid TDZ)
  let isInitializing = false;

  // START SINGLE MASTER TRACE that will be continuous throughout entire session
  // This trace ID will be updated to session_id when available, but the trace continues
  const MASTER_TRACE_ID = `session_${Date.now()}_${Math.random()
    .toString(36)
    .substr(2, 8)}`;
  globalHoneycombTracker.startSessionTrace(MASTER_TRACE_ID, {
    stage: "script_loading",
    page_url: window.location.href,
    page_hostname: window.location.hostname,
    is_master_trace: true,
    trace_start_source: "script_load"
  });

  // Track script loading as FIRST event in the master trace
  // globalHoneycombTracker.addEvent("script.loaded", {
  //   version: DEFAULT_CONFIG.VERSION,
  //   environment: DEFAULT_CONFIG.NODE_ENV,
  //   timestamp: Date.now(),
  //   user_agent: navigator.userAgent,
  //   page_url: window.location.href,
  //   page_hostname: window.location.hostname,
  //   trace_stage: "script_loaded"
  // });

  // Shared cache for cross-surface coordination (PDP starters, future widgets, etc).
  // Idempotent — safe to evaluate multiple times when the script is loaded twice.
  //
  // `widgetInstance` is the single SienaAIChat for the page. Merchants regularly
  // end up with several copies of this script (theme.liquid + Shopify ScriptTag +
  // app embed), and each copy has its own closure, so a module-level variable
  // cannot dedupe them: the iframe is only created after the async settings
  // fetch, every copy passed the "iframe exists?" check, and each ran its own
  // proactive-campaign timers (5 embeds → 5 "Say cheese" popups). Keeping the
  // instance here lets later copies bail out yet still serve the window API.
  window.__siena = window.__siena || {
    starters: { byUrl: {}, listeners: [], inflight: {} },
    pendingTaps: [],
    widgetInstance: null
  };

  // EAA-2902 follow-up: starters must only appear on product detail pages.
  // The backend can't yet gate non-product URLs — JS-rendered pages the crawler
  // can't read produce low-quality AI questions — so we block them on the
  // client by URL until server-side content fetching lands. Mirrors the widget
  // helper isProductPageUrl() and the instance method isShopifyProductPage().
  function siena_isProductPageUrl(url) {
    try {
      return new URL(url, window.location.href).pathname.indexOf("/products/") !== -1;
    } catch (_) {
      return typeof url === "string" && url.indexOf("/products/") !== -1;
    }
  }

  /**
   * ECH-1244: a merchant can pin a product on any page (typically a landing
   * page) so the PDP widget and the starters carousel work outside
   * `/products/` URLs. Supported hooks, first match wins:
   *   <div id="siena-embedded" data-product-handle="gruns-kids">
   *   <div id="siena-starters-carousel" data-product-handle="gruns-kids">
   *   <siena-starters-carousel product-slug="gruns-kids">
   * `data-product-handle` / `product-slug` accept a bare handle or a product
   * URL / path. `data-product-url` (any of the three) additionally fixes the
   * exact PDP URL starters are keyed by, for storefronts whose indexed domain
   * differs from the page origin.
   */
  var SIENA_PINNED_PRODUCT_SELECTOR = [
    "#siena-embedded[data-product-handle]",
    "#siena-embedded[data-product-url]",
    "#siena-starters-carousel[data-product-handle]",
    "#siena-starters-carousel[data-product-url]",
    "siena-starters-carousel[product-slug]",
    "siena-starters-carousel[data-product-handle]",
    "siena-starters-carousel[data-product-url]"
  ].join(", ");

  function siena_findPinnedProductElement() {
    try {
      return document.querySelector(SIENA_PINNED_PRODUCT_SELECTOR);
    } catch (_) {
      return null;
    }
  }

  /** Accepts "gruns-kids", "/products/gruns-kids", or a full PDP URL. */
  function siena_normalizeProductHandle(raw) {
    var value = String(raw || "").trim();
    if (!value) return null;
    var match = value.match(/\/products\/([^/?#]+)/);
    if (match) return match[1];
    value = value.replace(/^\/+|\/+$/g, "");
    return value || null;
  }

  /** Handle of the product pinned on the page, or null when nothing is pinned. */
  function siena_getPinnedProductHandle() {
    var el = siena_findPinnedProductElement();
    if (!el) return null;
    return siena_normalizeProductHandle(
      el.getAttribute("data-product-handle") ||
        el.getAttribute("product-slug") ||
        el.getAttribute("data-product-url")
    );
  }

  /**
   * Canonical PDP URL starters are keyed by when a product is pinned: the
   * explicit `data-product-url` if given, else `<origin>/products/<handle>`.
   */
  function siena_getPinnedProductUrl() {
    var el = siena_findPinnedProductElement();
    if (!el) return null;
    var explicit = (el.getAttribute("data-product-url") || "").trim();
    if (explicit) {
      try {
        return new URL(explicit, window.location.href).href;
      } catch (_) {
        // fall through to the handle-based URL
      }
    }
    var handle = siena_getPinnedProductHandle();
    return handle ? window.location.origin + "/products/" + handle : null;
  }

  /**
   * ECH-1244: true when the merchant deliberately placed a starters surface on
   * this page (embedded box, carousel div or <siena-starters-carousel>).
   * Placement is the opt-in for non-product pages: the backend generates
   * starters from any indexed page's content (EAA-2902), so landing pages and
   * collections get their own questions. Pages without a surface (e.g. the
   * homepage with only the bubble) keep the EAA-2902 product-page gate, since
   * the backend still can't tell readable pages from JS-rendered ones.
   */
  function siena_pageHasStartersSurface() {
    try {
      return !!document.querySelector(
        "#siena-embedded, #siena-starters-carousel, siena-starters-carousel"
      );
    } catch (_) {
      return false;
    }
  }

  /** Where starters may render: product URL, pinned product, or an opted-in page. */
  function siena_startersAllowedForUrl(url) {
    return (
      siena_isProductPageUrl(url) ||
      !!siena_getPinnedProductHandle() ||
      siena_pageHasStartersSurface()
    );
  }

  // EAA-2902: fetch pre-generated starters for a given page URL, deduping
  // concurrent calls and broadcasting to in-process listeners + the widget
  // iframe via postMessage. The backend canonicalizes the URL and resolves the
  // pgq_url_mapping row, so starters are keyed per page (product/collection/page).
  function siena_fetchStarters(url) {
    // EAA-2902 follow-up / ECH-1244: gated pages get an empty result. Tell
    // listeners too, so a <siena-starters-carousel> hides instead of showing
    // its loading skeleton forever.
    if (!siena_startersAllowedForUrl(url)) {
      window.__siena.starters.listeners.forEach(function (cb) {
        try {
          cb({ url: url, starters: [] });
        } catch (_) {}
      });
      return Promise.resolve([]);
    }
    if (window.__siena.starters.byUrl[url]) {
      return Promise.resolve(window.__siena.starters.byUrl[url]);
    }
    if (window.__siena.starters.inflight[url]) {
      return window.__siena.starters.inflight[url];
    }

    var apiBase = DEFAULT_CONFIG.API_BASE;
    var auth = window.__siena.auth || {};
    var headers = { "Content-Type": "application/json" };
    if (auth.organizationUuid) headers["x-siena-organization-uuid"] = auth.organizationUuid;
    if (auth.appKey) {
      headers["x-siena-app-key"] = auth.appKey;
      headers["x-siena-livechat-app-key"] = auth.appKey;
    }

    var promise = fetch(apiBase + "/v1/live_chat/pre_generated_question", {
      method: "POST",
      headers: headers,
      body: JSON.stringify({ url: url })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var starters = Array.isArray(data && data.data) ? data.data : [];
        window.__siena.starters.byUrl[url] = starters;
        window.__siena.starters.listeners.forEach(function (cb) {
          try {
            cb({ url: url, starters: starters });
          } catch (_) {}
        });
        var iframe = document.querySelector("iframe.siena-iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              action: "STARTERS_LOADED",
              url: url,
              starters: starters
            },
            "*"
          );
        }
        return starters;
      })
      .catch(function (err) {
        console.warn("[siena] starters fetch failed", err);
        return [];
      })
      .then(function (result) {
        delete window.__siena.starters.inflight[url];
        return result;
      });

    window.__siena.starters.inflight[url] = promise;
    return promise;
  }

  window.__siena.fetchStarters = siena_fetchStarters;

  // Open the chat widget (if not already open) and send a starter question.
  // Carousel pills call this when tapped; if the script hasn't initialized yet,
  // pendingTaps queues the call and we drain it once openWidgetAndSend is wired up.
  function siena_openWidgetAndSend(payload) {
    if (!payload) return;

    function send() {
      var ifr = document.getElementById("SIENA_CHAT_IFRAME");
      if (!ifr) ifr = document.querySelector("iframe.siena-iframe");
      if (!ifr || !ifr.contentWindow) {
        window.setTimeout(send, 100);
        return;
      }
      // First open the widget...
      ifr.contentWindow.postMessage({ action: "openWidget" }, "*");
      // ...then dispatch the starter question. Widget's "sendMessage" handler
      // expects { message, pre_generated_question_uuid }.
      ifr.contentWindow.postMessage(
        {
          action: "sendMessage",
          message: payload.text,
          pre_generated_question_uuid: payload.pre_generated_question_uuid,
          // ECH-1343: headless storefronts navigate without a reload.
          current_page_url: window.location.href
        },
        "*"
      );
    }
    // Small delay so the iframe is mounted on first paint after the script loads.
    window.setTimeout(send, 200);
  }

  window.__siena.openWidgetAndSend = siena_openWidgetAndSend;

  // Drain any taps that landed in the queue before openWidgetAndSend was wired.
  if (Array.isArray(window.__siena.pendingTaps)) {
    var queuedTaps = window.__siena.pendingTaps.splice(0);
    queuedTaps.forEach(siena_openWidgetAndSend);
  }

  // <siena-starters-carousel> custom element.
  // IMPORTANT: keep in sync with packages/live-chat/src/components/siena-starters-carousel.ts
  // (canonical TS source). Inlined here because siena-chat-web.js is a standalone
  // script copied verbatim into dist/ and is not bundled with the React app.
  // Verbatim copy of the .question-btn pill from packages/live-chat/public/embedded-search.html.
  // EFE-1196: colors come from --siena-starters-* custom properties. The light
  // values below are today's hardcoded palette; the dark block mirrors the
  // widget's .dark tokens (packages/live-chat/src/index.css) so the carousel
  // follows the Live Chat "Theme" setting. The host font is set inline by the
  // element from the Live Chat "Font" setting (see _applyTheme).
  var SIENA_CAROUSEL_STYLES =
    "@import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,100..900;1,100..900&display=swap');" +
    ":host { display: block; width: 100%; max-width: 100%; overflow: hidden;" +
    '  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",' +
    '    Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;' +
    "  --siena-starters-text: #24242b;" +
    "  --siena-starters-border: rgba(36, 36, 43, 0.12);" +
    "  --siena-starters-chip-bg: #ffffff;" +
    "  --siena-starters-chip-hover-bg: #f5f5f5;" +
    "  --siena-starters-skeleton-a: #f0f0f0;" +
    "  --siena-starters-skeleton-b: #f8f8f8; }" +
    ':host([data-siena-theme="dark"]) {' +
    "  --siena-starters-text: #DDDCDB;" +
    "  --siena-starters-border: #44403C;" +
    "  --siena-starters-chip-bg: #302D2A;" +
    "  --siena-starters-chip-hover-bg: #44403C;" +
    "  --siena-starters-skeleton-a: #302D2A;" +
    "  --siena-starters-skeleton-b: #44403C; }" +
    ".carousel { display: flex; flex-wrap: nowrap; gap: 8px;" +
    "  overflow-x: auto; overflow-y: hidden;" +
    "  padding: 6px 0 8px; width: 100%; max-width: 100%;" +
    "  -webkit-overflow-scrolling: touch;" +
    "  scrollbar-width: none; -ms-overflow-style: none;" +
    "  --fade-w: 28px;" +
    "  --fade-left: transparent;" +
    "  --fade-right: transparent;" +
    "  mask-image: linear-gradient(to right, var(--fade-left) 0," +
    "    #000 var(--fade-w), #000 calc(100% - var(--fade-w))," +
    "    var(--fade-right) 100%);" +
    "  -webkit-mask-image: linear-gradient(to right, var(--fade-left) 0," +
    "    #000 var(--fade-w), #000 calc(100% - var(--fade-w))," +
    "    var(--fade-right) 100%); }" +
    ".carousel.fade-left { --fade-left: transparent; }" +
    ".carousel.fade-right { --fade-right: transparent; }" +
    ".carousel:not(.fade-left) { --fade-left: #000; }" +
    ".carousel:not(.fade-right) { --fade-right: #000; }" +
    ".carousel::-webkit-scrollbar { display: none; width: 0; height: 0; }" +
    ".question-btn { display: flex; padding: 8px;" +
    "  justify-content: center; align-items: center; gap: 4px;" +
    "  border-radius: 20px; border: 1px solid var(--siena-starters-border);" +
    "  background-color: var(--siena-starters-chip-bg);" +
    "  color: var(--siena-starters-text); cursor: pointer;" +
    "  transition: all 0.2s ease; flex: 0 0 auto; font-family: inherit; }" +
    ".question-btn:hover { background-color: var(--siena-starters-chip-hover-bg);" +
    "  transform: translateY(-2px);" +
    "  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); }" +
    ".question-btn svg { flex-shrink: 0; }" +
    ".question-text { color: var(--siena-starters-text); font-size: 14px;" +
    "  font-style: normal; font-weight: 450; line-height: 135%;" +
    "  text-align: left; max-width: 280px; overflow: hidden;" +
    "  text-overflow: ellipsis; white-space: nowrap; }" +
    ".skeleton-btn { flex: 0 0 auto; height: 36px; width: 180px;" +
    "  border-radius: 20px; border: 1px solid var(--siena-starters-border);" +
    "  background: linear-gradient(90deg, var(--siena-starters-skeleton-a) 25%," +
    "    var(--siena-starters-skeleton-b) 50%, var(--siena-starters-skeleton-a) 75%);" +
    "  background-size: 200% 100%; animation: shimmer 1.5s infinite linear; }" +
    "@keyframes shimmer { 0% { background-position: -200% 0; }" +
    "  100% { background-position: 200% 0; } }";

  // Sparkles SVG copied verbatim from packages/live-chat/public/embedded-search.html.
  var SIENA_SPARKLES_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">' +
    '<path d="M7.35002 10.8686L6.11802 10.3813L5.63135 9.14928C5.48002 8.76795 4.85268 8.76795 4.70135 9.14928L4.21468 10.3813L2.98268 10.8686C2.79202 10.9439 2.66602 11.1286 2.66602 11.3333C2.66602 11.5379 2.79135 11.7226 2.98268 11.7979L4.21468 12.2853L4.70135 13.5173C4.77668 13.7079 4.96135 13.8333 5.16602 13.8333C5.37068 13.8333 5.55535 13.7079 5.63068 13.5173L6.11735 12.2853L7.34935 11.7979C7.54002 11.7226 7.66602 11.5379 7.66602 11.3333C7.66602 11.1286 7.54135 10.9439 7.35002 10.8686Z" fill="currentColor"/>' +
    '<path d="M13.0167 6.53527L10.83 5.6706L9.96468 3.48327C9.81335 3.10193 9.18602 3.10193 9.03468 3.48327L8.16935 5.6706L5.98268 6.53527C5.79202 6.6106 5.66602 6.79527 5.66602 6.99993C5.66602 7.2046 5.79135 7.38927 5.98268 7.4646L8.16935 8.32927L9.03468 10.5166C9.11002 10.7073 9.29468 10.8326 9.49935 10.8326C9.70402 10.8326 9.88868 10.7073 9.96402 10.5166L10.8293 8.32927L13.016 7.4646C13.2067 7.38927 13.3327 7.2046 13.3327 6.99993C13.3327 6.79527 13.208 6.6106 13.0167 6.53527Z" fill="currentColor"/>' +
    '</svg>';

  function siena_escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /** Trim, drop empties, and coerce fixed starters to a clean string[]. */
  function siena_normalizeFixedStarters(list) {
    if (!Array.isArray(list)) return [];
    return list
      .map(function (s) {
        return typeof s === "string" ? s.trim() : "";
      })
      .filter(Boolean);
  }

  /** EAA-3522: response items sourced "fixed:*" are server-resolved pinned starters. */
  function siena_isPinnedStarter(s) {
    return !!s && typeof s.source === "string" && s.source.indexOf("fixed") === 0;
  }

  /**
   * EAA-3204: merge merchant-authored fixed starters ahead of the AI-generated
   * questions. Fixed starters have no uuid (sent as a regular message on click)
   * and render without the AI sparkle icon. AI questions whose text duplicates a
   * fixed starter are dropped so the same chip never appears twice.
   */
  function siena_mergeFixedStarters(fixedStrings, aiStarters) {
    // EAA-3522: pinned starters in the response replace the settings list.
    var all = Array.isArray(aiStarters) ? aiStarters : [];
    var pinned = all.filter(siena_isPinnedStarter).map(function (s) {
      return s.question;
    });
    if (pinned.length) fixedStrings = pinned;
    aiStarters = all.filter(function (s) {
      return !siena_isPinnedStarter(s);
    });
    var fixed = siena_normalizeFixedStarters(fixedStrings);
    var seen = {};
    fixed.forEach(function (q) {
      seen[q.toLowerCase()] = true;
    });
    var fixedPills = fixed.map(function (q) {
      return { question: q, uuid: "" };
    });
    var aiPills = (Array.isArray(aiStarters) ? aiStarters : []).filter(function (
      a
    ) {
      return (
        a &&
        a.question &&
        !seen[String(a.question).trim().toLowerCase()]
      );
    });
    return fixedPills.concat(aiPills);
  }

  /** EFE-1196: the font every starters surface used before theming landed. */
  var SIENA_STARTERS_DEFAULT_FONT = "Inter";
  var SIENA_STARTERS_FONT_FALLBACKS =
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,' +
    ' Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif';
  /** Same weights the widget requests in useGoogleFont.ts. */
  var SIENA_STARTERS_FONT_WEIGHTS = "400;500;600";

  /**
   * EFE-1196: read the Live Chat brand styling off the /v1/live_chat/settings
   * payload into the theme the starters surfaces (PDP widget, carousel iframe,
   * <siena-starters-carousel>) apply. Mirrors what the widget itself uses:
   * `dark_mode` picks the light/dark token palette, `font_selected` the Google
   * font, `brand_color` the accent (send button). Defaults reproduce today's
   * look exactly, so orgs that never customised Live Chat see no change.
   *
   * IMPORTANT: keep in sync with normalizeStartersTheme() in
   * src/components/siena-starters-carousel.ts.
   */
  function siena_normalizeStartersTheme(settings) {
    var font =
      settings && typeof settings.font_selected === "string"
        ? settings.font_selected.trim()
        : "";
    var brand =
      settings && typeof settings.brand_color === "string"
        ? settings.brand_color.trim()
        : "";
    return {
      dark: !!(settings && settings.dark_mode === true),
      fontFamily: font && font !== SIENA_STARTERS_DEFAULT_FONT ? font : null,
      brandColor: brand || null
    };
  }

  /** EFE-1196: CSS font-family stack for a theme (selected font first). */
  function siena_startersFontStack(fontFamily) {
    if (!fontFamily) return SIENA_STARTERS_FONT_FALLBACKS;
    return '"' + fontFamily.replace(/"/g, "") + '", ' + SIENA_STARTERS_FONT_FALLBACKS;
  }

  /**
   * EFE-1196: load a Google font into the host document once. Fonts declared
   * in the document apply inside open shadow roots, so this also covers the
   * <siena-starters-carousel> element. No-op for the default (Inter) font.
   */
  function siena_ensureStartersFont(fontFamily) {
    if (!fontFamily || fontFamily === SIENA_STARTERS_DEFAULT_FONT) return;
    try {
      if (document.querySelector('link[data-siena-font="' + fontFamily + '"]')) {
        return;
      }
      var link = document.createElement("link");
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=" +
        encodeURIComponent(fontFamily).replace(/%20/g, "+") +
        ":wght@" +
        SIENA_STARTERS_FONT_WEIGHTS +
        "&display=swap";
      link.setAttribute("data-siena-font", fontFamily);
      document.head.appendChild(link);
    } catch (e) {}
  }

  // EAA-3440: autoscroll for the starters carousel. Speed is px per second.
  var SIENA_STARTERS_AUTOSCROLL_DEFAULT_SPEED = 30;
  var SIENA_STARTERS_AUTOSCROLL_MIN_SPEED = 10;
  var SIENA_STARTERS_AUTOSCROLL_MAX_SPEED = 120;
  /** Idle time after the shopper wheels, drags or taps before autoscroll resumes. */
  var SIENA_STARTERS_AUTOSCROLL_RESUME_MS = 2000;

  /** EAA-3730: merchant max starters for the carousel (1..6); null = no cap. */
  function siena_normalizeMaxStarters(settings) {
    var max = settings && settings.max_starters_carousel;
    return typeof max === "number" && max >= 1 && Math.floor(max) === max
      ? max
      : null;
  }

  /**
   * EAA-3440: read the carousel autoscroll settings off the
   * /v1/live_chat/settings payload into { enabled, speed } (px per second).
   * A missing or out-of-range speed falls back to / clamps to the defaults.
   */
  function siena_normalizeStartersAutoscroll(settings) {
    var enabled = !!(
      settings && settings.starters_carousel_autoscroll_enabled === true
    );
    var raw = Number(settings && settings.starters_carousel_autoscroll_speed);
    var speed =
      isFinite(raw) && raw > 0 ? raw : SIENA_STARTERS_AUTOSCROLL_DEFAULT_SPEED;
    speed = Math.min(
      SIENA_STARTERS_AUTOSCROLL_MAX_SPEED,
      Math.max(SIENA_STARTERS_AUTOSCROLL_MIN_SPEED, speed)
    );
    return { enabled: enabled, speed: speed };
  }

  /**
   * EAA-3440: continuously scroll a starters carousel, marquee style.
   *
   * - Runs only while the pills overflow the container. Pills are cloned once
   *   (aria-hidden) so the loop wraps seamlessly instead of snapping back.
   * - Pauses while the shopper hovers with a mouse, touches, focuses or scrolls
   *   the carousel and resumes SIENA_STARTERS_AUTOSCROLL_RESUME_MS after the
   *   last interaction.
   * - Honours prefers-reduced-motion, skips RTL layouts, and idles while the
   *   tab is hidden or the carousel is off-screen.
   *
   * `bindClone(button)` wires click handling on each cloned pill. Returns a
   * stop() function — call it before re-rendering the carousel.
   *
   * IMPORTANT: keep in sync with the copies in
   * src/components/siena-starters-carousel.ts and public/starters-carousel.html.
   */
  function siena_startStartersAutoscroll(carouselEl, autoscroll, bindClone) {
    var noop = function () {};
    if (!carouselEl || !autoscroll || !autoscroll.enabled) return noop;
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return noop;
    }
    if (window.getComputedStyle(carouselEl).direction === "rtl") return noop;
    var pills = Array.prototype.slice.call(carouselEl.children);
    if (pills.length === 0) return noop;

    var speed = autoscroll.speed || SIENA_STARTERS_AUTOSCROLL_DEFAULT_SPEED;
    var stopped = false;
    var rafId = null;
    var lastTs = null;
    // Fractional scroll position we own — scrollLeft may round it.
    var pos = carouselEl.scrollLeft;
    // Distance from an original pill to its clone; 0 = nothing to scroll.
    var loopWidth = 0;
    var clones = [];
    var hoverPaused = false;
    var touchPaused = false;
    var focusPaused = false;
    var inView = true;
    var resumeAt = 0;

    function now() {
      return typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();
    }

    function originalsWidth() {
      var first = pills[0].getBoundingClientRect();
      var last = pills[pills.length - 1].getBoundingClientRect();
      return last.right - first.left;
    }

    function addClones() {
      if (clones.length) return;
      pills.forEach(function (pill) {
        var clone = pill.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("tabindex", "-1");
        clone.setAttribute("data-siena-clone", "true");
        if (typeof bindClone === "function") bindClone(clone);
        carouselEl.appendChild(clone);
        clones.push(clone);
      });
    }

    function removeClones() {
      clones.forEach(function (clone) {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      });
      clones = [];
      loopWidth = 0;
    }

    // Re-measure after render/resize: clone when overflowing, unclone otherwise.
    function refresh() {
      if (stopped) return;
      if (originalsWidth() > carouselEl.clientWidth + 1) {
        addClones();
        loopWidth =
          clones[0].getBoundingClientRect().left -
          pills[0].getBoundingClientRect().left;
        schedule();
      } else if (clones.length) {
        removeClones();
        pos = 0;
        carouselEl.scrollLeft = 0;
      }
    }

    function isPaused(ts) {
      return (
        hoverPaused ||
        touchPaused ||
        focusPaused ||
        !inView ||
        document.hidden ||
        ts < resumeAt
      );
    }

    function frame(ts) {
      rafId = null;
      if (stopped) return;
      if (!inView || document.hidden) {
        // Idle until the observers wake us up again.
        lastTs = null;
        return;
      }
      if (lastTs == null) lastTs = ts;
      var dt = Math.min(ts - lastTs, 100) / 1000;
      lastTs = ts;
      if (loopWidth > 0 && !isPaused(ts)) {
        pos += speed * dt;
        while (pos >= loopWidth) pos -= loopWidth;
        carouselEl.scrollLeft = pos;
      }
      rafId = requestAnimationFrame(frame);
    }

    function schedule() {
      if (stopped || rafId != null) return;
      lastTs = null;
      rafId = requestAnimationFrame(frame);
    }

    function holdOff() {
      resumeAt = now() + SIENA_STARTERS_AUTOSCROLL_RESUME_MS;
    }

    function onScroll() {
      // A scroll we did not produce: adopt the shopper's position and wait.
      if (Math.abs(carouselEl.scrollLeft - pos) > 1.5) {
        pos = carouselEl.scrollLeft;
        holdOff();
      }
    }
    function isMouse(e) {
      return !e.pointerType || e.pointerType === "mouse";
    }
    function onPointerEnter(e) {
      if (isMouse(e)) hoverPaused = true;
    }
    function onPointerLeave(e) {
      if (isMouse(e)) hoverPaused = false;
    }
    function onTouchStart() {
      touchPaused = true;
    }
    function onTouchEnd() {
      touchPaused = false;
      holdOff();
    }
    function onFocusIn() {
      focusPaused = true;
    }
    function onFocusOut() {
      focusPaused = false;
    }
    function onVisibility() {
      if (!document.hidden) schedule();
    }

    var hasPointer = typeof window.PointerEvent !== "undefined";
    var enterEvent = hasPointer ? "pointerenter" : "mouseenter";
    var leaveEvent = hasPointer ? "pointerleave" : "mouseleave";
    var passive = { passive: true };
    carouselEl.addEventListener("scroll", onScroll, passive);
    carouselEl.addEventListener(enterEvent, onPointerEnter);
    carouselEl.addEventListener(leaveEvent, onPointerLeave);
    carouselEl.addEventListener("touchstart", onTouchStart, passive);
    carouselEl.addEventListener("touchend", onTouchEnd, passive);
    carouselEl.addEventListener("touchcancel", onTouchEnd, passive);
    carouselEl.addEventListener("wheel", holdOff, passive);
    carouselEl.addEventListener("focusin", onFocusIn);
    carouselEl.addEventListener("focusout", onFocusOut);
    document.addEventListener("visibilitychange", onVisibility);

    var resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(refresh);
      resizeObserver.observe(carouselEl);
    } else {
      window.addEventListener("resize", refresh);
    }
    var intersectionObserver = null;
    if (typeof IntersectionObserver !== "undefined") {
      intersectionObserver = new IntersectionObserver(function (entries) {
        inView = entries.some(function (entry) {
          return entry.isIntersecting;
        });
        if (inView) schedule();
      });
      intersectionObserver.observe(carouselEl);
    }

    requestAnimationFrame(refresh);

    return function stop() {
      if (stopped) return;
      stopped = true;
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = null;
      carouselEl.removeEventListener("scroll", onScroll);
      carouselEl.removeEventListener(enterEvent, onPointerEnter);
      carouselEl.removeEventListener(leaveEvent, onPointerLeave);
      carouselEl.removeEventListener("touchstart", onTouchStart);
      carouselEl.removeEventListener("touchend", onTouchEnd);
      carouselEl.removeEventListener("touchcancel", onTouchEnd);
      carouselEl.removeEventListener("wheel", holdOff);
      carouselEl.removeEventListener("focusin", onFocusIn);
      carouselEl.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("visibilitychange", onVisibility);
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener("resize", refresh);
      if (intersectionObserver) intersectionObserver.disconnect();
      removeClones();
    };
  }

  function defineSienaStartersCarousel() {
    if (typeof customElements === "undefined") return;
    if (customElements.get("siena-starters-carousel")) return;

    class SienaStartersCarousel extends HTMLElement {
      static get observedAttributes() {
        return ["product-id", "shopify-domain", "product-slug"];
      }
      constructor() {
        super();
        this._listenerRegistered = false;
        this._stopAutoscroll = null;
      }
      connectedCallback() {
        if (!this.shadowRoot) this.attachShadow({ mode: "open" });
        this._renderSkeleton();
        this._subscribe();
      }
      disconnectedCallback() {
        this._teardownAutoscroll();
      }
      attributeChangedCallback() {
        if (this.shadowRoot) this._subscribe();
      }
      _teardownAutoscroll() {
        if (typeof this._stopAutoscroll === "function") this._stopAutoscroll();
        this._stopAutoscroll = null;
      }
      _pageUrl() {
        // EAA-2902: starters are keyed by the page URL where the carousel is
        // embedded (the PDP/collection/page), not by product identifiers.
        // ECH-1244: a pinned product (data-product-url / product-slug /
        // data-product-handle on this element, or a pin elsewhere on the page)
        // keys them to that product's URL instead, so the carousel works on
        // landing pages.
        var explicit = (this.getAttribute("data-product-url") || "").trim();
        if (explicit) {
          try {
            return new URL(explicit, window.location.href).href;
          } catch (_) {}
        }
        var own = siena_normalizeProductHandle(
          this.getAttribute("product-slug") ||
            this.getAttribute("data-product-handle")
        );
        if (own) return window.location.origin + "/products/" + own;
        return siena_getPinnedProductUrl() || window.location.href;
      }
      _subscribe() {
        var self = this;
        if (!window.__siena) {
          window.setTimeout(function () {
            self._subscribe();
          }, 200);
          return;
        }
        var url = this._pageUrl();
        var cached =
          window.__siena.starters &&
          window.__siena.starters.byUrl &&
          window.__siena.starters.byUrl[url];
        if (cached) {
          this._renderPills(self._mergeFixed(cached));
          return;
        }
        if (!this._listenerRegistered) {
          this._listenerRegistered = true;
          window.__siena.starters.listeners.push(function (ev) {
            if (ev.url === url) self._renderPills(self._mergeFixed(ev.starters));
          });
        }
        if (typeof window.__siena.fetchStarters === "function") {
          window.__siena.fetchStarters(url);
        }
      }
      _mergeFixed(aiStarters) {
        // EAA-3204: prepend the merchant's fixed starters (exposed at settings
        // load) so they always render first, even when the AI fetch is empty.
        var fixed = window.__siena && window.__siena.fixedStarters;
        var merged = siena_mergeFixedStarters(fixed, aiStarters);
        // EAA-3730: the merchant's max caps the carousel; null = no cap.
        var max = window.__siena && window.__siena.maxStarters;
        return max ? merged.slice(0, max) : merged;
      }
      _applyTheme() {
        // EFE-1196: follow the Live Chat theme + font (exposed at settings
        // load). Before settings land this is a no-op and the light defaults
        // baked into SIENA_CAROUSEL_STYLES apply.
        var theme = window.__siena && window.__siena.startersTheme;
        if (!theme) return;
        if (theme.dark) this.setAttribute("data-siena-theme", "dark");
        else this.removeAttribute("data-siena-theme");
        if (theme.fontFamily) {
          siena_ensureStartersFont(theme.fontFamily);
          this.style.fontFamily = siena_startersFontStack(theme.fontFamily);
        } else {
          this.style.fontFamily = "";
        }
      }
      _renderSkeleton() {
        if (!this.shadowRoot) return;
        this._teardownAutoscroll();
        this._applyTheme();
        this.shadowRoot.innerHTML =
          "<style>" +
          SIENA_CAROUSEL_STYLES +
          "</style>" +
          '<div class="carousel" aria-busy="true">' +
          '<span class="skeleton-btn"></span>' +
          '<span class="skeleton-btn"></span>' +
          '<span class="skeleton-btn"></span>' +
          '<span class="skeleton-btn"></span>' +
          "</div>";
      }
      _renderPills(starters) {
        if (!this.shadowRoot) return;
        this._teardownAutoscroll();
        if (!starters || starters.length === 0) {
          this.style.display = "none";
          return;
        }
        this.style.display = "block";
        this._applyTheme();
        var pills = starters
          .map(function (s) {
            // Fixed starters (no uuid) render without the AI sparkle icon to
            // distinguish merchant-defined chips from AI-generated ones.
            return (
              '<button class="question-btn" type="button" data-uuid="' +
              siena_escapeHtml(s.uuid) +
              '">' +
              (s.uuid ? SIENA_SPARKLES_SVG : "") +
              '<span class="question-text">' +
              siena_escapeHtml(s.question) +
              "</span>" +
              "</button>"
            );
          })
          .join("");
        this.shadowRoot.innerHTML =
          "<style>" +
          SIENA_CAROUSEL_STYLES +
          '</style><div class="carousel" role="list">' +
          pills +
          "</div>";
        // Map vertical wheel to horizontal scroll for mouse users.
        var carouselEl = this.shadowRoot.querySelector(".carousel");
        if (carouselEl) {
          carouselEl.addEventListener(
            "wheel",
            function (e) {
              if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                carouselEl.scrollLeft += e.deltaY;
                e.preventDefault();
              }
            },
            { passive: false }
          );

          // Edge fade masks based on scroll position.
          var updateFades = function () {
            var canLeft = carouselEl.scrollLeft > 1;
            var canRight =
              carouselEl.scrollLeft + carouselEl.clientWidth <
              carouselEl.scrollWidth - 1;
            carouselEl.classList.toggle("fade-left", canLeft);
            carouselEl.classList.toggle("fade-right", canRight);
          };
          carouselEl.addEventListener("scroll", updateFades, { passive: true });
          requestAnimationFrame(updateFades);
          if (typeof ResizeObserver !== "undefined") {
            new ResizeObserver(updateFades).observe(carouselEl);
          } else {
            window.addEventListener("resize", updateFades);
          }
        }
        var bindPill = function (btn) {
          btn.addEventListener("click", function () {
            var uuid = btn.dataset.uuid || "";
            var textNode = btn.querySelector(".question-text");
            var text = textNode ? textNode.textContent || "" : "";
            // Fixed starters carry no uuid — send as a regular message (null).
            if (typeof window.__siena.openWidgetAndSend === "function") {
              window.__siena.openWidgetAndSend({
                pre_generated_question_uuid: uuid || null,
                text: text
              });
            } else {
              window.__siena = window.__siena || { pendingTaps: [] };
              window.__siena.pendingTaps = window.__siena.pendingTaps || [];
              window.__siena.pendingTaps.push({
                pre_generated_question_uuid: uuid || null,
                text: text
              });
            }
          });
        };
        this.shadowRoot.querySelectorAll(".question-btn").forEach(bindPill);
        // EAA-3440: keep the carousel drifting when the merchant enabled it.
        // Settings land on window.__siena at settings load; pills rendered
        // before that are re-rendered from there (see the settings bridge).
        if (carouselEl) {
          this._stopAutoscroll = siena_startStartersAutoscroll(
            carouselEl,
            window.__siena && window.__siena.startersAutoscroll,
            bindPill
          );
        }
      }
    }

    customElements.define("siena-starters-carousel", SienaStartersCarousel);
  }

  // Apply per-customer position offsets and toggle the mobile-active class via matchMedia.
  // No-op when none of the offset fields are provided (preserves legacy behavior).
  // Mobile threshold is hard-coded at 768px — no per-customer override.
  function applyPositionOffsets(iframeEl, settings) {
    if (
      settings.desktop_bottom_offset_px == null &&
      settings.desktop_side_offset_px == null &&
      settings.mobile_bottom_offset_px == null &&
      settings.mobile_side_offset_px == null
    ) {
      return; // legacy behavior — no var injection
    }

    if (settings.desktop_bottom_offset_px != null) {
      iframeEl.style.setProperty(
        "--siena-bottom-offset",
        `${settings.desktop_bottom_offset_px}px`
      );
    }
    if (settings.desktop_side_offset_px != null) {
      iframeEl.style.setProperty(
        "--siena-side-offset",
        `${settings.desktop_side_offset_px}px`
      );
    }
    if (settings.mobile_bottom_offset_px != null) {
      iframeEl.style.setProperty(
        "--siena-mobile-bottom-offset",
        `${settings.mobile_bottom_offset_px}px`
      );
    }
    if (settings.mobile_side_offset_px != null) {
      iframeEl.style.setProperty(
        "--siena-mobile-side-offset",
        `${settings.mobile_side_offset_px}px`
      );
    }

    const mq = window.matchMedia("(max-width: 768px)");
    const apply = function () {
      iframeEl.classList.toggle("siena-iframe--mobile-active", mq.matches);
    };
    apply();
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
    } else if (mq.addListener) {
      mq.addListener(apply); // legacy Safari fallback
    }
  }

  class SienaAIChat {
    // ISO 639-1 codes a storefront realistically routes on. Used only to
    // tell "/fr/…" (locale) from "/us/…" or "/id/123" (not a locale).
    static KNOWN_LANGUAGES = new Set([
      "en","fr","es","de","it","pt","nl","sv","da","nb","no","fi","pl","cs",
      "sk","hu","ro","bg","el","tr","ru","uk","he","ar","hi","th","vi","id",
      "ms","ja","ko","zh","ca","eu","gl","et","lv","lt","sl","hr","sr","is","ga"
    ]);
    constructor(config = {}) {
      this.config = { ...DEFAULT_CONFIG, ...config };
      this.iframe = null;
      this.searchBarIframe = null;
      this.embeddedIframe = null;
      this.apiKeys = null;
      this.chatSettings = null;
      this.isMobile = this.checkIsMobile();
      this.appData = null;
      this.searchBarClosed =
        this.safeStorageGet("sienaSearchBarClosed") === "true" ? true : false;
      this.searchBarUsed =
        this.safeLocalStorageGet("searchbar_used") === "true";
      this.productData = null;
      this.currency = "USD"; // Default currency
      this.currencyFetched = false;
      this.locale = "en"; // EAA-3327: storefront language, resolved in setInitialData
      this._currencyFetchPromise = null;

      // Proactive campaigns
      this.proactiveCampaigns = [];
      this.proactivePopupElement = null;
      this.activeProactiveCampaign = null;
      this.proactiveTriggersActive = false;
      this.proactiveScrollHandler = null;
      this.proactiveTimeoutId = null;

      // this.showEmbedded = config.showEmbedded || false;
      // this.embeddedElementId = config.embeddedElementId || "siena-embedded";
      this.embeddedElementId = "siena-embedded";

      // Widget state for headless API (SNA-6840)
      this.widgetState = "closed";
      this.conversationStartedFired = false;
      this.unreadCount = 0;

      // Session tracking for monitoring updates
      this.currentSessionUuid = null;
      this.currentConversationUuid = null;
      this.currentUserId = null; // Track user ID for Sentry context
      this.sessionStartTime = Date.now(); // Track when this session started

      // Load persisted user ID from localStorage
      this.persistedUserId = this.safeLocalStorageGet("siena_user_id");

      // Initialize unified tracing system
      this.initializeUnifiedTracing();

      // Use the global Honeycomb tracker
      this.honeycomb = globalHoneycombTracker;
      this.initStartTime = performance.now();

      // Update master trace with widget construction details (DON'T start new trace)
      if (this.honeycomb.sessionSpan) {
        this.honeycomb.sessionSpan.addField("stage", "widget_construction");
        this.honeycomb.sessionSpan.addField("client_id", this.clientId);
        this.honeycomb.sessionSpan.addField("connection_id", this.connectionId);
        this.honeycomb.sessionSpan.addField("is_mobile", this.isMobile);
        this.honeycomb.sessionSpan.addField(
          "widget_construction_start",
          this.initStartTime
        );
      }

      // Initialize Sentry context for this widget instance
      this.initializeSentryContext();

      // Track widget construction
      this.honeycomb.addEvent("widget.constructed", {
        is_mobile: this.isMobile,
        search_bar_closed: this.searchBarClosed,
        search_bar_used: this.searchBarUsed,
        embedded_element_id: this.embeddedElementId,
        config_overrides: Object.keys(config).length,
        fix_version: "v2.1.0-unified-tracing", // Track unified persistent tracing
        had_persisted_user_id: !!this.persistedUserId,
        persisted_user_id: this.persistedUserId || null,
        trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
      });

      // Track user ID persistence status
      if (this.persistedUserId) {
        this.honeycomb.addEvent("user.id_loaded_from_storage", {
          user_id: this.persistedUserId,
          storage_type: "localStorage",
          widget_initialization: true,
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });
      }
    }

    // Initialize Sentry context for widget-specific error tracking
    initializeSentryContext() {
      if (!DEFAULT_CONFIG.SENTRY_DSN) return;

      // Set widget-specific context that persists across all widget errors
      window.SienaWidgetSentryContext = {
        widget_version: DEFAULT_CONFIG.VERSION,
        widget_environment: DEFAULT_CONFIG.NODE_ENV,
        app_key: this.apiKeys?.token || "unknown",
        is_mobile: this.isMobile,
        page_url: window.location.href,
        page_hostname: window.location.hostname,
        user_agent: navigator.userAgent,
        widget_id: `widget_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 8)}`,

        // Dynamic context (updated as widget state changes)
        user_id: this.currentUserId,
        session_uuid: this.currentSessionUuid,
        conversation_uuid: this.currentConversationUuid
      };
    }

    // Update Sentry context when user/session information changes
    updateSentryUserContext(
      userId,
      sessionUuid,
      conversationUuid,
      context = {}
    ) {
      // Handle user ID changes and persistence
      // NOTE: userId is null during session initialization - this is expected
      if (userId && userId !== this.currentUserId) {
        this.handleUserIdChange(userId, {
          source: "sentry_context_update",
          function: "updateSentryUserContext",
          endpoint: context.endpoint || "unknown",
          ...context
        });
      }

      // Handle session changes
      if (sessionUuid && sessionUuid !== this.currentSessionUuid) {
        this.handleSessionChange(sessionUuid, this.currentSessionUuid);
      }

      this.currentUserId = userId;
      this.currentSessionUuid = sessionUuid;
      this.currentConversationUuid = conversationUuid;

      // Update global Sentry context
      if (window.SienaWidgetSentryContext) {
        window.SienaWidgetSentryContext.user_id = userId;
        window.SienaWidgetSentryContext.session_uuid = sessionUuid;
        window.SienaWidgetSentryContext.conversation_uuid = conversationUuid;
      }
    }

    // Handle user ID changes and localStorage persistence
    handleUserIdChange(newUserId, context = {}) {
      const previousUserId = this.currentUserId;
      const persistedUserId = this.persistedUserId;
      const now = Date.now(); // Single timestamp for consistency

      // Get detailed context for better debugging
      const changeContext = {
        trigger_source: context.source || "unknown",
        trigger_function: context.function || "unknown",
        api_endpoint: context.endpoint || "unknown",
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        has_previous_session: !!previousUserId,
        has_persisted_storage: !!persistedUserId,
        localStorage_age: this.getLocalStorageAge("siena_user_id"),
        session_age: this.getSessionAge(),
        ...context
      };

      // Determine if this is first time setting user ID or a change
      if (!previousUserId && !persistedUserId) {
        // First time user ID is set
        this.safeLocalStorageSet("siena_user_id", newUserId);
        this.safeLocalStorageSet("siena_user_id_timestamp", now.toString());
        this.persistedUserId = newUserId;

        this.honeycomb.addEvent("user.id_stored", {
          user_id: newUserId,
          storage_type: "localStorage",
          is_first_time: true,
          had_persisted_id: false,
          session_uuid: this.currentSessionUuid,
          conversation_uuid: this.currentConversationUuid,
          timestamp: now,
          trace_id: this.getUnifiedTraceId(), // UNIFIED TRACE_ID
          event_category: "user_tracking",
          ...changeContext
        });
      } else if (previousUserId && newUserId !== previousUserId) {
        // User ID changed during session
        this.safeLocalStorageSet("siena_user_id", newUserId);
        this.safeLocalStorageSet("siena_user_id_timestamp", now.toString());
        this.persistedUserId = newUserId;

        this.honeycomb.addEvent("user.id_changed", {
          new_user_id: newUserId,
          previous_user_id: previousUserId,
          persisted_user_id: persistedUserId,
          change_reason: "session_update",
          detailed_reason: `User ID changed during active session from ${previousUserId} to ${newUserId}`,
          session_uuid: this.currentSessionUuid,
          conversation_uuid: this.currentConversationUuid,
          timestamp: now,
          trace_id: this.getUnifiedTraceId(), // UNIFIED TRACE_ID
          event_category: "user_tracking",
          severity: "warn",
          ...changeContext
        });
      } else if (
        !previousUserId &&
        persistedUserId &&
        newUserId !== persistedUserId
      ) {
        // Different user ID than persisted (user switch)
        this.safeLocalStorageSet("siena_user_id", newUserId);
        this.safeLocalStorageSet("siena_user_id_timestamp", now.toString());
        const oldPersistedId = this.persistedUserId;
        this.persistedUserId = newUserId;

        this.honeycomb.addEvent("user.id_changed", {
          new_user_id: newUserId,
          previous_user_id: oldPersistedId,
          persisted_user_id: oldPersistedId,
          change_reason: "user_switch",
          detailed_reason: `Different user detected: localStorage had ${oldPersistedId}, API returned ${newUserId}`,
          session_uuid: this.currentSessionUuid,
          conversation_uuid: this.currentConversationUuid,
          timestamp: now,
          trace_id: this.getUnifiedTraceId(), // UNIFIED TRACE_ID
          event_category: "user_tracking",
          severity: "error", // This is the critical issue we want to track
          ...changeContext
        });
      } else if (
        !previousUserId &&
        persistedUserId &&
        newUserId === persistedUserId
      ) {
        // Returning user - same as persisted
        this.honeycomb.addEvent("user.id_restored", {
          user_id: newUserId,
          was_persisted: true,
          detailed_reason: `Returning user: localStorage and API both have ${newUserId}`,
          session_uuid: this.currentSessionUuid,
          conversation_uuid: this.currentConversationUuid,
          timestamp: now,
          trace_id: this.getUnifiedTraceId(), // UNIFIED TRACE_ID
          event_category: "user_tracking",
          ...changeContext
        });
      }
    }

    // Helper method to get localStorage age
    getLocalStorageAge(key) {
      try {
        const item = localStorage.getItem(key + "_timestamp");
        if (item) {
          return Date.now() - parseInt(item);
        }
        return null;
      } catch (e) {
        return null;
      }
    }

    // Helper method to get session age
    getSessionAge() {
      if (this.sessionStartTime) {
        return Date.now() - this.sessionStartTime;
      }
      return null;
    }

    // Calculate the approximate width of the chat bubble based on text content
    calculateBubbleWidth(
      chatButtonText,
      isMobile,
      isIndependentMobilePositioning
    ) {
      // Base width for icon only (56px icon + padding)
      const baseWidth = 80;

      // Don't show text if no text provided or on mobile with independent positioning
      if (
        !chatButtonText ||
        chatButtonText.trim().length === 0 ||
        (isMobile && isIndependentMobilePositioning)
      ) {
        return baseWidth;
      }

      // Approximate character width calculation
      // Using 7px per character for 14px bold font (text-sm + font-bold)
      const textWidth = chatButtonText.length * 7;

      // Add padding (16px for left/right padding + 8px for spacing between icon and text)
      const totalTextWidth = textWidth + 24;

      // Return base width + text width, with a minimum of base width
      return Math.max(baseWidth, baseWidth + totalTextWidth);
    }

    // ========================================
    // UNIFIED TRACING SYSTEM
    // ========================================

    /**
     * Initialize unified tracing system for persistent trace_id across sessions
     * INFINITE PERSISTENCE: Never expires
     */
    initializeUnifiedTracing() {
      const TRACE_ID_KEY = "siena_persistent_trace_id";
      const TRACE_ID_TIMESTAMP_KEY = "siena_persistent_trace_id_timestamp";

      try {
        // Check if we have an existing trace_id
        const existingTraceId = this.safeLocalStorageGet(TRACE_ID_KEY);

        if (existingTraceId) {
          // Use existing trace_id (no expiry check)
          this.unifiedTraceId = existingTraceId;
          console.debug(
            "[Widget] Using existing persistent trace_id:",
            existingTraceId
          );
          return;
        }

        // Generate new trace_id if none exists
        this.generateNewUnifiedTraceId();
      } catch (error) {
        // Fallback if localStorage fails
        console.warn("[Widget] localStorage failed for tracing:", error);
        this.unifiedTraceId = this.generateUUID();
      }
    }

    /**
     * Generate a new unified trace_id and persist it
     */
    generateNewUnifiedTraceId() {
      this.unifiedTraceId = this.generateUUID();

      try {
        this.safeLocalStorageSet(
          "siena_persistent_trace_id",
          this.unifiedTraceId
        );
        console.debug(
          "[Widget] Generated new unified trace_id:",
          this.unifiedTraceId
        );
      } catch (error) {
        console.warn("[Widget] Failed to persist unified trace_id:", error);
      }
    }

    /**
     * Generate UUID v4 for widget use
     */
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    }

    /**
     * Get the unified trace_id - SINGLE source of truth for ALL events
     * CRITICAL: Check for parent trace_id override first
     */
    getUnifiedTraceId() {
      // Priority 1: Check if parent has provided a trace_id via postMessage
      if (this.parentTraceId) {
        return this.parentTraceId;
      }

      // Priority 2: Use localStorage unified trace_id
      if (!this.unifiedTraceId) {
        this.initializeUnifiedTracing();
      }
      return this.unifiedTraceId || "widget-fallback-" + Date.now();
    }

    /**
     * Set parent trace_id to override localStorage trace_id
     * This ensures iframe uses parent window's trace_id for unified tracing
     */
    setParentTraceId(parentTraceId) {
      const oldTraceId = this.getUnifiedTraceId();
      this.parentTraceId = parentTraceId;

      // Sync to localStorage so unified system uses parent's trace_id
      try {
        this.safeLocalStorageSet("siena_persistent_trace_id", parentTraceId);
        this.safeLocalStorageSet(
          "siena_persistent_trace_id_timestamp",
          Date.now().toString()
        );
        this.unifiedTraceId = parentTraceId; // Update internal cache

        console.debug("[Widget] Synchronized trace_id with parent:", {
          parentTraceId,
          oldTraceId,
          synchronized: true
        });

        // Track this synchronization event
        this.honeycomb.addEvent("trace.synchronized_with_parent", {
          unified_trace_id: parentTraceId,
          old_trace_id: oldTraceId,
          iframe_domain: window.location.hostname,
          parent_domain: document.referrer
            ? new URL(document.referrer).hostname
            : "unknown",
          synchronization_reason: "parent_postmessage",
          timestamp: Date.now(),
          trace_id: parentTraceId // Main trace_id field for Honeycomb
        });
      } catch (error) {
        console.warn("[Widget] Failed to sync trace_id with parent:", error);
      }
    }

    // Handle session changes (when session ID actually changes, not just updates)
    handleSessionChange(newSessionUuid, previousSessionUuid) {
      if (previousSessionUuid) {
        // This is a session change (not initial set)
        this.honeycomb.addEvent("session.changed", {
          new_session_uuid: newSessionUuid,
          previous_session_uuid: previousSessionUuid,
          user_id: this.currentUserId,
          conversation_uuid: this.currentConversationUuid,
          change_type: "session_switch",
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });

        // Update trace ID to use real session ID (CONTINUE same trace, don't start new)
        this.honeycomb.updateTraceId(newSessionUuid);

        // Add session change details to existing trace
        if (this.honeycomb.sessionSpan) {
          this.honeycomb.sessionSpan.addField("user_id", this.currentUserId);
          this.honeycomb.sessionSpan.addField(
            "conversation_uuid",
            this.currentConversationUuid
          );
          this.honeycomb.sessionSpan.addField("session_type", "changed");
          this.honeycomb.sessionSpan.addField(
            "previous_session",
            previousSessionUuid
          );
          this.honeycomb.sessionSpan.addField("session_updated", true);
        }
      } else {
        // This is initial session establishment
        // this.honeycomb.addEvent("session.established", {
        //   session_uuid: newSessionUuid,
        //   user_id: this.currentUserId,
        //   conversation_uuid: this.currentConversationUuid,
        //   had_previous_session: false,
        //   timestamp: Date.now(),
        //   trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        // });

        // Update trace ID to use real session ID (CONTINUE same trace, don't start new)
        this.honeycomb.updateTraceId(newSessionUuid);

        // Add session establishment details to existing trace
        if (this.honeycomb.sessionSpan) {
          this.honeycomb.sessionSpan.addField("user_id", this.currentUserId);
          this.honeycomb.sessionSpan.addField(
            "conversation_uuid",
            this.currentConversationUuid
          );
          this.honeycomb.sessionSpan.addField("session_type", "established");
          this.honeycomb.sessionSpan.addField("is_initial", true);
          this.honeycomb.sessionSpan.addField("session_established", true);
        }
      }
    }

    // Helper method to categorize HTTP status codes
    categorizeHttpStatus(status) {
      if (status >= 200 && status < 300) return "success";
      if (status >= 300 && status < 400) return "redirect";
      if (status === 400) return "bad_request";
      if (status === 401) return "unauthorized";
      if (status === 403) return "forbidden";
      if (status === 404) return "not_found";
      if (status === 429) return "rate_limited";
      if (status >= 400 && status < 500) return "client_error";
      if (status >= 500 && status < 600) return "server_error";
      return "unknown";
    }

    async init(apiKeys) {
      this.apiKeys = apiKeys;
      const sienaCookie = this.getCookie("SienaAIChat");

      // Start initialization span
      const initSpan = this.honeycomb.startSpan("widget.initialization");
      initSpan.addField("app_key", apiKeys.token || "missing");
      initSpan.addField("siena_cookie", sienaCookie || "none");

      // Update existing preliminary trace with init parameters
      // The preliminary trace was already started in constructor
      if (this.honeycomb.sessionSpan) {
        this.honeycomb.sessionSpan.addField(
          "app_key",
          apiKeys.token || "missing"
        );
        this.honeycomb.sessionSpan.addField(
          "siena_cookie",
          sienaCookie || "none"
        );
        this.honeycomb.sessionSpan.addField(
          "initialization_start",
          this.initStartTime
        );
        this.honeycomb.sessionSpan.addField("session_type", "initialization");
        this.honeycomb.sessionSpan.addField("init_phase", "started");
      }

      // Track initialization attempt
      this.honeycomb.addEvent("widget.init.started", {
        app_key: apiKeys.token || "missing",
        siena_cookie: sienaCookie || "none",
        page_product_path: window.location.pathname.includes("/products/"),
        init_start_time: this.initStartTime,
        trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
      });

      if (sienaCookie === "removed") {
        this.honeycomb.addEvent("widget.init.skipped", {
          reason: "siena_cookie_removed",
          app_key: apiKeys.token || "missing",
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });
        initSpan.addField("skip_reason", "siena_cookie_removed");
        initSpan.finish();
        return;
      }

      try {
        // Track settings fetch
        const settingsStartTime = performance.now();
        const settings = await this.fetchChatSettings(apiKeys.token);
        const settingsFetchTime = performance.now() - settingsStartTime;

        this.honeycomb.addEvent("widget.settings.fetched", {
          app_key: apiKeys.token,
          duration_ms: settingsFetchTime,
          settings_received: !!settings,
          settings_size: settings ? JSON.stringify(settings).length : 0,
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });

        if (!settings) {
          this.honeycomb.addEvent("widget.init.failed", {
            reason: "no_settings_received",
            app_key: apiKeys.token,
            duration_ms: performance.now() - this.initStartTime,
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });
          initSpan.addField("failure_reason", "no_settings_received");
          initSpan.finish();
          return;
        }

        this.chatSettings = settings;
        initSpan.addField("settings_fetched", true);

        // Expose auth on window.__siena so the carousel can call /pre_generated_question
        // with the same headers the in-iframe embedded-search component uses.
        try {
          window.__siena = window.__siena || {};
          window.__siena.auth = {
            organizationUuid: settings.organization_uuid,
            appKey: apiKeys && apiKeys.token
          };
          // EAA-3204: expose merchant-authored fixed conversation starters so
          // the <siena-starters-carousel> element can render them ahead of the
          // AI-generated questions (single source of truth = integration settings).
          window.__siena.fixedStarters = siena_normalizeFixedStarters(
            settings.fixed_conversation_starters
          );
          // EAA-3440: carousel autoscroll (enabled + px/s speed) for the
          // <siena-starters-carousel> element.
          window.__siena.startersAutoscroll =
            siena_normalizeStartersAutoscroll(settings);
          // EAA-3730: merchant max starters for the <siena-starters-carousel> element.
          window.__siena.maxStarters = siena_normalizeMaxStarters(settings);
          // EFE-1196: Live Chat brand styling (theme, font, brand color) for
          // the <siena-starters-carousel> element.
          window.__siena.startersTheme = siena_normalizeStartersTheme(settings);
        } catch (e) {}

        // Register the <siena-starters-carousel> custom element. Idempotent —
        // safe to call even if a Shopify theme inlines the element before this
        // runs. We register unconditionally so the merchant's tag works as soon
        // as it appears in their theme; surface gating happens at the BE
        // settings response layer once that PR lands.
        try {
          defineSienaStartersCarousel();
        } catch (carouselError) {
          console.warn(
            "[siena] failed to register starters carousel",
            carouselError
          );
        }

        // EAA-3440: carousels that rendered before settings arrived re-render
        // so they pick up fixed starters + autoscroll from the live settings.
        try {
          document
            .querySelectorAll("siena-starters-carousel")
            .forEach(function (el) {
              if (typeof el._subscribe === "function") el._subscribe();
            });
        } catch (e) {}

        // Check if active is false - if so, return immediately
        if (settings.active === false) {
          this.honeycomb.addEvent("widget.init.skipped", {
            reason: "chat_disabled",
            app_key: apiKeys.token,
            settings: {
              active: settings.active,
              is_visible_mobile: settings.is_visible_mobile,
              is_visible_desktop: settings.is_visible_desktop
            }
          });
          initSpan.addField("skip_reason", "chat_disabled");
          initSpan.finish();
          return;
        }

        // COMMENTED OUT: Check if widget should be displayed
        // We now always initialize the widget but with 0x0 dimensions if visibility is off
        // This allows clients to programmatically launch the widget even when visibility is disabled
        /*
        if (!this.shouldDisplayChat(settings)) {
          this.honeycomb.addEvent("widget.init.skipped", {
            reason: "should_not_display",
            app_key: apiKeys.token,
            settings: {
              is_visible_mobile: settings.is_visible_mobile,
              is_visible_desktop: settings.is_visible_desktop,
              embedded_search_enabled: settings.embedded_search_enabled
            },
            is_mobile: this.isMobile
          });
          initSpan.addField("skip_reason", "should_not_display");
          initSpan.finish();
          return;
        }
        */

        // Track product page detection and data fetching.
        // ECH-1244: a product pinned via data-product-handle counts as product
        // context, so landing pages can host the PDP widget and carousel.
        const isProductPage = this.hasProductContext();
        initSpan.addField("is_product_page", isProductPage);
        initSpan.addField(
          "pinned_product_handle",
          siena_getPinnedProductHandle()
        );

        // Fetch productData whenever we're on a product page so the surfaces
        // that still need it (embedded-search, in-widget product cards) can use
        // it. EAA-2902: the starters carousel is now URL-based and no longer
        // consumes productData, so it is intentionally excluded from this gate
        // (and works on non-product pages where productData is never fetched).
        const startersCarouselMounted = !!document.getElementById(
          "siena-starters-carousel"
        );
        const embeddedMounted = !!document.getElementById(
          this.embeddedElementId
        );
        if (
          isProductPage &&
          (settings.is_pre_generated_questions_visible || embeddedMounted)
        ) {
          const productFetchStart = performance.now();
          this.productData = await this.fetchShopifyProductData();
          const productFetchTime = performance.now() - productFetchStart;

          this.honeycomb.addEvent("widget.product_data.fetched", {
            app_key: apiKeys.token,
            duration_ms: productFetchTime,
            product_found: !!this.productData,
            product_id: this.productData?.id || null,
            product_handle: this.productData?.handle || null,
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });

          initSpan.addField("product_data_fetched", !!this.productData);
        }

        if (settings && settings.connected_store_domain && this.productData) {
          this.productData.shopify_domain = settings.connected_store_domain;
        }

        // Always create the webchat
        const webchatStart = performance.now();
        this.createWebchat(window.location.hostname, sienaCookie);
        const webchatTime = performance.now() - webchatStart;

        this.honeycomb.addEvent("widget.webchat.created", {
          app_key: apiKeys.token,
          duration_ms: webchatTime,
          hostname: window.location.hostname,
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });

        initSpan.addField("webchat_created", true);

        // Create search bar based on conditions
        const searchBarStart = performance.now();
        this.createSearchBarIframe();
        const searchBarTime = performance.now() - searchBarStart;

        this.honeycomb.addEvent("widget.search_bar.created", {
          app_key: apiKeys.token,
          duration_ms: searchBarTime,
          search_bar_enabled: this.shouldShowSearchBar(),
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });

        initSpan.addField("search_bar_created", true);

        // Initialize proactive campaigns after widget is ready (non-blocking)
        this.initProactiveCampaigns()
          .then(() => {
            initSpan.addField("proactive_campaigns_initialized", true);
          })
          .catch((proactiveError) => {
            console.warn(
              "[Proactive] Failed to initialize campaigns:",
              proactiveError
            );
            initSpan.addField(
              "proactive_campaigns_error",
              proactiveError.message
            );
          });

        const showEmbedded = this.chatSettings.embedded_search_enabled;
        // Create embedded chat if enabled and the merchant placed the container.
        // ECH-1244: product data is optional. On product pages (or with a pinned
        // product) the box gets the product's starters and add-to-cart context;
        // on any other page it runs on starters generated from that page.
        if (showEmbedded && embeddedMounted) {
          const embeddedStart = performance.now();
          this.createEmbeddedChat(this.productData);
          const embeddedTime = performance.now() - embeddedStart;

          this.honeycomb.addEvent("widget.embedded.created", {
            app_key: apiKeys.token,
            duration_ms: embeddedTime,
            product_id: this.productData?.id || null,
            starters_url: this.getStartersUrl(),
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });

          initSpan.addField("embedded_created", true);
        }

        // Create starters carousel iframe if the customer placed the placeholder div.
        // ECH-1244: mounts on any page that has the div. Starters resolve by
        // the page URL (backend generates them from the page content), or by
        // the pinned product's URL when data-product-handle is set. Placing the
        // div is the merchant's opt-in for that page.
        if (startersCarouselMounted) {
          try {
            this.createStartersCarousel(this.getStartersUrl());
          } catch (err) {
            console.warn("[siena] failed to create starters carousel", err);
          }
        }

        // Track successful initialization
        const totalInitTime = performance.now() - this.initStartTime;
        this.honeycomb.addEvent("widget.init.completed", {
          app_key: apiKeys.token,
          total_duration_ms: totalInitTime,
          webchat_created: true,
          search_bar_created: true,
          embedded_created: showEmbedded && embeddedMounted,
          product_data_available: !!this.productData,
          settings_summary: {
            active: settings.active,
            is_visible_mobile: settings.is_visible_mobile,
            is_visible_desktop: settings.is_visible_desktop,
            embedded_search_enabled: settings.embedded_search_enabled,
            search_bar_mobile: settings.search_bar_mobile,
            search_bar_desktop: settings.search_bar_desktop
          }
        });

        initSpan.addField("success", true);
        initSpan.addField("total_duration_ms", totalInitTime);
        initSpan.finish();
      } catch (error) {
        const totalInitTime = performance.now() - this.initStartTime;

        // Track initialization failure
        this.honeycomb.addEvent("widget.init.failed", {
          app_key: apiKeys.token || "missing",
          error_message: error.message || error.toString(),
          error_name: error.name || "Error",
          total_duration_ms: totalInitTime,
          settings_loaded: !!this.chatSettings,
          stage: this.chatSettings
            ? "after_settings_loaded"
            : "during_settings_fetch",
          stack_preview: error.stack
            ? error.stack.substring(0, 500)
            : "no stack",
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });

        initSpan.addField("error", error.message || error.toString());
        initSpan.addField("success", false);
        initSpan.addField("total_duration_ms", totalInitTime);
        initSpan.finish();

        console.error("Failed to initialize SienaAIChat:", error);
        sendErrorToSentry(error, {
          method: "init",
          token: apiKeys.token,
          appKey: apiKeys.token,
          cookie: sienaCookie,
          settings: this.chatSettings,
          hostname: window.location.hostname,
          url: window.location.href,
          stage: this.chatSettings
            ? "after_settings_loaded"
            : "during_settings_fetch",
          config: this.config
        });
      }
    }

    // Fetch chat settings from the API
    async fetchChatSettings(token) {
      // EAA-3327: tell the API which storefront language the page is in so
      // merchant-authored copy (heading, greeting, handoff replies…) comes back
      // already translated. Server ignores it unless the org flag is on.
      const locale = this.resolveStorefrontLocale();
      const url = `${this.config.API_BASE}/v1/live_chat/settings?key=${token}&locale=${encodeURIComponent(
        locale
      )}`;
      const requestData = { token, endpoint: url };
      const startTime = performance.now();
      const requestId = `api_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 8)}`;

      // Check cache first. The response is language-specific (EAA-3327), so
      // the key must carry the locale or a /fr visit would serve French
      // settings on the next English page for the cache lifetime.
      const cacheKey = `siena_settings_${token}_${locale.toLowerCase()}`;
      const cachedSettings = this.getCachedApiResponse(cacheKey);
      if (cachedSettings) {
        // this.honeycomb.addEvent("api.settings.fetch.cache_hit", {
        //   request_id: requestId,
        //   app_key: token || "missing",
        //   cache_key: cacheKey,
        //   timestamp: Date.now(),
        //   trace_id: this.getUnifiedTraceId()
        // });
        return cachedSettings;
      }

      // Start tracing span for settings fetch (as child of widget initialization)
      const settingsSpan = this.honeycomb.startSpan("fetch_chat_settings");
      settingsSpan.addField("request_id", requestId);
      settingsSpan.addField("app_key", token || "missing");
      settingsSpan.addField("api_base", this.config.API_BASE);
      settingsSpan.addField("url", url);
      settingsSpan.addField("cache_hit", false);

      // Track API call start event
      // this.honeycomb.addEvent("api.settings.fetch.started", {
      //   request_id: requestId,
      //   endpoint: "chat_settings",
      //   app_key: token || "missing",
      //   api_url: url,
      //   timestamp: Date.now(),
      //   trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
      // });

      // Track API request start
      this.honeycomb.addEvent("widget.api.request.started", {
        request_id: requestId,
        endpoint: "chat_settings",
        method: "GET",
        url: url,
        app_key: token || "missing",
        api_base: this.config.API_BASE,
        trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
      });

      try {
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            "x-siena-app-key": token,
            "x-siena-livechat-app-key": token
          },
          credentials: "include"
        });

        const apiDuration = performance.now() - startTime;

        // Track API response received
        // this.honeycomb.addEvent("api.settings.fetch.response", {
        //   request_id: requestId,
        //   status_code: response.status,
        //   status_ok: response.ok,
        //   duration_ms: apiDuration,
        //   timestamp: Date.now(),
        //   trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        // });

        if (!response.ok) {
          const errorText = await response
            .text()
            .catch(() => "Failed to read error response");

          // Track API error response
          this.honeycomb.addEvent("widget.api.request.failed", {
            request_id: requestId,
            endpoint: "chat_settings",
            method: "GET",
            url: url,
            app_key: token || "missing",
            status_code: response.status,
            status_text: response.statusText,
            duration_ms: apiDuration,
            error_text: errorText.substring(0, 500), // Truncate long error messages
            response_size_bytes: errorText ? errorText.length : 0,
            response_category: this.categorizeHttpStatus(response.status),
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });

          const error = new Error(`HTTP error! Status: ${response.status}`);
          error.statusCode = response.status;
          error.responseText = errorText;

          // Enhanced error context for API failures
          sendErrorToSentry(error, {
            method: "fetchChatSettings",
            url: url,
            token: token,
            apiBase: this.config.API_BASE,
            status: response.status,
            statusText: response.statusText,
            requestData: requestData,
            responseData: errorText,
            headers: response.headers
              ? Object.fromEntries(response.headers.entries())
              : {}
          });

          throw error;
        }

        // Safely parse JSON response with error handling
        let data;
        let responseText = "";
        try {
          responseText = await response.text();

          // Check if response is empty
          if (!responseText || responseText.trim() === "") {
            throw new Error("Empty response received from server");
          }

          // Check if response looks like HTML (common with proxy/gateway errors)
          if (responseText.trim().startsWith("<")) {
            throw new Error("HTML response received instead of JSON");
          }

          data = JSON.parse(responseText);
        } catch (parseError) {
          const error = new Error(
            `Failed to parse response as JSON: ${parseError.message}`
          );
          error.originalError = parseError;
          error.responsePreview = responseText
            ? responseText.substring(0, 200)
            : "Failed to read response";

          sendErrorToSentry(error, {
            method: "fetchChatSettings",
            url: url,
            token: token,
            apiBase: this.config.API_BASE,
            requestData: requestData,
            error_type: "json_parsing_error",
            parseError: parseError.message,
            responsePreview: error.responsePreview,
            responseStatus: response.status,
            responseHeaders: response.headers
              ? Object.fromEntries(response.headers.entries())
              : {}
          });

          throw error;
        }

        // Validate that we have the expected data structure
        if (!data || typeof data !== "object") {
          const error = new Error(
            "Invalid response structure: expected object"
          );
          sendErrorToSentry(error, {
            method: "fetchChatSettings",
            url: url,
            token: token,
            apiBase: this.config.API_BASE,
            requestData: requestData,
            error_type: "response_validation_error",
            receivedData: typeof data,
            dataPreview: JSON.stringify(data).substring(0, 200)
          });
          throw error;
        }

        // Complete tracing span successfully
        const completionDuration = performance.now() - startTime;

        // Track API success event
        this.honeycomb.addEvent("api.settings.fetch.success", {
          request_id: requestId,
          duration_ms: completionDuration,
          status_code: response.status,
          response_size: responseText ? responseText.length : 0,
          settings_active: data.data?.active || false,
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });
        settingsSpan.addField("success", true);
        settingsSpan.addField("status_code", response.status);
        settingsSpan.addField(
          "response_size",
          responseText ? responseText.length : 0
        );
        settingsSpan.addField("settings_active", data.data?.active || false);
        settingsSpan.addField("duration_ms", completionDuration);
        settingsSpan.finish();

        // Cache the settings for 5 minutes (300000ms)
        this.setCachedApiResponse(cacheKey, data.data, 300000);

        return data.data;
      } catch (error) {
        // Complete tracing span with error
        const errorDuration = performance.now() - startTime;

        // Track API failure event
        this.honeycomb.addEvent("api.settings.fetch.failed", {
          request_id: requestId,
          duration_ms: errorDuration,
          error_message: error.message || error.toString(),
          error_type: error.name || "Error",
          status_code: error.statusCode || 0,
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
        });

        settingsSpan.addField("success", false);
        settingsSpan.addField(
          "error_message",
          error.message || error.toString()
        );
        settingsSpan.addField("error_type", error.name || "Error");
        settingsSpan.addField("status_code", error.statusCode || 0);
        settingsSpan.addField("duration_ms", errorDuration);
        settingsSpan.finish();

        // Only send to Sentry if we haven't already done so above
        if (!error.statusCode) {
          sendErrorToSentry(error, {
            method: "fetchChatSettings",
            url: url,
            token: token,
            apiBase: this.config.API_BASE,
            requestData: requestData,
            error_type: "network_or_parsing_error"
          });
        }
        throw error;
      }
    }

    async fetchProductSuggestions() {
      const url = `${this.config.API_BASE}/v1/live_chat/pre_generated_question`;
      // ECH-1244: pinned product URL on landing pages, page URL on PDPs.
      const pageUrl = this.getStartersUrl();
      const requestData = {
        url: pageUrl
      };

      // Start tracing span for product suggestions fetch
      const productSuggestionsSpan = this.honeycomb.startSpan(
        "fetch_product_suggestions"
      );
      productSuggestionsSpan.addField("product_id", this.productData?.id);
      productSuggestionsSpan.addField(
        "product_handle",
        this.productData?.handle
      );
      productSuggestionsSpan.addField(
        "shopify_domain",
        this.productData?.shopify_domain
      );
      productSuggestionsSpan.addField("page_url", pageUrl);
      productSuggestionsSpan.addField("api_url", url);

      const startTime = performance.now();

      try {
        const response = await fetch(url, {
          method: "POST",
          body: JSON.stringify(requestData),
          headers: {
            "Content-Type": "application/json",
            "x-siena-organization-uuid": this.chatSettings.organization_uuid,
            "x-siena-livechat-app-key": this.apiKeys.token,
            "x-siena-app-key": this.apiKeys.token
          }
        });

        if (!response.ok) {
          const errorText = await response
            .text()
            .catch(() => "Failed to read error response");
          const error = new Error(`HTTP error! Status: ${response.status}`);

          sendErrorToSentry(error, {
            method: "fetchProductSuggestions",
            url: url,
            status: response.status,
            statusText: response.statusText,
            requestData: requestData,
            responseData: errorText,
            productData: this.productData,
            organizationUuid: this.chatSettings.organization_uuid
          });

          throw error;
        }

        // Safely parse JSON response with error handling
        let data;
        let responseText = "";
        try {
          responseText = await response.text();

          // Check if response is empty
          if (!responseText || responseText.trim() === "") {
            throw new Error("Empty response received from server");
          }

          // Check if response looks like HTML (common with proxy/gateway errors)
          if (responseText.trim().startsWith("<")) {
            throw new Error("HTML response received instead of JSON");
          }

          data = JSON.parse(responseText);
        } catch (parseError) {
          const error = new Error(
            `Failed to parse product suggestions response: ${parseError.message}`
          );
          sendErrorToSentry(error, {
            method: "fetchProductSuggestions",
            url: url,
            requestData: requestData,
            error_type: "json_parsing_error",
            parseError: parseError.message,
            responsePreview: responseText
              ? responseText.substring(0, 200)
              : "Failed to read response",
            responseStatus: response.status,
            productData: this.productData,
            organizationUuid: this.chatSettings.organization_uuid
          });
          throw error;
        }

        // Safety check: ensure iframe and contentWindow exist before posting message
        if (this.embeddedIframe && this.embeddedIframe.contentWindow) {
          this.embeddedIframe.contentWindow.postMessage(
            {
              action: "productSuggestions",
              data: data.data
            },
            "*"
          );
        } else {
          console.warn(
            "Cannot send product suggestions: embedded iframe not available"
          );
        }

        // Complete tracing span successfully
        const productDuration = performance.now() - startTime;
        productSuggestionsSpan.addField("success", true);
        productSuggestionsSpan.addField(
          "suggestions_count",
          data.data?.length || 0
        );
        productSuggestionsSpan.addField("duration_ms", productDuration);
        productSuggestionsSpan.addField(
          "iframe_available",
          !!(this.embeddedIframe && this.embeddedIframe.contentWindow)
        );
        productSuggestionsSpan.finish();
      } catch (error) {
        console.error("Failed to fetch product suggestions:", error);

        // ECH-1244: let the embedded box drop its loading skeleton instead of
        // waiting forever when the starters request fails.
        if (this.embeddedIframe && this.embeddedIframe.contentWindow) {
          this.embeddedIframe.contentWindow.postMessage(
            { action: "productSuggestions", data: [] },
            "*"
          );
        }

        // Complete tracing span with error
        const productErrorDuration = performance.now() - startTime;
        productSuggestionsSpan.addField("success", false);
        productSuggestionsSpan.addField(
          "error_message",
          error.message || error.toString()
        );
        productSuggestionsSpan.addField("error_type", error.name || "Error");
        productSuggestionsSpan.addField("status_code", error.statusCode || 0);
        productSuggestionsSpan.addField("duration_ms", productErrorDuration);
        productSuggestionsSpan.finish();

        // Send to Sentry if not already sent
        if (!error.statusCode) {
          sendErrorToSentry(error, {
            method: "fetchProductSuggestions",
            url: url,
            requestData: requestData,
            productData: this.productData,
            error_type: "network_or_parsing_error"
          });
        }
        return [];
      }
    }

    // Check if current page is a Shopify product page
    isShopifyProductPage() {
      const path = window.location.pathname;
      return path.includes("/products/");
    }

    /**
     * ECH-1244: true on a PDP or when the merchant pinned a product on the page
     * (data-product-handle). Gates product-data fetching, the embedded PDP
     * widget and the starters carousel.
     */
    hasProductContext() {
      return !!siena_getPinnedProductHandle() || this.isShopifyProductPage();
    }

    /**
     * ECH-1244: the URL conversation starters are keyed by. A pinned product
     * maps to its canonical `/products/<handle>` URL so landing pages reuse the
     * PDP's starters; otherwise the current page URL (backend canonicalizes).
     */
    getStartersUrl() {
      return siena_getPinnedProductUrl() || window.location.href;
    }

    /** ECH-1244: whether starters may render on this page (see siena_startersAllowedForUrl). */
    startersAllowedOnPage() {
      return siena_startersAllowedForUrl(this.getStartersUrl());
    }

    /** Product handle: a pinned product wins over the `/products/` URL (ECH-1244). */
    getProductHandle() {
      const pinned = siena_getPinnedProductHandle();
      if (pinned) return pinned;
      const path = window.location.pathname;
      const match = path.match(/\/products\/([^/?]+)/);
      return match ? match[1] : null;
    }

    getShopifyConnectedStoreDomain() {
      // Check if window.Shopify.routes.root exists, if not use default path
      let rootPath =
        (window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        "/";
      const connectedStoreDomain =
        this?.chatSettings?.connected_store_domain || "";

      // Only use connected_store_domain for headless stores that need cross-origin API calls.
      // Native Shopify stores should use same-origin paths to avoid CORS issues.
      if (connectedStoreDomain && connectedStoreDomain?.length > 1 && this.isHeadlessStore()) {
        rootPath = `https://${connectedStoreDomain}/`;
      }

      return rootPath;
    }

    /**
     * EAA-3263: apply the configured discount code to the native Shopify cart
     * via the Cart AJAX API (supported since Shopify Summer Editions '25).
     * Returns the updated cart object, or null when no code is configured or
     * the request fails — callers must treat null as "fall back to cart.js".
     * Never throws: a discount problem must not break add-to-cart.
     */
    applyDiscountToNativeCart(rootPath) {
      const discountCode = this.chatSettings?.discount_code;
      if (!discountCode) return Promise.resolve(null);

      return fetch(`${rootPath}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ discount: discountCode })
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`cart/update.js HTTP ${response.status}`);
          }
          return response.json();
        })
        .then((cart) => {
          // Shopify returns 200 even for invalid/expired codes, and automatic
          // or previously-entered discounts also populate the discount fields —
          // so "applied" must mean OUR code landed, not just any discount
          // being present. Code discounts surface their code as the
          // application title (cart- and line-level) and in discount_codes.
          const codeMatches = (value) =>
            typeof value === "string" &&
            value.toLowerCase() === discountCode.toLowerCase();
          const applied =
            (cart.discount_codes || []).some(
              (entry) => codeMatches(entry.code) && entry.applicable !== false
            ) ||
            (cart.cart_level_discount_applications || []).some(
              (application) =>
                codeMatches(application.title) || codeMatches(application.key)
            ) ||
            (cart.items || []).some((item) =>
              (item.discounts || []).some((discount) =>
                codeMatches(discount.title)
              )
            );

          this.honeycomb.addEvent(
            applied ? "cart.discount_applied" : "cart.discount_not_applied",
            {
              discount_code: discountCode,
              cart_total_price: cart.total_price,
              timestamp: Date.now(),
              trace_id: this.getUnifiedTraceId()
            }
          );
          return cart;
        })
        .catch((err) => {
          this.honeycomb.addEvent("cart.discount_not_applied", {
            discount_code: discountCode,
            error_message: err.message || err.toString(),
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId()
          });
          return null;
        });
    }

    /**
     * HEADLESS SHOPIFY SUPPORT
     * Detects if the widget is running on a headless Shopify store
     * (custom frontend like Hydrogen, Next.js, etc. that doesn't have native Shopify cart endpoints)
     */
    isHeadlessStore() {
      // Cache the result to avoid repeated checks
      if (this._isHeadlessStore !== undefined) {
        return this._isHeadlessStore;
      }

      const connectedStoreDomain =
        this?.chatSettings?.connected_store_domain || "";

      // If no connected store domain, we're likely on a native Shopify store
      if (!connectedStoreDomain || connectedStoreDomain.length <= 1) {
        this._isHeadlessStore = false;
        return false;
      }

      // Check if window.Shopify exists (native Shopify stores have this object)
      const hasShopifyObject =
        typeof window.Shopify !== "undefined" &&
        window.Shopify !== null &&
        typeof window.Shopify.routes !== "undefined";

      // Check if we're on the connected store domain
      const currentHost = window.location.hostname.toLowerCase();
      const connectedHost = connectedStoreDomain
        .replace(/^https?:\/\//, "")
        .toLowerCase();
      const isOnConnectedDomain =
        currentHost === connectedHost ||
        currentHost.endsWith("." + connectedHost);

      // It's headless if:
      // 1. We have a connected_store_domain configured
      // 2. We're NOT on that domain (different frontend)
      // 3. AND we don't have window.Shopify (no native Shopify JS)
      this._isHeadlessStore = !isOnConnectedDomain && !hasShopifyObject;

      if (this._isHeadlessStore) {
        console.log(
          "[Siena] Headless Shopify store detected. Cart operations will emit events for custom handling."
        );
        this.honeycomb.addEvent("headless.detected", {
          connected_store_domain: connectedStoreDomain,
          current_hostname: currentHost,
          has_shopify_object: hasShopifyObject,
          is_on_connected_domain: isOnConnectedDomain,
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId()
        });
      }

      return this._isHeadlessStore;
    }

    /**
     * Emit a headless event for external handling
     * Returns a promise that resolves with the event result if a handler responds
     */
    emitHeadlessEvent(eventName, detail) {
      return new Promise((resolve, reject) => {
        const eventDetail = {
          ...detail,
          _sienaResolve: resolve,
          _sienaReject: reject,
          _sienaHandled: false
        };

        const event = new CustomEvent(eventName, {
          detail: eventDetail,
          bubbles: true,
          cancelable: true
        });

        // Track the event emission
        this.honeycomb.addEvent(`headless.event_emitted`, {
          event_name: eventName,
          detail_keys: Object.keys(detail),
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId()
        });

        document.dispatchEvent(event);

        // If no handler marks it as handled within 100ms, resolve with a fallback
        setTimeout(() => {
          if (!eventDetail._sienaHandled) {
            this.honeycomb.addEvent(`headless.event_not_handled`, {
              event_name: eventName,
              timestamp: Date.now(),
              trace_id: this.getUnifiedTraceId()
            });
            resolve({ handled: false });
          }
        }, 100);
      });
    }

    // Fetch product data from Shopify API
    async fetchShopifyProductData() {
      try {
        const handle = this.getProductHandle();
        if (!handle) return null;

        // HEADLESS STORE SUPPORT: Fetch from connected Shopify store
        if (this.isHeadlessStore()) {
          const connectedDomain = this.chatSettings?.connected_store_domain;

          if (connectedDomain) {
            try {
              // Fetch product data from Shopify's public .js endpoint, in the
              // storefront language when localization is on (EAA-3327), so
              // the page product and the chat cards agree on the title.
              const response = await this.fetchLocalizedProductJs(
                connectedDomain,
                handle
              );

              if (response) {
                const product = await response.json();
                return {
                  id: product.id,
                  title: product.title,
                  url: `/products/${handle}`,
                  handle: handle,
                  shopify_domain: connectedDomain,
                  headless: true
                };
              }
            } catch (fetchError) {
              console.warn(
                "[Siena] Failed to fetch product from connected store:",
                fetchError
              );
            }
          }

          // Fallback: return basic data from URL
          const shopifyDomain = connectedDomain || window.location.hostname;
          return {
            id: null,
            title: null,
            url: `/products/${handle}`,
            handle: handle,
            shopify_domain: shopifyDomain,
            headless: true
          };
        }

        // NATIVE SHOPIFY: Fetch from products endpoint
        let rootPath = this.getShopifyConnectedStoreDomain();

        const response = await fetch(`${rootPath}products/${handle}.js`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const product = await response.json();

        const shopifyDomain = findShopifyDomain();
        return {
          id: product.id,
          title: product.title,
          url: product.url,
          handle: handle,
          shopify_domain: shopifyDomain || window.location.hostname
        };
      } catch (error) {
        console.warn("Failed to fetch Shopify product data:", error);
        return null;
      }
    }

    // Fetch cart currency from Shopify API
    async fetchCartCurrency() {
      // HEADLESS STORE SUPPORT: Skip cart.js fetch for headless stores
      if (this.isHeadlessStore()) {
        // Emit event for headless store to provide currency if needed
        const event = new CustomEvent("siena:currency:request", {
          detail: {
            callback: (currency) => {
              this.currency = currency;
            }
          },
          bubbles: true,
          cancelable: true
        });
        document.dispatchEvent(event);

        // Use Shopify.currency if available, otherwise default
        if (
          window.Shopify &&
          window.Shopify.currency &&
          window.Shopify.currency.active
        ) {
          return window.Shopify.currency.active;
        }
        return "USD"; // Default fallback for headless
      }

      try {
        // Check if window.Shopify.routes.root exists, if not use default path
        let rootPath = this.getShopifyConnectedStoreDomain();
        const response = await fetch(`${rootPath}cart.js`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const cart = await response.json();
        return cart.currency || "USD"; // Default to USD if currency not found
      } catch (error) {
        console.warn("Failed to fetch cart currency:", error);
        return "USD"; // Default fallback
      }
    }

    // On-demand currency fetch triggered by iframe when product cards render (EFE-823)
    // Uses promise deduplication to ensure only one cart.js fetch fires
    async handleFetchCurrency() {
      if (!this.currencyFetched) {
        if (!this._currencyFetchPromise) {
          this._currencyFetchPromise = (async () => {
            const currencyFetchStart = performance.now();
            this.currency = await Promise.race([
              this.fetchCartCurrency(),
              new Promise((resolve) => setTimeout(() => resolve("USD"), 5000))
            ]);
            this.currencyFetched = true;
            const currencyFetchTime = performance.now() - currencyFetchStart;

            this.honeycomb.addEvent("widget.currency.fetched", {
              app_key: this.apiKeys?.token,
              duration_ms: currencyFetchTime,
              currency: this.currency,
              trigger: "on_demand",
              trace_id: this.getUnifiedTraceId()
            });
          })();
        }
        await this._currencyFetchPromise;
      }
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          { action: "setCurrency", currency: this.currency },
          "*"
        );
      }
    }

    originFromProductUrl(productUrl) {
      if (!productUrl || typeof productUrl !== "string") return null;
      try {
        const url = /^https?:\/\//i.test(productUrl)
          ? new URL(productUrl)
          : new URL(productUrl, window.location.origin);
        const host = url.hostname.toLowerCase();
        if (host === "shopify.com" || host === "www.shopify.com") return null;
        return url.origin;
      } catch (e) {
        return null;
      }
    }

    mapShopifyProductJs(product, shopifyDomain) {
      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        price: product.price,
        compare_at_price: product.compare_at_price,
        featured_image: product.featured_image,
        images: product.images || [],
        variants:
          (product.variants &&
            product.variants.map((variant) => ({
              id: variant.id,
              title: variant.title,
              option1: variant.option1,
              option2: variant.option2,
              option3: variant.option3,
              price: variant.price,
              compare_at_price: variant.compare_at_price,
              available: variant.available,
              sku: variant.sku,
              options: [
                variant.option1,
                variant.option2,
                variant.option3
              ].filter(Boolean),
              featured_image: variant.featured_image,
              selling_plan_allocations: variant.selling_plan_allocations || []
            }))) ||
          [],
        options:
          (product.options &&
            product.options.map((option, index) => ({
              name: option.name || option,
              position: option.position || index + 1,
              values: option.values || []
            }))) ||
          [],
        available: product.available,
        rating_count: product.rating_count,
        shopify_url: `${shopifyDomain}/products/${product.handle}`,
        tags: product?.tags || [],
        selling_plan_groups: product.selling_plan_groups || [],
        requires_selling_plan: !!product.requires_selling_plan
      };
    }

    async fetchProductDataFromProductUrl(handle, productUrl) {
      const origin = this.originFromProductUrl(productUrl);
      if (!origin) return null;
      try {
        const response = await fetch(
          `${origin}/products/${encodeURIComponent(handle)}.js`
        );
        if (!response.ok) return null;
        const product = await response.json();
        return this.mapShopifyProductJs(product, origin);
      } catch (e) {
        return null;
      }
    }

    // Fetch product data by handle for iframe components
    async fetchProductDataByHandle(handle, productUrl) {
      try {
        if (!handle) return null;

        // HEADLESS STORE SUPPORT: Fetch product data from connected Shopify store
        if (this.isHeadlessStore()) {
          // First, emit event for headless store to optionally provide product data
          const eventDetail = {
            handle: handle,
            _productData: null // Will be set by event handler if available
          };

          const event = new CustomEvent("siena:product:fetch", {
            detail: eventDetail,
            bubbles: true,
            cancelable: true
          });
          document.dispatchEvent(event);

          // If event handler provided product data, use it
          if (eventDetail._productData) {
            return eventDetail._productData;
          }

          // For headless, fetch from the connected Shopify store domain
          const connectedDomain = this.chatSettings?.connected_store_domain;
          if (connectedDomain) {
            try {
              // Fetch product data from Shopify's public .js endpoint, in the
              // storefront language when localization is on (EAA-3327).
              const response = await this.fetchLocalizedProductJs(
                connectedDomain,
                handle
              );

              if (!response) {
                return this.fetchProductDataFromProductUrl(handle, productUrl);
              }

              const product = await response.json();
              const shopifyDomain = `https://${connectedDomain}`;
              return {
                ...this.mapShopifyProductJs(product, shopifyDomain),
                description: product.description,
                headless: true
              };
            } catch (fetchError) {
              return this.fetchProductDataFromProductUrl(handle, productUrl);
            }
          }

          return this.fetchProductDataFromProductUrl(handle, productUrl);
        }

        // NATIVE SHOPIFY: Fetch from products endpoint
        let rootPath = this.getShopifyConnectedStoreDomain();

        const response = await fetch(`${rootPath}products/${handle}.js`);

        if (!response.ok) {
          return this.fetchProductDataFromProductUrl(handle, productUrl);
        }

        const product = await response.json();
        return this.mapShopifyProductJs(product, window.location.origin);
      } catch (error) {
        console.warn("Failed to fetch product data by handle:", error);
        return this.fetchProductDataFromProductUrl(handle, productUrl);
      }
    }

    // Check if the chat should be displayed based on settings
    shouldDisplayChat(settings) {
      const { is_visible_mobile, is_visible_desktop, embedded_search_enabled } =
        settings;
      if (!is_visible_mobile && !is_visible_desktop && !embedded_search_enabled)
        return false;
      const show = this.isMobile ? is_visible_mobile : is_visible_desktop;
      return show || embedded_search_enabled;
    }

    // ========================================
    // PROACTIVE CAMPAIGNS
    // ========================================

    /**
     * Fetch proactive campaigns from the API
     * @returns {Promise<Array>} Array of active proactive campaigns
     */
    async fetchProactiveCampaigns() {
      try {
        // Include the appKey as query parameter (same pattern as settings endpoint)
        const url = `${this.config.API_BASE}/v1/live_chat/proactive_campaigns?key=${this.apiKeys?.token}`;
        const headers = {
          "Content-Type": "application/json"
        };

        const response = await fetch(url, {
          method: "GET",
          headers
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const result = await response.json();

        // API returns { data: [...campaigns] }
        const campaigns = result?.data || [];

        if (isDevelopment()) {
          console.log(
            "[Proactive] Fetched campaigns from API:",
            campaigns.length
          );
        }

        return campaigns;
      } catch (e) {
        console.warn("[Proactive] Failed to fetch campaigns from API:", e);
        return [];
      }
    }

    /**
     * Initialize proactive campaigns after widget is created
     */
    async initProactiveCampaigns() {
      // Skip on mobile with center positioning
      if (this.isMobile && this.isMobileCenterPositioned()) {
        if (isDevelopment()) {
          console.log("[Proactive] Skipped: Mobile center positioning");
        }
        return;
      }

      // Fetch campaigns from API
      this.proactiveCampaigns = await this.fetchProactiveCampaigns();

      if (this.proactiveCampaigns.length === 0) {
        if (isDevelopment()) {
          console.log("[Proactive] No active campaigns found");
        }
        return;
      }

      // Find matching campaign for current page
      const matchingCampaign = this.findMatchingCampaign();
      if (!matchingCampaign) {
        if (isDevelopment()) {
          console.log("[Proactive] No matching campaign for current URL");
        }
        return;
      }

      // Check if already shown this session
      if (this.hasShownCampaignThisSession(matchingCampaign.uuid)) {
        if (isDevelopment()) {
          console.log(
            "[Proactive] Campaign already shown this session:",
            matchingCampaign.name
          );
        }
        return;
      }

      // Set up triggers
      this.setupProactiveTriggers(matchingCampaign);
    }

    /**
     * Check if mobile center positioning is enabled
     */
    isMobileCenterPositioned() {
      const settings = this.chatSettings;
      if (!settings) return false;

      return (
        settings.independent_mobile_positioning &&
        settings.mobile_positioning &&
        settings.mobile_positioning.includes("CENTER")
      );
    }

    /**
     * Find the first campaign that matches the current URL
     * Skips campaigns that were dismissed within 24 hours
     */
    findMatchingCampaign() {
      const currentUrl = window.location.pathname;

      for (const campaign of this.proactiveCampaigns) {
        // Skip if dismissed within 24 hours
        if (this.isCampaignDismissed(campaign.uuid)) {
          if (isDevelopment()) {
            console.log(
              "[Proactive] Skipping dismissed campaign:",
              campaign.name
            );
          }
          continue;
        }

        // Check exclusions first
        if (this.isUrlExcluded(currentUrl, campaign.exclude_urls)) {
          continue;
        }

        // Check if targets all pages
        if (campaign.target_all_pages) {
          return campaign;
        }

        // Check specific URLs
        if (this.isUrlMatched(currentUrl, campaign.target_urls)) {
          return campaign;
        }
      }

      return null;
    }

    /**
     * Check if URL matches any exclusion patterns
     */
    isUrlExcluded(url, excludeUrls) {
      if (!excludeUrls || excludeUrls.length === 0) return false;
      return excludeUrls.some((pattern) => this.matchUrlPattern(url, pattern));
    }

    /**
     * Check if URL matches any target patterns
     */
    isUrlMatched(url, targetUrls) {
      if (!targetUrls || targetUrls.length === 0) return false;
      return targetUrls.some((pattern) => this.matchUrlPattern(url, pattern));
    }

    /**
     * Match URL against a pattern with wildcard support
     * Supports: /products/*, /collections/star/items, exact matches
     * (where 'star' represents the * wildcard character)
     */
    matchUrlPattern(url, pattern) {
      if (!pattern) return false;

      // Normalize pattern
      pattern = pattern.trim();
      if (!pattern.startsWith("/")) {
        // Handle full URLs by extracting pathname
        try {
          const patternUrl = new URL(pattern, window.location.origin);
          pattern = patternUrl.pathname;
        } catch (e) {
          // If not a valid URL, assume it's a path
          pattern = "/" + pattern;
        }
      }

      // Convert wildcard pattern to regex
      // Escape special regex characters except *
      const regexPattern = pattern
        .replace(/[.+?^${}()|[\]\\]/g, "\\$&") // Escape special chars
        .replace(/\*/g, ".*"); // Convert * to .*

      const regex = new RegExp("^" + regexPattern + "$", "i");
      return regex.test(url);
    }

    /**
     * Check if campaign was shown this session (for page navigation)
     */
    hasShownCampaignThisSession(campaignUuid) {
      try {
        const shownKey = "siena_proactive_shown_" + campaignUuid;
        return sessionStorage.getItem(shownKey) === "true";
      } catch (e) {
        return false;
      }
    }

    /**
     * Mark campaign as shown this session (for page navigation)
     */
    markCampaignShown(campaignUuid) {
      try {
        const shownKey = "siena_proactive_shown_" + campaignUuid;
        sessionStorage.setItem(shownKey, "true");
      } catch (e) {
        console.warn("[Proactive] Failed to save shown state:", e);
      }
    }

    /**
     * Check if a campaign was dismissed within the last 24 hours
     */
    isCampaignDismissed(campaignUuid) {
      try {
        const dismissedData = this.safeLocalStorageGet(
          "siena_proactive_dismissed"
        );
        if (!dismissedData) return false;

        const dismissed = JSON.parse(dismissedData);
        const dismissedAt = dismissed[campaignUuid];
        if (!dismissedAt) return false;

        // Check if dismissed within last 24 hours
        const twentyFourHours = 24 * 60 * 60 * 1000;
        const now = Date.now();
        return now - dismissedAt < twentyFourHours;
      } catch (e) {
        return false;
      }
    }

    /**
     * Mark a campaign as dismissed for 24 hours
     */
    dismissCampaign(campaignUuid) {
      try {
        const dismissedData = this.safeLocalStorageGet(
          "siena_proactive_dismissed"
        );
        const dismissed = dismissedData ? JSON.parse(dismissedData) : {};

        // Add this campaign with current timestamp
        dismissed[campaignUuid] = Date.now();

        // Clean up old entries (older than 24 hours)
        const twentyFourHours = 24 * 60 * 60 * 1000;
        const now = Date.now();
        for (const uuid in dismissed) {
          if (now - dismissed[uuid] >= twentyFourHours) {
            delete dismissed[uuid];
          }
        }

        this.safeLocalStorageSet(
          "siena_proactive_dismissed",
          JSON.stringify(dismissed)
        );
      } catch (e) {
        console.warn("[Proactive] Failed to save dismissed state:", e);
      }
    }

    /**
     * Set up triggers for a proactive campaign
     */
    setupProactiveTriggers(campaign) {
      this.activeProactiveCampaign = campaign;
      this.proactiveTriggersActive = true;

      if (campaign.trigger_type === "time") {
        // Time-based trigger
        const delayMs = (campaign.trigger_value || 5) * 1000;
        if (isDevelopment()) {
          console.log(
            `[Proactive] Setting up time trigger: ${campaign.trigger_value}s`
          );
        }
        this.proactiveTimeoutId = setTimeout(() => {
          this.triggerProactivePopup(campaign);
        }, delayMs);
      } else if (campaign.trigger_type === "scroll") {
        // Scroll-based trigger
        const targetPercent = campaign.trigger_value || 50;
        if (isDevelopment()) {
          console.log(
            `[Proactive] Setting up scroll trigger: ${targetPercent}%`
          );
        }
        this.proactiveScrollHandler = () => {
          const scrollPercent = this.getScrollPercent();
          if (scrollPercent >= targetPercent && this.proactiveTriggersActive) {
            this.triggerProactivePopup(campaign);
          }
        };
        window.addEventListener("scroll", this.proactiveScrollHandler, {
          passive: true
        });
      }
    }

    /**
     * Get current scroll percentage
     */
    getScrollPercent() {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      if (scrollHeight <= 0) return 100;
      return Math.round((scrollTop / scrollHeight) * 100);
    }

    /**
     * Trigger the proactive popup
     */
    triggerProactivePopup(campaign) {
      if (!this.proactiveTriggersActive) return;

      // Disable triggers to prevent re-triggering during this page view
      this.cleanupProactiveTriggers();

      // Don't mark as shown yet - only mark when user clicks close button
      // This allows the popup to show again if user just clicks to open widget

      // Show popup
      this.showProactivePopup(campaign);

      // Track event
      this.honeycomb.addEvent("proactive.campaign_triggered", {
        organization_uuid: this.chatSettings?.organization_uuid,
        campaign_uuid: campaign.uuid,
        campaign_name: campaign.name,
        trigger_type: campaign.trigger_type,
        trigger_value: campaign.trigger_value,
        page_url: window.location.href,
        is_mobile: this.isMobile,
        positioning: this.chatSettings?.positioning,
        trace_id: this.getUnifiedTraceId()
      });
    }

    /**
     * Clean up proactive triggers
     */
    cleanupProactiveTriggers() {
      this.proactiveTriggersActive = false;

      if (this.proactiveTimeoutId) {
        clearTimeout(this.proactiveTimeoutId);
        this.proactiveTimeoutId = null;
      }

      if (this.proactiveScrollHandler) {
        window.removeEventListener("scroll", this.proactiveScrollHandler);
        this.proactiveScrollHandler = null;
      }
    }

    /**
     * Show the proactive popup
     */
    showProactivePopup(campaign) {
      // Remove any existing popup
      this.hideProactivePopup();

      // Get positioning
      const isLeftPositioned = this.chatSettings?.positioning === "LEFT";

      // Create wrapper for popup + triangle
      const wrapper = document.createElement("div");
      wrapper.id = "siena-proactive-popup";
      wrapper.className = "siena-proactive-popup";
      wrapper.style.cssText = `
        position: fixed;
        bottom: 84px;
        ${isLeftPositioned ? "left: 14px;" : "right: 14px;"}
        z-index: 2147483646;
        opacity: 0;
        transform: translateY(10px);
        transition: opacity 0.5s ease, transform 0.5s ease;
      `;

      // Create popup bubble
      const popup = document.createElement("div");
      popup.style.cssText = `
        position: relative;
        max-width: 280px;
        background: white;
        border-radius: 16px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
        border: 1px solid rgba(0, 0, 0, 0.06);
        padding: 12px 16px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.5;
        color: #18181b;
        cursor: pointer;
      `;

      // Create triangle pointer
      const triangle = document.createElement("div");
      triangle.style.cssText = `
        position: absolute;
        bottom: -8px;
        ${isLeftPositioned ? "left: 24px;" : "right: 24px;"}
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid white;
        filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.05));
      `;
      popup.appendChild(triangle);

      // Mobile adjustments
      if (this.isMobile) {
        wrapper.style.bottom = "80px";
        wrapper.style.left = isLeftPositioned ? "16px" : "auto";
        wrapper.style.right = isLeftPositioned ? "auto" : "16px";
        popup.style.maxWidth = "260px";
      }

      // Create message content
      const messageSpan = document.createElement("span");
      messageSpan.textContent = campaign.message;
      popup.appendChild(messageSpan);

      // Create close button (hidden by default, shown on hover)
      const closeBtn = document.createElement("button");
      closeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;
      closeBtn.style.cssText = `
        position: absolute;
        top: -8px;
        right: -8px;
        width: 24px;
        height: 24px;
        border: 1px solid #e4e4e7;
        background: #f4f4f5;
        border-radius: 50%;
        color: #71717a;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        padding: 0;
        opacity: 0;
        transform: scale(0);
      `;

      // Mobile: Always show close button with larger touch target
      if (this.isMobile) {
        closeBtn.style.width = "28px";
        closeBtn.style.height = "28px";
        closeBtn.style.opacity = "1";
        closeBtn.style.transform = "scale(1)";
      }

      // Show/hide close button on hover (desktop only)
      if (!this.isMobile) {
        wrapper.onmouseenter = () => {
          closeBtn.style.opacity = "1";
          closeBtn.style.transform = "scale(1)";
        };
        wrapper.onmouseleave = () => {
          closeBtn.style.opacity = "0";
          closeBtn.style.transform = "scale(0)";
        };
      }

      closeBtn.onmouseenter = () => {
        closeBtn.style.background = "#e4e4e7";
        closeBtn.style.color = "#18181b";
      };
      closeBtn.onmouseleave = () => {
        closeBtn.style.background = "#f4f4f5";
        closeBtn.style.color = "#71717a";
      };
      closeBtn.onclick = (e) => {
        e.stopPropagation();
        // Dismiss this specific campaign for 24 hours (allows other campaigns to show)
        this.dismissCampaign(campaign.uuid);
        this.markCampaignShown(campaign.uuid);
        this.hideProactivePopup();
        this.cleanupProactiveTriggers();
        this.honeycomb.addEvent("proactive.popup_closed", {
          organization_uuid: this.chatSettings?.organization_uuid,
          campaign_uuid: campaign.uuid,
          campaign_name: campaign.name,
          trigger_type: campaign.trigger_type,
          trigger_value: campaign.trigger_value,
          page_url: window.location.href,
          is_mobile: this.isMobile,
          positioning: this.chatSettings?.positioning,
          close_method: "button",
          dismiss_duration_hours: 24,
          trace_id: this.getUnifiedTraceId()
        });
      };
      popup.appendChild(closeBtn);

      // Click popup to open widget - dismiss campaign and track for conversion
      popup.onclick = () => {
        // Immediately dismiss this campaign for 24 hours (won't show again on redirect)
        this.dismissCampaign(campaign.uuid);

        // Store proactive campaign data for conversion tracking
        const proactiveCampaignData = {
          uuid: campaign.uuid,
          name: campaign.name,
          trigger_type: campaign.trigger_type,
          trigger_value: campaign.trigger_value,
          page_url: window.location.href,
          clicked_at: Date.now(),
          organization_uuid: this.chatSettings?.organization_uuid
        };

        this.hideProactivePopup();
        // Open widget and pass proactive campaign data to iframe
        this.openWidgetFromProactive(proactiveCampaignData);

        this.honeycomb.addEvent("proactive.popup_clicked", {
          organization_uuid: this.chatSettings?.organization_uuid,
          campaign_uuid: campaign.uuid,
          campaign_name: campaign.name,
          trigger_type: campaign.trigger_type,
          trigger_value: campaign.trigger_value,
          page_url: window.location.href,
          is_mobile: this.isMobile,
          positioning: this.chatSettings?.positioning,
          trace_id: this.getUnifiedTraceId()
        });
      };

      // Assemble
      wrapper.appendChild(popup);

      // Add to page
      document.body.appendChild(wrapper);
      this.proactivePopupElement = wrapper;

      // Animate in - double rAF ensures browser paints initial state before transitioning
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          wrapper.style.opacity = "1";
          wrapper.style.transform = "translateY(0)";
        });
      });

      // Track impression (only once per campaign per session)
      const shownCampaignsKey = "siena_proactive_shown_campaigns";
      let shownCampaigns = [];
      try {
        const stored = this.safeStorageGet(shownCampaignsKey);
        if (stored) {
          shownCampaigns = JSON.parse(stored);
        }
      } catch (e) {
        shownCampaigns = [];
      }

      if (!shownCampaigns.includes(campaign.uuid)) {
        // First time showing this campaign in this session
        shownCampaigns.push(campaign.uuid);
        this.safeStorageSet(shownCampaignsKey, JSON.stringify(shownCampaigns));

        this.honeycomb.addEvent("proactive.popup_shown", {
          organization_uuid: this.chatSettings?.organization_uuid,
          campaign_uuid: campaign.uuid,
          campaign_name: campaign.name,
          trigger_type: campaign.trigger_type,
          trigger_value: campaign.trigger_value,
          page_url: window.location.href,
          is_mobile: this.isMobile,
          positioning: this.chatSettings?.positioning,
          trace_id: this.getUnifiedTraceId()
        });
      }
    }

    /**
     * Hide and remove the proactive popup
     */
    hideProactivePopup() {
      if (this.proactivePopupElement) {
        this.proactivePopupElement.style.opacity = "0";
        this.proactivePopupElement.style.transform =
          "translateY(10px) scale(0.95)";
        setTimeout(() => {
          if (
            this.proactivePopupElement &&
            this.proactivePopupElement.parentNode
          ) {
            this.proactivePopupElement.parentNode.removeChild(
              this.proactivePopupElement
            );
          }
          this.proactivePopupElement = null;
        }, 300);
      }
    }

    /**
     * Open the widget programmatically
     */
    openWidget() {
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          {
            action: "openWidget",
            traceContext: {
              traceId: this.getUnifiedTraceId(),
              sessionId: this.currentSessionUuid,
              source: "proactive_popup"
            }
          },
          "*"
        );
      }
    }

    /**
     * Open widget from proactive campaign click - passes campaign data for conversion tracking
     */
    openWidgetFromProactive(proactiveCampaignData) {
      console.log(
        "[Proactive] Opening widget with campaign data:",
        proactiveCampaignData
      );
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          {
            action: "openWidget",
            proactiveCampaignData: proactiveCampaignData,
            traceContext: {
              traceId: this.getUnifiedTraceId(),
              sessionId: this.currentSessionUuid,
              source: "proactive_popup"
            }
          },
          "*"
        );
      }
    }

    // Create and append the iframe
    createWebchat(hostname, sienaCookie) {
      try {
        // Prevent duplicate iframe creation
        const existingIframe = document.getElementById("SIENA_CHAT_IFRAME");
        if (this.iframe || existingIframe) {
          console.warn("Siena chat iframe already exists");
          if (existingIframe && !this.iframe) {
            this.iframe = existingIframe;
          }
          return;
        }

        this.iframe = document.createElement("iframe");
        this.configureIframe();
        document.body.appendChild(this.iframe);
        applyPositionOffsets(this.iframe, this.chatSettings || {});
        this.setInitialData(hostname, sienaCookie);
        this.attachEventListeners();
      } catch (error) {
        sendErrorToSentry(error, {
          method: "createWebchat",
          hostname,
          cookie: sienaCookie || "NONE",
          chatSettings: JSON.stringify(this.chatSettings)
        });
      }
    }

    // Configure iframe attributes and classes
    configureIframe() {
      const {
        positioning,
        independent_mobile_positioning,
        mobile_positioning,
        show_bubble,
        search_bar_mobile
      } = this.chatSettings;

      let positionClass = "";
      let mobileClass = "";

      if (this.isMobile) {
        // Mobile positioning logic
        if (independent_mobile_positioning) {
          mobileClass = `siena-iframe--mobile-${mobile_positioning
            .toLowerCase()
            .replace("_", "-")}`;
        } else if (mobile_positioning && mobile_positioning.includes("LEFT")) {
          positionClass = "siena-iframe--left";
        } else {
          positionClass = "siena-iframe--right";
        }
      } else {
        // Desktop positioning logic - never affected by mobile settings
        positionClass =
          positioning === "RIGHT"
            ? "siena-iframe--right"
            : "siena-iframe--left";
        // Ensure mobile class is never set on desktop
        mobileClass = "";
      }

      // Filter out empty strings and add the remaining classes
      const classesToAdd = ["siena-iframe", positionClass, mobileClass].filter(
        Boolean
      );
      this.iframe.classList.add(...classesToAdd);
      Object.assign(this.iframe, {
        src: this.config.IFRAME_SRC,
        crossorigin: "anonymous",
        allowFullScreen: true,
        // EFE-1247 voice input: speech recognition needs the mic, which a cross-origin iframe only gets with this.
        allow: "fullscreen; autoplay; microphone",
        loading: "lazy",
        id: "SIENA_CHAT_IFRAME",
        title: "Siena AI Chat"
      });

      // Set width to 0 if search bar is enabled for the current device, show_bubble is false,
      // or if widget visibility is turned off for current device
      const isVisibilityOff = this.isMobile
        ? !this.chatSettings.is_visible_mobile
        : !this.chatSettings.is_visible_desktop;

      if (
        (this.isMobile &&
          search_bar_mobile === true &&
          !this.searchBarClosed) ||
        show_bubble === false ||
        (!this.chatSettings.is_visible_mobile &&
          !this.chatSettings.is_visible_desktop &&
          this.chatSettings.embedded_search_enabled) ||
        isVisibilityOff
      ) {
        this.iframe.style.width = "0";
        this.iframe.style.height = "0";
      } else {
        // Set initial width based on chat button text
        const bubbleWidth = this.calculateBubbleWidth(
          this.chatSettings?.chat_button_text,
          this.isMobile,
          this.chatSettings?.independent_mobile_positioning
        );
        this.iframe.style.setProperty("width", `${bubbleWidth}px`, "important");
      }
    }

    // Set initial data to the iframe
    setInitialData(hostname, sienaCookie) {
      const appData = {
        chatUUID: this.apiKeys.chatUUID,
        chatSettings: this.chatSettings,
        hostname,
        appKey: this.apiKeys.token,
        isMobile: this.isMobile,
        view: sienaCookie,
        currentPageUrl: window.location.href,
        // ECH-1244: the widget fetches its own starter chips by this URL; on a
        // landing page with a pinned product it is that product's PDP URL.
        // startersAllowed lifts the widget's product-page gate on pages where
        // the merchant placed a starters surface.
        startersUrl: this.getStartersUrl(),
        startersAllowed: this.startersAllowedOnPage(),
        productData: this.productData,
        currency: this.currency,
        locale: this.resolveStorefrontLocale()
      };
      this.appData = appData;
    }


    // EAA-3327: the storefront's active language, NOT the browser's.
    // 1. Native Shopify themes expose window.Shopify.locale ("fr-CA" on a
    //    /fr-ca storefront).
    // 2. Headless (Hydrogen) stores have no Shopify global; they route the
    //    language as the first URL segment ("/fr", "/fr-ca") - that is the
    //    signal the shopper actually chose, so it beats <html lang>, which
    //    some headless apps derive from Accept-Language instead.
    // 3. <html lang>.
    // 4. Browser language (navigator.languages) only when the page says
    //    nothing at all - agreed with Nico as the last resort.
    // The iframe decides whether it supports the locale.
    resolveStorefrontLocale() {
      try {
        const shopifyLocale = window.Shopify && window.Shopify.locale;
        if (typeof shopifyLocale === "string" && shopifyLocale) {
          this.locale = shopifyLocale;
          return this.locale;
        }
        const pathLocale = this.getLocaleFromPath(window.location.pathname);
        if (pathLocale) {
          this.locale = pathLocale;
          return this.locale;
        }
        const htmlLang =
          document.documentElement && document.documentElement.lang;
        if (typeof htmlLang === "string" && htmlLang) {
          this.locale = htmlLang;
          return this.locale;
        }
        const browserLangs =
          (navigator.languages && navigator.languages.length
            ? navigator.languages
            : [navigator.language]) || [];
        if (typeof browserLangs[0] === "string" && browserLangs[0]) {
          this.locale = browserLangs[0];
          return this.locale;
        }
      } catch (e) {
        // ignore - fall through to default
      }
      return this.locale;
    }

    // "/fr/products/x" -> "fr", "/fr-ca/" -> "fr-ca", "/products/x" -> null.
    // The segment must look like a locale ("xx" or "xx-yy", "_" accepted)
    // AND its language part must be a real language code, so country or
    // short routes like "/us/", "/uk/", "/id/123" are not mistaken for one.
    getLocaleFromPath(pathname) {
      const first = (pathname || "").split("/").filter(Boolean)[0] || "";
      const m = /^([a-z]{2})(?:[-_]([a-z]{2}))?$/i.exec(first);
      if (!m || !SienaAIChat.KNOWN_LANGUAGES.has(m[1].toLowerCase())) {
        return null;
      }
      return m[2] ? `${m[1].toLowerCase()}-${m[2].toLowerCase()}` : m[1].toLowerCase();
    }

    // EAA-3327: fetch Shopify's public /products/{handle}.js from the
    // connected store in the storefront language (only when localization is
    // on for this org), falling back to the bare URL. Returns the Response
    // (ok) or null.
    async fetchLocalizedProductJs(connectedDomain, handle) {
      const productLocale = this.chatSettings?.is_localization_enabled
        ? this.resolveStorefrontLocale()
        : "";
      for (const prefix of this.getLocalePathCandidates(productLocale)) {
        const response = await fetch(
          `https://${connectedDomain}/${prefix}products/${handle}.js`
        );
        if (response.ok) return response;
      }
      return null;
    }

    // Storefront URL prefixes to try for a locale, most specific first:
    // "fr-CA" -> ["fr-ca/", "fr/", ""]; "en" -> ["en/", ""]; "" -> [""].
    // Shopify serves translated /products/{handle}.js under the language
    // prefix it publishes ("/fr/" or "/fr-ca/"), and 404s the others.
    getLocalePathCandidates(locale) {
      const tag = (locale || "").toLowerCase().replace("_", "-");
      const candidates = [];
      if (tag) candidates.push(`${tag}/`);
      const base = tag.split("-")[0];
      if (base && base !== tag) candidates.push(`${base}/`);
      candidates.push("");
      return candidates;
    }

    // Attach event listeners for resize and messages
    attachEventListeners() {
      window.addEventListener("message", (e) => this.handleAllMessages(e));
      window.addEventListener("resize", this.handleWindowResize);
      window.addEventListener("load", () => this.sendWindowSize());
      this.watchPageUrl();
    }

    // ECH-1343: headless storefronts change pages with history.pushState and
    // never reload this script, so typed messages would keep the first URL.
    // Polling catches every router (pushState, replaceState, back/forward,
    // hash) without patching the storefront's history object.
    watchPageUrl() {
      let lastUrl = window.location.href;
      setInterval(() => {
        const url = window.location.href;
        if (url === lastUrl) return;
        lastUrl = url;
        if (this.appData) this.appData.currentPageUrl = url;
        if (this.iframe && this.iframe.contentWindow) {
          this.iframe.contentWindow.postMessage(
            { action: "setCurrentPageUrl", url },
            "*"
          );
        }
      }, 500);
    }

    handlePong(event) {
      setTimeout(() => {
        if (this.iframe && this.iframe.contentWindow) {
          this.iframe.contentWindow.postMessage(
            {
              action: "init",
              data: this.appData,
              traceContext: {
                traceId: this.getUnifiedTraceId(), // SIMPLE: Just send the localStorage trace_id
                sessionId: this.currentSessionUuid,
                source: "parent_widget"
              }
            },
            "*"
          );
          this.sendWindowSize();
          // The pending sendMessage queue is drained from
          // handleWidgetInitializedAck — fired by the widget React app via a
          // "widgetInitialized" postMessage AFTER SET_INITIAL_DATA reducer
          // commits and state.appKey is observable. No timing-based delays.
        }
      }, 0);
    }

    handleWidgetInitializedAck() {
      this.widgetInitialized = true;
      if (
        Array.isArray(this.pendingPostInitMessages) &&
        this.pendingPostInitMessages.length
      ) {
        const drained = this.pendingPostInitMessages.splice(0);
        drained.forEach((sendFn) => {
          try { sendFn(); } catch (_) {}
        });
      }
    }

    updateCartCountUI(cart) {
      // Update cart count in the UI - handle multiple cart count element types
      const cartCountElement = document.querySelector("[data-cart-count]");
      if (cartCountElement) {
        cartCountElement.textContent = cart.item_count;
      }

      // Handle modal cart button with data-cart-size
      const cartSizeElement = document.querySelector("[data-cart-size]");
      if (cartSizeElement) {
        cartSizeElement.setAttribute("data-cart-size", cart.item_count);
        const cartSizeSpan = cartSizeElement.querySelector("span");
        if (cartSizeSpan) {
          cartSizeSpan.textContent = cart.item_count;
        }
      }

      // Update cart count container visibility
      const cartCountContainer = document.querySelector(
        "#CartCount, .site-header__cart-count, [data-cart-count-bubble]"
      );
      if (cartCountContainer) {
        if (cart.item_count > 0) {
          // Show cart count when there are items
          cartCountContainer.classList.remove("hide", "critical-hidden");
        } else {
          // Hide cart count when cart is empty
          cartCountContainer.classList.add("hide", "critical-hidden");
        }
      }

      // Handle cart-count-bubble visibility (for modal cart buttons)
      const cartBubble = document.querySelector(".cart-count-bubble");
      if (cartBubble) {
        if (cart.item_count > 0) {
          // Show cart bubble when there are items
          cartBubble.classList.remove("hide", "critical-hidden");
          cartBubble.style.display = "";
        } else {
          // Hide cart bubble when cart is empty
          cartBubble.classList.add("hide", "critical-hidden");
          cartBubble.style.display = "none";
        }
      }

      // Handle cart icon styling (add cart-populated class when cart has items)
      const cartIcon = document.querySelector(
        ".icon-bag, .header__icon--cart svg"
      );
      if (cartIcon) {
        if (cart.item_count > 0) {
          // Add cart-populated class when cart has items
          cartIcon.classList.add("cart-populated");
        } else {
          // Remove cart-populated class when cart is empty
          cartIcon.classList.remove("cart-populated");
        }
      }
    }

    handleAddToCart(productId, quantity = 1, productInfo = null, sellingPlan = null) {
      // Start tracing span for cart operation
      const cartSpan = this.honeycomb.startSpan("handle_add_to_cart");
      cartSpan.addField("product_id", productId);
      cartSpan.addField("quantity", quantity);
      cartSpan.addField("product_title", productInfo?.title || "unknown");
      cartSpan.addField("has_product_info", !!productInfo);
      cartSpan.addField("is_headless", this.isHeadlessStore());
      cartSpan.addField("selling_plan_id", sellingPlan || null);
      cartSpan.addField("is_subscription", !!sellingPlan);

      const startTime = performance.now();

      // HEADLESS STORE SUPPORT: Emit event for external handling instead of CORS-failing fetch
      if (this.isHeadlessStore()) {
        // Timeout for waiting for handler response (5 seconds)
        const HEADLESS_HANDLER_TIMEOUT = 5000;

        return new Promise((resolveCart, rejectCart) => {
          let handled = false;
          let timeoutId = null;

          // Resolve callback for handler to call on success
          const resolve = (result = {}) => {
            if (handled) return;
            handled = true;
            if (timeoutId) clearTimeout(timeoutId);

            const headlessDuration = performance.now() - startTime;
            cartSpan.addField("success", true);
            cartSpan.addField("headless_mode", true);
            cartSpan.addField("handler_responded", true);
            cartSpan.addField("duration_ms", headlessDuration);
            cartSpan.finish();

            this.honeycomb.addEvent("cart.add_headless_success", {
              product_id: productId,
              quantity: quantity,
              product_title: productInfo?.title || "unknown",
              handler_result: JSON.stringify(result).substring(0, 200),
              timestamp: Date.now(),
              trace_id: this.getUnifiedTraceId()
            });

            // Notify iframe of success
            if (this.iframe && this.iframe.contentWindow) {
              this.iframe.contentWindow.postMessage(
                {
                  action: "addToCartResponse",
                  data: { item_count: quantity, headless: true, ...result },
                  product: productInfo,
                  headless: true,
                  traceContext: {
                    traceId: this.getUnifiedTraceId(),
                    sessionId: this.currentSessionUuid,
                    source: "parent_widget"
                  }
                },
                "*"
              );
            }

            // Update cart count UI if result contains item_count (same as native Shopify)
            if (result && typeof result.item_count === "number") {
              this.updateCartCountUI({ item_count: result.item_count });
            }

            // Dispatch cart updated event for theme/framework compatibility (same as native Shopify)
            document.body.dispatchEvent(
              new CustomEvent("siena:cart:updated", {
                detail: {
                  cart: { item_count: result?.item_count || quantity, headless: true },
                  source: "siena-chatbot",
                  action: "add-to-cart",
                  headless: true
                }
              })
            );

            resolveCart({ headless: true, success: true, ...result });
          };

          // Reject callback for handler to call on failure
          const reject = (error = {}) => {
            if (handled) return;
            handled = true;
            if (timeoutId) clearTimeout(timeoutId);

            const errorMessage =
              typeof error === "string"
                ? error
                : error?.message || "Add to cart failed";

            const headlessDuration = performance.now() - startTime;
            cartSpan.addField("success", false);
            cartSpan.addField("headless_mode", true);
            cartSpan.addField("handler_responded", true);
            cartSpan.addField("error_message", errorMessage);
            cartSpan.addField("duration_ms", headlessDuration);
            cartSpan.finish();

            this.honeycomb.addEvent("cart.add_headless_error", {
              product_id: productId,
              quantity: quantity,
              product_title: productInfo?.title || "unknown",
              error_message: errorMessage,
              timestamp: Date.now(),
              trace_id: this.getUnifiedTraceId()
            });

            console.error("[Siena] Add to cart failed:", errorMessage);

            // Notify iframe of error
            if (this.iframe && this.iframe.contentWindow) {
              this.iframe.contentWindow.postMessage(
                {
                  action: "addToCartResponse",
                  error: errorMessage,
                  headless: true,
                  traceContext: {
                    traceId: this.getUnifiedTraceId(),
                    sessionId: this.currentSessionUuid,
                    source: "parent_widget"
                  }
                },
                "*"
              );
            }

            rejectCart({ headless: true, success: false, error: errorMessage });
          };

          const eventDetail = {
            variantId: productId,
            quantity: quantity,
            productInfo: productInfo,
            productTitle: productInfo?.title || "unknown",
            productId: productInfo?.id || null,
            sellingPlan: sellingPlan || null,
            // EAA-3263/EAA-3235: store's onAddToCart handler applies this via
            // its own cart implementation (e.g. Storefront API cartDiscountCodesUpdate).
            discountCode: this.chatSettings?.discount_code || null,
            // Provide resolve/reject callbacks for the handler
            resolve: resolve,
            reject: reject
          };

          // Dispatch event for headless store to handle
          const event = new CustomEvent("siena:cart:add", {
            detail: eventDetail,
            bubbles: true,
            cancelable: true
          });

          this.honeycomb.addEvent("cart.add_headless_event", {
            product_id: productId,
            quantity: quantity,
            product_title: productInfo?.title || "unknown",
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId()
          });

          document.dispatchEvent(event);

          // Set timeout for unresponsive handlers
          timeoutId = setTimeout(() => {
            if (handled) return;
            handled = true;

            const headlessDuration = performance.now() - startTime;
            cartSpan.addField("success", false);
            cartSpan.addField("headless_mode", true);
            cartSpan.addField("handler_responded", false);
            cartSpan.addField("error_message", "handler_timeout");
            cartSpan.addField("duration_ms", headlessDuration);
            cartSpan.finish();

            this.honeycomb.addEvent("cart.add_headless_timeout", {
              product_id: productId,
              quantity: quantity,
              product_title: productInfo?.title || "unknown",
              timeout_ms: HEADLESS_HANDLER_TIMEOUT,
              timestamp: Date.now(),
              trace_id: this.getUnifiedTraceId()
            });

            console.warn(
              "[Siena] Headless add-to-cart handler did not respond within " +
                HEADLESS_HANDLER_TIMEOUT +
                "ms. " +
                "Make sure you have implemented SienaHeadlessSetup({ onAddToCart: ... }) " +
                "and called detail.resolve() or detail.reject() in your handler. " +
                "See: https://docs.siena.cx/headless-shopify-setup"
            );

            // Notify iframe of timeout error
            if (this.iframe && this.iframe.contentWindow) {
              this.iframe.contentWindow.postMessage(
                {
                  action: "addToCartResponse",
                  error:
                    "Add to cart handler not configured. Please contact the store for assistance.",
                  headless: true,
                  handlerTimeout: true,
                  traceContext: {
                    traceId: this.getUnifiedTraceId(),
                    sessionId: this.currentSessionUuid,
                    source: "parent_widget"
                  }
                },
                "*"
              );
            }

            rejectCart({
              headless: true,
              success: false,
              error: "handler_timeout",
              handlerTimeout: true
            });
          }, HEADLESS_HANDLER_TIMEOUT);
        });
      }

      // NATIVE SHOPIFY: Use the standard cart/add.js endpoint
      let rootPath = this.getShopifyConnectedStoreDomain();

      const cartItem = {
        id: productId,
        quantity: quantity,
        properties: {
          "Added via": "Siena Chatbot"
        }
      };
      if (sellingPlan) {
        cartItem.selling_plan = Number(sellingPlan);
      }

      return fetch(`${rootPath}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [cartItem]
        })
      })
        .then((addResponse) => {
          // Shopify 422s valid-looking adds (e.g. selling-plan-only variants);
          // without this check the chain would apply discounts and record
          // cart.add_success for a rejected addition. The catch below already
          // emits cart.add_error and notifies the iframe.
          if (!addResponse.ok) {
            throw new Error(`cart/add.js HTTP ${addResponse.status}`);
          }
          return addResponse;
        })
        .then(() =>
          // EAA-3263: cart/update.js returns the full cart, so when a discount
          // code is configured it replaces the plain cart.js refresh — the UI
          // then renders discounted totals. Null result = no code or discount
          // failure; fall back to cart.js so the UI still updates.
          this.applyDiscountToNativeCart(rootPath).then(
            (cartWithDiscount) =>
              cartWithDiscount ||
              fetch(`${rootPath}cart.js`).then((r) => r.json())
          )
        )
        .then((cart) => {
          // Track successful cart addition
          this.honeycomb.addEvent("cart.add_success", {
            product_id: productId,
            quantity: quantity,
            cart_item_count: cart.item_count,
            cart_total_price: cart.total_price,
            product_title: productInfo?.title || "unknown",
            selling_plan_id: sellingPlan || null,
            is_subscription: !!sellingPlan,
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });

          // Complete tracing span successfully
          const cartDuration = performance.now() - startTime;
          cartSpan.addField("success", true);
          cartSpan.addField("cart_item_count", cart.item_count);
          cartSpan.addField("cart_total_price", cart.total_price);
          cartSpan.addField("duration_ms", cartDuration);
          cartSpan.finish();

          // Update cart count UI using dedicated function
          this.updateCartCountUI(cart);

          // Dispatch cart updated event for theme compatibility and cart modal integration
          document.body.dispatchEvent(
            new CustomEvent("siena:cart:updated", {
              detail: {
                cart: cart,
                source: "siena-chatbot",
                action: "add-to-cart"
              }
            })
          );

          // Notify storefront themes that listen for cart refresh requests.
          window.dispatchEvent(new CustomEvent("cart:update"));

          // Notify iframe of successful cart addition
          if (this.iframe && this.iframe.contentWindow) {
            this.iframe.contentWindow.postMessage(
              {
                action: "addToCartResponse",
                data: cart,
                product: productInfo,
                traceContext: {
                  traceId: this.getUnifiedTraceId(),
                  sessionId: this.currentSessionUuid,
                  source: "parent_widget"
                }
              },
              "*"
            );
          }
        })
        .catch((err) => {
          console.error("❌ Add to cart error:", err);

          // Track failed cart addition
          this.honeycomb.addEvent("cart.add_error", {
            product_id: productId,
            quantity: quantity,
            error_message: err.message || err.toString(),
            product_title: productInfo?.title || "unknown",
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });

          // Complete tracing span with error
          const cartErrorDuration = performance.now() - startTime;
          cartSpan.addField("success", false);
          cartSpan.addField("error_message", err.message || err.toString());
          cartSpan.addField("error_type", err.name || "Error");
          cartSpan.addField("duration_ms", cartErrorDuration);
          cartSpan.finish();

          // Notify iframe of error
          if (this.iframe && this.iframe.contentWindow) {
            this.iframe.contentWindow.postMessage(
              {
                action: "addToCartResponse",
                error: err.message,
                traceContext: {
                  traceId: this.getUnifiedTraceId(),
                  sessionId: this.currentSessionUuid,
                  source: "parent_widget"
                }
              },
              "*"
            );
          }
        });
    }

    handleTagShopifyCart(live_chat_session_uuid, live_chat_conversation_uuid) {
      // Track cart tagging event (separate from user ID management)
      this.honeycomb.addEvent("session.changed", {
        trigger_source: "tag_shopify_cart",
        session_uuid: live_chat_session_uuid,
        conversation_uuid: live_chat_conversation_uuid,
        has_existing_session: !!this.currentSessionUuid,
        session_changed: this.currentSessionUuid !== live_chat_session_uuid,
        operation_type: "cart_session_tagging",
        is_headless: this.isHeadlessStore(),
        timestamp: Date.now(),
        trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
      });

      // Update session/conversation tracking WITHOUT triggering user ID changes
      this.currentSessionUuid = live_chat_session_uuid;
      this.currentConversationUuid = live_chat_conversation_uuid;

      // HEADLESS STORE SUPPORT: Emit event for external handling
      if (this.isHeadlessStore()) {
        const eventDetail = {
          sessionUuid: live_chat_session_uuid,
          conversationUuid: live_chat_conversation_uuid,
          attributes: {
            live_chat_session_uuid,
            live_chat_conversation_uuid
          }
        };

        const event = new CustomEvent("siena:cart:tag", {
          detail: eventDetail,
          bubbles: true,
          cancelable: true
        });

        document.dispatchEvent(event);

        this.honeycomb.addEvent("session.tagged_headless_event", {
          session_uuid: live_chat_session_uuid,
          conversation_uuid: live_chat_conversation_uuid,
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId()
        });

        // Notify iframe of "success" for headless mode
        if (this.iframe && this.iframe.contentWindow) {
          this.iframe.contentWindow.postMessage(
            {
              action: "tagShopifyCart",
              data: { headless: true },
              headless: true
            },
            "*"
          );
        }

        return;
      }

      // NATIVE SHOPIFY: Use the standard cart/update.js endpoint
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          attributes: {
            live_chat_session_uuid,
            live_chat_conversation_uuid
          }
        })
      })
        .then((response) => response.json())
        .then((data) => {
          if (this.iframe && this.iframe.contentWindow) {
            this.iframe.contentWindow.postMessage(
              { action: "tagShopifyCart", data: data },
              "*"
            );
          }

          // Track successful session tagging
          this.honeycomb.addEvent("session.tagged_successfully", {
            session_uuid: live_chat_session_uuid,
            conversation_uuid: live_chat_conversation_uuid,
            cart_data_size: JSON.stringify(data).length,
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });
        })
        .catch((err) => {
          console.error("Tag Shopify cart error:", err);

          // Track session tagging failure
          this.honeycomb.addEvent("session.tagging_failed", {
            session_uuid: live_chat_session_uuid,
            conversation_uuid: live_chat_conversation_uuid,
            error_message: err.message || err.toString(),
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });
        });
    }

    async handleFetchProductData(handle, productUrl) {
      try {
        const productData = await this.fetchProductDataByHandle(
          handle,
          productUrl
        );

        // Send response to iframe (product may be null if not found - that's ok)
        if (this.iframe && this.iframe.contentWindow) {
          this.iframe.contentWindow.postMessage(
            {
              action: "productDataResponse",
              handle: handle,
              product: productData
            },
            "*"
          );
        }
      } catch (error) {
        // Only log unexpected errors, not 404s (which are handled by returning null)
        console.warn("[Siena] Unexpected error fetching product data:", error);

        // Send null response back to iframe
        if (this.iframe && this.iframe.contentWindow) {
          this.iframe.contentWindow.postMessage(
            {
              action: "productDataResponse",
              handle: handle,
              product: null
            },
            "*"
          );
        }
      }
    }

    // Consolidated message handler
    handleAllMessages(e) {
      try {
        // Basic validation
        if (!e.data || !e.data.action) return;

        // Determine message source for routing (without strict validation)
        const isFromMainIframe =
          this.iframe &&
          this.iframe.contentWindow &&
          e.source === this.iframe.contentWindow;
        const isFromSearchBar =
          this.searchBarIframe &&
          this.searchBarIframe.contentWindow &&
          e.source === this.searchBarIframe.contentWindow;
        const isFromEmbedded =
          this.embeddedIframe &&
          this.embeddedIframe.contentWindow &&
          e.source === this.embeddedIframe.contentWindow;

        const { action } = e.data;

        // Route search bar specific messages
        if (isFromSearchBar && this.isSearchBarAction(action)) {
          this.handleSearchBarMessage(e);
          return;
        }

        // Route main iframe messages
        if (
          (isFromMainIframe || isFromEmbedded) &&
          !this.isSearchBarAction(action)
        ) {
          this.handleIframeMessage(e);
          return;
        }

        // Handle cross-component messages or messages from unknown sources
        if (
          this.isCrossComponentAction(action) ||
          (!isFromMainIframe && !isFromSearchBar && !isFromEmbedded)
        ) {
          this.handleCrossComponentMessage(e);
        }
      } catch (error) {
        sendErrorToSentry(error, {
          method: "handleAllMessages",
          action: e.data && e.data.action,
          source: e.source ? "iframe" : "unknown"
        });
      }
    }

    // Check if action is search bar specific
    isSearchBarAction(action) {
      const searchBarActions = [
        "closeSearchBar",
        "searchBarClick",
        "updateSearchBarWidth",
        "searchBarFocus",
        "searchBarBlur"
      ];
      return searchBarActions.includes(action);
    }

    // Check if action can come from any component or external source
    isCrossComponentAction(action) {
      const crossComponentActions = [
        "openAttachment",
        "sendMessage",
        "ping",
        "fetchProductSuggestions",
        "resize",
        "sendHeight",
        "openWidget",
        "tagShopifyCart",
        "fetchProductData",
        "addToCart",
        "clearChat",
        "resetConversation",
        "updateUserContext",
        "fetchCurrency",
        "proactiveConversion"
      ];
      return crossComponentActions.includes(action);
    }

    // Handle search bar specific messages
    handleSearchBarMessage(e) {
      if (!e.data || !e.data.action) return;

      const { action } = e.data;

      switch (action) {
        case "closeSearchBar":
          if (this.searchBarIframe) {
            this.searchBarIframe.style.display = "none";

            // For mobile, when search bar is closed, show bubble in center-right
            if (this.isMobile && this.chatSettings.search_bar_mobile === true) {
              this.searchBarClosed = true;
              this.safeStorageSet("sienaSearchBarClosed", "true");

              // Show the bubble and position it in center-right
              const bubbleWidth = this.calculateBubbleWidth(
                this.chatSettings?.chat_button_text,
                this.isMobile,
                this.chatSettings?.independent_mobile_positioning
              );
              this.iframe.style.setProperty(
                "width",
                `${bubbleWidth}px`,
                "important"
              );
            }
          }
          break;

        case "searchBarClick":
          // FIXED: Require explicit user interaction - remove automatic opening
          // Only open if user explicitly clicked (not from stored state)
          if (this.isMobile && e.data.userInteraction === true) {
            this.searchBarIframe.contentWindow.postMessage(
              { action: "blurSearchBar" },
              "*"
            );
            this.handleOpenWidget();
          }
          break;

        case "updateSearchBarWidth":
          // Update the search bar iframe width if not mobile or not expanded
          if (this.searchBarIframe && e.data.width) {
            // Only apply non-mobile width updates, or mobile width updates when not expanded
            if (this.isMobile && e.data.isExpanding) {
              this.searchBarIframe.style.width = "100%";
              break;
            }
            if (!this.isMobile || (this.isMobile && e.data.width !== "100%")) {
              this.searchBarIframe.style.width = e.data.width;
            }
          }
          break;

        case "searchBarFocus":
          if (this.isMobile) {
            document.body.style.overflow = "hidden";
          }
          break;

        case "searchBarBlur":
          if (this.isMobile) {
            document.body.style.overflow = "auto";
          }
          break;
      }
    }

    // Handle messages that can come from any component or external source
    handleCrossComponentMessage(e) {
      const { action } = e.data;

      switch (action) {
        case "ping":
          this.handlePong();
          break;

        case "widgetInitialized":
          this.handleWidgetInitializedAck();
          break;

        case "fetchProductSuggestions":
          this.fetchProductSuggestions();
          break;

        case "openAttachment":
          this.handleOpenWidget();
          setTimeout(() => {
            this.handleOpenAttachment();
          }, 300);
          break;

        case "sendMessage":
          // SNA-6840: Fire conversation started event on first user message
          if (!this.conversationStartedFired) {
            this.conversationStartedFired = true;
            document.dispatchEvent(
              new CustomEvent("siena:conversation:started", {
                detail: { timestamp: Date.now() }
              })
            );
          }

          // Store that search bar has been used (only on mobile from search bar)
          const isFromSearchBar =
            this.searchBarIframe &&
            this.searchBarIframe.contentWindow &&
            e.source === this.searchBarIframe.contentWindow;

          // Track message sending
          this.honeycomb.addEvent("message.sent", {
            trigger_source: isFromSearchBar ? "search_bar" : "widget",
            message_length: e.data.message ? e.data.message.length : 0,
            has_pre_generated_uuid: !!e.data.pre_generated_question_uuid,
            is_mobile: this.isMobile,
            widget_was_closed: !this.iframe.classList.contains(
              "siena-iframe--bottom-0"
            ),
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });

          // Forward the message to the chat iframe
          this.handleOpenWidget();

          if (this.isMobile && isFromSearchBar) {
            this.safeLocalStorageSet("searchbar_used", "true");
            this.searchBarUsed = true;
          }

          // Allow the widget to fully open before sending the message
          const postSendMessage = () => {
            if (this.iframe && this.iframe.contentWindow) {
              this.iframe.contentWindow.postMessage(
                {
                  action: "sendMessage",
                  message: e.data.message,
                  pre_generated_question_uuid:
                    e.data.pre_generated_question_uuid,
                  current_page_url: window.location.href,
                  traceContext: {
                    traceId: this.getUnifiedTraceId(),
                    sessionId: this.currentSessionUuid,
                    source: "parent_widget"
                  }
                },
                "*"
              );
            } else {
              // Track failure to send message
              this.honeycomb.addEvent("message.send_failed", {
                reason: "iframe_not_available",
                message_length: e.data.message ? e.data.message.length : 0,
                timestamp: Date.now(),
                trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
              });
            }
          };

          setTimeout(() => {
            // If widget hasn't pinged + received init yet (state.appKey would
            // be null inside the iframe, BE rejects /messages with
            // "Key is required"), queue the message and replay it from
            // handlePong once init has been delivered.
            if (this.widgetInitialized) {
              postSendMessage();
            } else {
              this.pendingPostInitMessages =
                this.pendingPostInitMessages || [];
              this.pendingPostInitMessages.push(postSendMessage);
            }
          }, 300);
          break;

        case "resize":
          const { height, width, openWidget } = e.data;

          let heightValue = parseInt(height);
          let newHeight = height;
          let newWidth = width;

          // Don't override dimensions for fullscreen - let widget handle its own modal
          if (heightValue > window.innerHeight && height !== "100%") {
            newHeight = `${window.innerHeight}px`;
          }

          // Only set width/height if widget is being opened or already open
          if (
            openWidget ||
            this.iframe.classList.contains("siena-iframe--bottom-0")
          ) {
            this.iframe.style.height = newHeight;
            this.iframe.style.width = newWidth;
          }

          if (openWidget) {
            // Hide proactive popup when widget opens
            this.hideProactivePopup();
            this.cleanupProactiveTriggers();

            // Check if widget is already open to avoid duplicate tracking
            const wasAlreadyOpen = this.iframe.classList.contains(
              "siena-iframe--bottom-0"
            );

            this.iframe.classList.add("siena-iframe--bottom-0");

            // Track widget opening only if it wasn't already open
            if (!wasAlreadyOpen) {
              this.widgetState = "open";
              this.honeycomb.addEvent("widget.opened", {
                trigger_source: "resize_handler",
                is_mobile: this.isMobile,
                search_bar_visible: this.shouldShowSearchBar(),
                search_bar_closed: this.searchBarClosed,
                opening_height: height,
                opening_width: width,
                timestamp: Date.now(),
                trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
              });
              // SNA-6840: Dispatch open event for headless integrations
              document.dispatchEvent(
                new CustomEvent("siena:widget:opened", {
                  detail: { timestamp: Date.now() }
                })
              );
            }

            // Always ensure width is set when widget is opened
            if (
              this.iframe.style.width === "0px" ||
              this.iframe.style.width === "0"
            ) {
              this.iframe.style.width = width;
            }

            // Hide search bar when widget is open, except for certain cases
            const showSearchBarOnMobile =
              this.isMobile && this.chatSettings.search_bar_mobile === true;
            if (!showSearchBarOnMobile) {
              this.toggleSearchBarVisibility(false);
            } else if (this.searchBarIframe) {
              // For mobile with search bar, adjust searchbar width to 100%
              this.searchBarIframe.style.width = "100%";
            }
          } else {
            // FIXED: Simplified widget closing logic
            const wasOpen = this.iframe.classList.contains(
              "siena-iframe--bottom-0"
            );

            try {
              this.iframe.classList.remove("siena-iframe--bottom-0");

              // Only track widget closing if it was actually open
              if (wasOpen) {
                this.widgetState = "closed";
                this.honeycomb.addEvent("widget.closed", {
                  trigger_source: "resize_handler",
                  is_mobile: this.isMobile,
                  search_bar_visible: this.shouldShowSearchBar(),
                  search_bar_closed: this.searchBarClosed,
                  closing_height: height,
                  closing_width: width,
                  timestamp: Date.now(),
                  trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
                });
                // SNA-6840: Dispatch close event for headless integrations
                document.dispatchEvent(
                  new CustomEvent("siena:widget:closed", {
                    detail: { timestamp: Date.now() }
                  })
                );
              }
              // FIXED: Removed false positive "close_failed" tracking
              // Normal closed state is not a failure - no event needed
            } catch (error) {
              // Only track actual DOM manipulation failures
              this.honeycomb.addEvent("widget.close_failed", {
                reason: "dom_manipulation_failed",
                error_message: error?.message || error?.toString(),
                trigger_source: "resize_handler",
                is_mobile: this.isMobile,
                was_open: wasOpen,
                timestamp: Date.now(),
                trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
              });
            }

            // Determine if widget should be hidden when closed
            const shouldHideWidget =
              (this.isMobile &&
                this.chatSettings.search_bar_mobile === true &&
                !this.searchBarClosed) ||
              this.chatSettings.show_bubble === false ||
              (!this.chatSettings.is_visible_mobile &&
                !this.chatSettings.is_visible_desktop &&
                this.chatSettings.embedded_search_enabled) ||
              this.isVisibilityOff(); // NEW: Hide if visibility is turned off for current device

            if (shouldHideWidget) {
              this.iframe.style.width = "0";
              this.iframe.style.height = "0"; // Also hide height for complete hiding
            } else {
              // Calculate dynamic width based on chat button text
              const bubbleWidth = this.calculateBubbleWidth(
                this.chatSettings?.chat_button_text,
                this.isMobile,
                this.chatSettings?.independent_mobile_positioning
              );
              this.iframe.style.setProperty(
                "width",
                `${bubbleWidth}px`,
                "important"
              );
              this.iframe.style.height = "80px";
            }

            // Show search bar when widget is closed and search bar is enabled
            if (this.shouldShowSearchBar()) {
              this.toggleSearchBarVisibility(true);
            }

            // Reset searchbar width for mobile
            if (this.isMobile && this.searchBarIframe) {
              this.searchBarIframe.style.width = "350px";
            }
          }
          // Handle fullscreen mode coordination - only for actual 100% iframe dimensions
          // Widget's internal modal doesn't need these classes
          const isFullscreen = height === "100%" && width === "100%";
          const isMobileOpen =
            this.isMobile && width === "100%" && height.includes("dvh");

          if (isFullscreen) {
            if (!this.iframe.classList.contains("siena-iframe--fullscreen")) {
              this.iframe.classList.add("siena-iframe--fullscreen");
              document.body.style.overflow = "hidden";
            }
          } else if (isMobileOpen) {
            // For mobile with dvh, only disable body scroll, don't add fullscreen class
            // (fullscreen class has height: 100% !important which would override dvh)
            document.body.style.overflow = "hidden";
            this.setMobileViewport();
          } else {
            if (this.iframe.classList.contains("siena-iframe--fullscreen")) {
              this.iframe.classList.remove("siena-iframe--fullscreen");
            }
            document.body.style.overflow = "auto";
            this.restoreViewport();
          }

          // Widget handles its own fullscreen modal internally
          break;

        case "sendHeight":
          if (this.embeddedIframe) {
            this.embeddedIframe.style.height = `${e.data.height}px`;
          }
          break;

        case "unreadCountChanged":
          // ECH-1036: headless sites with their own chat button draw the badge from this event
          if (e.source !== this.iframe?.contentWindow) break;
          this.unreadCount = Number(e.data.count) || 0;
          document.dispatchEvent(
            new CustomEvent("siena:unread:changed", {
              detail: { count: this.unreadCount, timestamp: Date.now() }
            })
          );
          break;

        case "openWidget":
          // Ensure width is not 0 when opening widget
          if (
            this.iframe.style.width === "0px" ||
            this.iframe.style.width === "0"
          ) {
            this.iframe.style.width = "470px"; // Default width for widget
          }

          // FIXED: Widget opening is now tracked directly in resize handler above
          // Only send postMessage to iframe to notify it of the open state
          if (this.iframe && this.iframe.contentWindow) {
            try {
              this.iframe.contentWindow.postMessage(
                { action: "openWidget" },
                "*"
              );
            } catch (error) {
              // Track message posting failure
              this.honeycomb.addEvent("widget.open_failed", {
                reason: "message_post_failed",
                error_message: error.message || error.toString(),
                trigger_source: "resize_handler",
                is_mobile: this.isMobile,
                timestamp: Date.now(),
                trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
              });
            }
          }

          if (
            this.isMobile &&
            this.chatSettings.search_bar_mobile === true &&
            this.searchBarIframe
          ) {
            this.searchBarIframe.style.width = "100%";
          }
          break;

        case "tagShopifyCart":
          if (typeof window !== "undefined") {
            const { live_chat_session_uuid, live_chat_conversation_uuid } =
              e.data.data || {};
            if (live_chat_session_uuid && live_chat_conversation_uuid) {
              this.handleTagShopifyCart(
                live_chat_session_uuid,
                live_chat_conversation_uuid
              );
            }
          }
          break;

        case "fetchProductData":
          this.handleFetchProductData(e.data.handle, e.data.productUrl);
          break;

        case "fetchCurrency":
          this.handleFetchCurrency();
          break;

        case "addToCart":
          if (e.data.productId) {
            // Track add to cart attempt
            this.honeycomb.addEvent("cart.add_attempt", {
              product_id: e.data.productId,
              quantity: e.data.quantity || 1,
              has_product_info: !!e.data.product,
              product_title: e.data.product?.title || "unknown",
              selling_plan_id: e.data.sellingPlan || null,
              is_subscription: !!e.data.sellingPlan,
              trigger_source: "widget",
              timestamp: Date.now(),
              trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
            });

            this.handleAddToCart(
              e.data.productId,
              e.data.quantity || 1,
              e.data.product,
              e.data.sellingPlan || null
            );
          } else {
            // Track failed add to cart (missing product ID)
            this.honeycomb.addEvent("cart.add_failed", {
              reason: "missing_product_id",
              data_received: JSON.stringify(e.data).substring(0, 200),
              timestamp: Date.now(),
              trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
            });
          }
          break;

        case "clearChat":
        case "resetConversation":
          this.handleChatClear(e.data);
          break;

        case "updateUserContext":
          // Update Sentry and monitoring context when user info is available
          // NOTE: Session initialization sends NO userId, only session/conversation UUIDs
          if (e.data.userId || e.data.sessionUuid || e.data.conversationUuid) {
            this.updateSentryUserContext(
              e.data.userId || this.currentUserId, // userId will be null during session init
              e.data.sessionUuid || this.currentSessionUuid,
              e.data.conversationUuid || this.currentConversationUuid,
              e.data.context || {}
            );
          }
          break;

        case "proactiveConversion":
          // Fired by iframe when user sends first message after clicking proactive popup
          console.log(
            "[Proactive] Received proactiveConversion message",
            e.data
          );
          if (e.data.proactiveCampaignData) {
            const campaign = e.data.proactiveCampaignData;
            const timeToConversion =
              Date.now() - (campaign.clicked_at || Date.now());

            console.log(
              "[Proactive] Firing honeycomb event proactive.conversion",
              {
                organization_uuid: this.chatSettings?.organization_uuid,
                campaign_uuid: campaign.uuid,
                campaign_name: campaign.name
              }
            );
            this.honeycomb.addEvent("proactive.conversion", {
              organization_uuid: this.chatSettings?.organization_uuid,
              campaign_uuid: campaign.uuid,
              campaign_name: campaign.name,
              trigger_type: campaign.trigger_type,
              trigger_value: campaign.trigger_value,
              original_page_url: campaign.page_url,
              current_page_url: window.location.href,
              is_mobile: this.isMobile,
              positioning: this.chatSettings?.positioning,
              time_to_conversion_ms: timeToConversion,
              trace_id: this.getUnifiedTraceId()
            });
            console.log("[Proactive] Honeycomb event fired successfully");
          }
          break;

        case "traceSynchronized":
          // CRITICAL: Ensure widget also uses the same parent trace_id
          if (
            e.data.parentTraceId &&
            e.data.parentTraceId === this.getUnifiedTraceId()
          ) {
            console.debug(
              "[Widget] Trace already synchronized with parent:",
              e.data.parentTraceId
            );
          } else {
            console.debug(
              "[Widget] iframe trace sync confirmed, parent trace_id verified"
            );
          }

          // Track successful cross-frame trace synchronization
          this.honeycomb.addEvent("trace.cross_frame_synchronized", {
            unified_trace_id: e.data.parentTraceId,
            iframe_domain: e.data.iframeDomain,
            parent_domain: window.location.hostname,
            synchronized_at: e.data.synchronizedAt,
            cross_domain_success: true,
            timestamp: Date.now(),
            trace_id: e.data.parentTraceId // Main trace_id field for Honeycomb
          });
          break;
      }
    }

    // Handle messages from the iframe (simplified - forwards to cross-component handler)
    handleIframeMessage(e) {
      try {
        // Since we removed strict source validation, forward all iframe messages
        // to the cross-component handler for unified processing
        this.handleCrossComponentMessage(e);
      } catch (error) {
        sendErrorToSentry(error, {
          method: "handleIframeMessage",
          action: e.data && e.data.action,
          data: JSON.stringify(e.data || {})
        });
      }
    }

    handleChatClear(data) {
      // Reset conversation started flag so next conversation fires a new event
      this.conversationStartedFired = false;

      // Track chat clearing event
      this.honeycomb.addEvent("chat.cleared", {
        trigger_source: data.source || "unknown",
        clear_type:
          data.action === "resetConversation"
            ? "conversation_reset"
            : "chat_clear",
        session_uuid: this.currentSessionUuid,
        conversation_uuid: this.currentConversationUuid,
        user_id: this.currentUserId,
        user_initiated: data.userInitiated || false,
        reason: data.reason || "not_specified",
        clears_user_context: data.clearUserContext || false,
        timestamp: Date.now(),
        trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
      });

      // Finish current session trace if clearing user context
      if (data.clearUserContext || data.clearPersistedStorage) {
        this.honeycomb.finishSessionTrace();
      }

      // Clear user context if requested
      if (data.clearUserContext) {
        if (this.currentUserId) {
          this.honeycomb.addEvent("user.context_cleared", {
            cleared_user_id: this.currentUserId,
            cleared_session_uuid: this.currentSessionUuid,
            cleared_conversation_uuid: this.currentConversationUuid,
            trigger_source: data.source || "chat_clear",
            clears_persisted_storage: data.clearPersistedStorage || false,
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId() // UNIFIED TRACE_ID
          });
        }

        // Clear persisted user ID if requested
        if (data.clearPersistedStorage) {
          this.safeLocalStorageSet("siena_user_id", null);
          this.persistedUserId = null;
        }

        this.currentUserId = null;
      }

      // Reset session tracking since chat is cleared
      this.currentSessionUuid = null;
      this.currentConversationUuid = null;

      // Forward the message to the iframe
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          {
            action: data.action || "clearChat",
            ...data
          },
          "*"
        );
      }
    }

    handleOpenAttachment() {
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          { action: "openAttachment" },
          "*"
        );
      }
    }

    handleOpenWidget(widgetType = "widget") {
      if (this.iframe && this.iframe.contentWindow) {
        // Check if widget is already open to prevent random opening
        const isAlreadyOpen = this.iframe.classList.contains(
          "siena-iframe--bottom-0"
        );
        if (isAlreadyOpen) {
          return;
        }

        // Track widget opening
        this.honeycomb.addEvent("widget.opened", {
          trigger_source: "handleOpenWidget",
          widget_type: widgetType,
          is_mobile: this.isMobile,
          search_bar_visible: this.shouldShowSearchBar(),
          search_bar_closed: this.searchBarClosed,
          widget_was_hidden:
            this.iframe.style.width === "0" ||
            this.iframe.style.width === "0px",
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId()
        });

        try {
          if (this.iframe && this.iframe.contentWindow) {
            this.iframe.contentWindow.postMessage(
              {
                action: "openWidget",
                widgetType: widgetType
              },
              "*"
            );
          }
        } catch (error) {
          this.honeycomb.addEvent("widget.open_failed", {
            reason: "message_post_failed",
            error_message: error.message || error.toString(),
            trigger_source: "handleOpenWidget",
            widget_type: widgetType,
            is_mobile: this.isMobile,
            timestamp: Date.now(),
            trace_id: this.getUnifiedTraceId()
          });
        }
      } else {
        this.honeycomb.addEvent("widget.open_failed", {
          reason: "iframe_not_available",
          iframe_exists: !!this.iframe,
          content_window_exists: !!(this.iframe && this.iframe.contentWindow),
          trigger_source: "handleOpenWidget",
          widget_type: widgetType,
          is_mobile: this.isMobile,
          timestamp: Date.now(),
          trace_id: this.getUnifiedTraceId()
        });
      }
    }

    /**
     * Launch the widget programmatically - accessible to clients
     * @param {Object} options - Launch options
     * @param {string} [options.message] - Optional message to send when widget opens
     * @param {string} [options.type] - Widget type: 'fullscreen' or 'widget' (default: 'widget')
     */
    launchWidget(message = "", email = "", name = "") {
      if (!this.iframe) {
        console.warn("Siena widget not initialized - iframe is null");
        const domIframe = document.getElementById("SIENA_CHAT_IFRAME");
        if (domIframe) {
          console.warn(
            "FOUND: iframe exists in DOM but this.iframe is null! Attempting to recover..."
          );
          this.iframe = domIframe;
        } else {
          console.warn("iframe does not exist in DOM either");
          return;
        }
      }

      if (!this.iframe.contentWindow) {
        console.warn(
          "Iframe contentWindow not available - iframe may still be loading"
        );
        // Try to wait for iframe to load
        this.waitForIframeReady(() => {
          this.launchWidget(message, email);
        });
        return;
      }

      // Track launch
      this.honeycomb.addEvent("widget.launched_programmatically", {
        trigger_source: "client_api",
        widget_type: "widget",
        has_message: !!message,
        has_email: !!email,
        has_name: !!name,
        message_length: message ? message.length : 0,
        timestamp: Date.now(),
        trace_id: this.getUnifiedTraceId()
      });

      // If widget is already open, just send email, name and message
      if (this.iframe.classList.contains("siena-iframe--bottom-0")) {
        if (email) {
          this.sendEmailToWidget(email);
        }
        if (name) {
          this.sendNameToWidget(name);
        }
        if (message) {
          this.sendMessageToWidget(message);
        }
        return;
      }

      // Open widget normally
      this.makeWidgetVisible();
      this.handleOpenWidget();

      // Set email if provided (before sending message so useApi picks it up)
      if (email) {
        this.sendEmailToWidget(email);
      }

      // Set name if provided (before sending message so useApi picks it up)
      if (name) {
        this.sendNameToWidget(name);
      }

      // Send message if provided
      if (message) {
        setTimeout(() => {
          this.sendMessageToWidget(message);
        }, 500);
      }
    }

    /**
     * Launch the widget in fullscreen mode
     * @param {string} [message] - Optional message to send when widget opens
     */
    launchFullScreenWidget(message = "", email = "", name = "") {
      if (!this.iframe) {
        console.warn("Siena widget not initialized - iframe is null");
        return;
      }

      if (!this.iframe.contentWindow) {
        console.warn(
          "Iframe contentWindow not available - iframe may still be loading"
        );
        // Try to wait for iframe to load
        this.waitForIframeReady(() => {
          this.launchFullScreenWidget(message, email, name);
        });
        return;
      }

      // Track launch
      this.honeycomb.addEvent("widget.launched_programmatically", {
        trigger_source: "client_api",
        widget_type: "fullscreen",
        has_message: !!message,
        has_email: !!email,
        has_name: !!name,
        message_length: message ? message.length : 0,
        timestamp: Date.now(),
        trace_id: this.getUnifiedTraceId()
      });

      // If widget is already open, make it fullscreen
      if (this.iframe.classList.contains("siena-iframe--bottom-0")) {
        this.iframe.style.height = "100%";
        this.iframe.style.width = "100%";
        this.iframe.classList.add("siena-iframe--fullscreen");
        document.body.style.overflow = "hidden";

        if (email) {
          this.sendEmailToWidget(email);
        }
        if (name) {
          this.sendNameToWidget(name);
        }
        if (message) {
          setTimeout(() => {
            this.sendMessageToWidget(message);
          }, 300);
        }
        return;
      }

      // For fullscreen, make iframe minimally visible so resize messages work
      // but don't use makeWidgetVisible() which sets normal bubble dimensions
      if (
        this.iframe.style.width === "0" ||
        this.iframe.style.width === "0px"
      ) {
        this.iframe.style.width = "1px";
        this.iframe.style.height = "1px";
      }

      // Open widget directly in fullscreen
      this.handleOpenWidget("fullscreen");

      // Set email if provided (before sending message so useApi picks it up)
      if (email) {
        this.sendEmailToWidget(email);
      }

      // Set name if provided (before sending message so useApi picks it up)
      if (name) {
        this.sendNameToWidget(name);
      }

      // Send message if provided
      if (message) {
        setTimeout(() => {
          this.sendMessageToWidget(message);
        }, 500);
      }
    }

    /**
     * Wait for iframe to be ready with contentWindow available
     */
    waitForIframeReady(callback, maxAttempts = 10, attempt = 1) {
      if (this.iframe && this.iframe.contentWindow) {
        callback();
        return;
      }

      if (attempt >= maxAttempts) {
        console.error("Iframe failed to load after", maxAttempts, "attempts");
        return;
      }

      setTimeout(() => {
        this.waitForIframeReady(callback, maxAttempts, attempt + 1);
      }, 100);
    }

    /**
     * Send email to widget iframe to pre-set user email
     * @param {string} email - Email address to set
     */
    sendEmailToWidget(email) {
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          {
            action: "setEmail",
            email: email,
            traceContext: {
              traceId: this.getUnifiedTraceId(),
              sessionId: this.currentSessionUuid,
              source: "client_api"
            }
          },
          new URL(this.iframe.src).origin
        );
      }
    }

    /**
     * Send name to widget iframe to pre-set user name
     * @param {string} name - Full name to set (splits into first/last on whitespace)
     */
    sendNameToWidget(name) {
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          {
            action: "setName",
            name: name,
            traceContext: {
              traceId: this.getUnifiedTraceId(),
              sessionId: this.currentSessionUuid,
              source: "client_api"
            }
          },
          new URL(this.iframe.src).origin
        );
      }
    }

    /**
     * Send message to widget iframe
     */
    sendMessageToWidget(message) {
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          {
            action: "sendMessage",
            message: message,
            current_page_url: window.location.href,
            traceContext: {
              traceId: this.getUnifiedTraceId(),
              sessionId: this.currentSessionUuid,
              source: "client_api"
            }
          },
          "*"
        );
      }
    }

    /**
     * Make widget visible by setting appropriate dimensions
     * Used when launching widget programmatically even if visibility is off
     */
    makeWidgetVisible() {
      if (!this.iframe) return;

      // Calculate appropriate width
      const bubbleWidth = this.calculateBubbleWidth(
        this.chatSettings?.chat_button_text,
        this.isMobile,
        this.chatSettings?.independent_mobile_positioning
      );

      // Make widget visible
      this.iframe.style.setProperty("width", `${bubbleWidth}px`, "important");
      this.iframe.style.height = "80px";

      // Remove any hidden classes
      this.iframe.style.display = "block";
    }

    /**
     * Check if widget visibility is turned off for current device
     */
    isVisibilityOff() {
      return this.isMobile
        ? !this.chatSettings?.is_visible_mobile
        : !this.chatSettings?.is_visible_desktop;
    }

    // Handle window resize events
    handleWindowResize() {
      const chatBox =
        this.iframe &&
        this.iframe.contentWindow &&
        this.iframe.contentWindow.document &&
        this.iframe.contentWindow.document.querySelector(".siena-chat__box");
      if (!chatBox) return;
      const isVisible = chatBox && chatBox.classList.contains("visible");
      if (!isVisible) {
        this.iframe.classList.add("siena-iframe--minimized");
      } else if (this.isMobile) {
        this.iframe.classList.remove("siena-iframe--minimized");
        this.iframe.classList.add("siena-iframe--fullscreen");
      } else {
        this.iframe.classList.remove(
          "siena-iframe--minimized",
          "siena-iframe--fullscreen"
        );
      }

      // Handle search bar visibility
      if (this.searchBarIframe) {
        if (!this.isMobile && this.hasEnoughSpaceForSearchBar()) {
          this.searchBarIframe.style.display = this.shouldShowSearchBar()
            ? "block"
            : "none";
        } else {
          this.searchBarIframe.style.display = "none";
        }
      }

      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          { action: "resizeComplete" },
          "*"
        );
      }
      this.sendWindowSize();
    }

    // Utility: Check if the device is mobile
    checkIsMobile() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      return (
        window.innerWidth <= 768 ||
        /android|ip(hone|od|ad)|mobile/i.test(userAgent)
      );
    }

    // Utility: Get cookie value by name
    getCookie(name) {
      const nameEQ = `${name}=`;
      return document.cookie.split(";").reduce((value, c) => {
        c = c.trim();
        return c.startsWith(nameEQ) ? c.substring(nameEQ.length) : value;
      }, null);
    }

    // Utility: Set safe storage
    safeStorageSet(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {
        console.warn("Failed to set sessionStorage item:", e);
      }
    }

    // Utility: Get safe storage
    safeStorageGet(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        console.warn("Failed to get sessionStorage item:", e);
        return null;
      }
    }

    // Utility: Set localStorage item safely
    safeLocalStorageSet(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.warn("Failed to set localStorage item:", e);
      }
    }

    // Utility: Get localStorage item safely
    safeLocalStorageGet(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn("Failed to get localStorage item:", e);
        return null;
      }
    }

    // Utility: Get cached API response from sessionStorage
    getCachedApiResponse(key) {
      try {
        const cached = sessionStorage.getItem(key);
        if (!cached) return null;

        const { data, timestamp, expiresAt } = JSON.parse(cached);
        const now = Date.now();

        // Check if cache has expired
        if (expiresAt && now > expiresAt) {
          sessionStorage.removeItem(key);
          return null;
        }

        return data;
      } catch (e) {
        console.warn("Failed to get cached API response:", e);
        return null;
      }
    }

    // Utility: Cache API response in sessionStorage
    setCachedApiResponse(key, data, ttlMs = 300000) {
      try {
        const cacheData = {
          data,
          timestamp: Date.now(),
          expiresAt: ttlMs ? Date.now() + ttlMs : null
        };
        sessionStorage.setItem(key, JSON.stringify(cacheData));
      } catch (e) {
        console.warn("Failed to cache API response:", e);
      }
    }

    // Determine if search bar should be shown based on settings and device
    shouldShowSearchBar() {
      if (this.searchBarClosed) return false;

      if (this.isMobile) {
        return this.chatSettings.search_bar_mobile === true;
      } else {
        return this.chatSettings.search_bar_desktop !== false; // Default to true if undefined
      }
    }

    // Utility: Send window size to iframe
    sendWindowSize() {
      if (this.iframe && this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          {
            action: "updateSize",
            width: window.innerWidth,
            height: window.innerHeight
          },
          "*"
        );
      }
    }

    // Viewport meta tag management: only override on mobile when chat is open,
    // restore the original value when chat is closed.
    setMobileViewport() {
      const meta = document.querySelector('meta[name="viewport"]');
      if (!meta) return;
      if (!this._originalViewportContent) {
        this._originalViewportContent = meta.content;
      }
      meta.content =
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0";
    }

    restoreViewport() {
      if (!this._originalViewportContent) return;
      const meta = document.querySelector('meta[name="viewport"]');
      if (meta) {
        meta.content = this._originalViewportContent;
      }
    }

    // Create and configure the search bar iframe
    createSearchBarIframe() {
      // Check if search bar should be created based on settings
      if (this.searchBarIframe) return;

      // Check if search bar should be shown for current device
      if (!this.shouldShowSearchBar()) return;

      // Don't create if there's not enough space (for desktop)
      if (!this.isMobile && !this.hasEnoughSpaceForSearchBar()) return;

      this.searchBarIframe = document.createElement("iframe");
      this.searchBarIframe.classList.add("siena-search-bar");

      // Set initial width for mobile
      if (this.isMobile) {
        this.searchBarIframe.style.width = "350px";
      }

      // Get the origin from the main chat iframe URL
      const chatUrl = new URL(this.config.IFRAME_SRC);
      const searchBarUrl = `${chatUrl.origin}/dist/search-bar.html`;

      this.searchBarIframe.src = searchBarUrl;
      this.searchBarIframe.crossOrigin = "anonymous";
      this.searchBarIframe.allowFullScreen = true;
      this.searchBarIframe.allow = "fullscreen; autoplay";
      this.searchBarIframe.loading = "lazy";
      this.searchBarIframe.id = "SIENA_SEARCH_BAR_IFRAME";
      this.searchBarIframe.title = "Siena AI Search";

      document.body.appendChild(this.searchBarIframe);

      // Pass the brand color to the search bar iframe when it's loaded
      this.searchBarIframe.addEventListener("load", () => {
        if (this.chatSettings && this.chatSettings.brand_color) {
          this.searchBarIframe.contentWindow.postMessage(
            {
              action: "updateBrandColor",
              brandColor: this.chatSettings.brand_color
            },
            "*"
          );
        }

        // Pass the isMobile state to the search bar iframe
        this.searchBarIframe.contentWindow.postMessage(
          {
            action: "updateIsMobile",
            isMobile: this.isMobile
          },
          "*"
        );

        // Pass settings to the search bar iframe
        if (this.chatSettings) {
          this.searchBarIframe.contentWindow.postMessage(
            {
              action: "updateSettings",
              settings: this.chatSettings
            },
            "*"
          );
        }

        // Pass input placeholder if available
        if (this.chatSettings && this.chatSettings.input_placeholder) {
          this.searchBarIframe.contentWindow.postMessage(
            {
              action: "updateInputPlaceholder",
              placeholder: this.chatSettings.input_placeholder
            },
            "*"
          );
        }
      });
    }

    // Check if there's enough space for the search bar
    hasEnoughSpaceForSearchBar() {
      return window.innerWidth >= 1080;
    }

    // Add utility function to toggle search bar visibility
    toggleSearchBarVisibility(isVisible) {
      // Don't change visibility if search bar is closed on mobile
      if (!isVisible && this.isMobile && this.searchBarClosed) {
        return;
      }

      // Don't hide search bar if it should be shown on this device
      if (
        !isVisible &&
        this.isMobile &&
        this.chatSettings.search_bar_mobile === true
      ) {
        return;
      }

      if (this.searchBarIframe && this.searchBarIframe.contentWindow) {
        this.searchBarIframe.contentWindow.postMessage(
          { action: isVisible ? "showSearchBar" : "hideSearchBar" },
          "*"
        );
      }
    }

    // Create and configure the starters carousel iframe.
    // Mirrors createEmbeddedChat: customer drops <div id="siena-starters-carousel">,
    // we mount an iframe pointing at /dist/starters-carousel.html.
    createStartersCarousel(pageUrl) {
      const container = document.getElementById("siena-starters-carousel");
      if (!container) return;

      this.startersCarouselIframe = document.createElement("iframe");
      this.startersCarouselIframe.classList.add("siena-starters-carousel");
      this.startersCarouselIframe.style.cssText =
        "width:100%;border:0;background:transparent;display:block;height:60px;";

      const chatUrl = new URL(this.config.IFRAME_SRC);
      const carouselUrl = `${chatUrl.origin}/dist/starters-carousel.html`;
      this.startersCarouselIframe.src = carouselUrl;
      this.startersCarouselIframe.crossOrigin = "anonymous";
      this.startersCarouselIframe.id = "SIENA_STARTERS_CAROUSEL_IFRAME";
      this.startersCarouselIframe.title = "Siena Conversation Starters";

      container.innerHTML = "";
      container.appendChild(this.startersCarouselIframe);

      const sendInit = () => {
        if (!this.startersCarouselIframe.contentWindow) return;
        this.startersCarouselIframe.contentWindow.postMessage(
          {
            action: "initStartersCarousel",
            apiBase: this.config.API_BASE,
            // EAA-2902: starters resolve by the host page URL (where the
            // carousel is embedded), not by product identifiers.
            url: pageUrl,
            // EAA-3204: merchant-authored fixed starters render first, ahead of
            // the AI-generated questions the iframe fetches.
            fixedStarters: siena_normalizeFixedStarters(
              this.chatSettings && this.chatSettings.fixed_conversation_starters
            ),
            // EAA-3440: carousel autoscroll (enabled + px/s speed).
            autoscroll: siena_normalizeStartersAutoscroll(this.chatSettings),
            // EAA-3730: merchant max starters (null = no cap).
            maxStarters: siena_normalizeMaxStarters(this.chatSettings),
            // EFE-1196: Live Chat brand styling (theme, font, brand color).
            theme: siena_normalizeStartersTheme(this.chatSettings),
            auth: {
              organizationUuid:
                this.chatSettings && this.chatSettings.organization_uuid,
              appKey: this.apiKeys && this.apiKeys.token
            }
          },
          "*"
        );
      };

      this.startersCarouselIframe.addEventListener("load", sendInit);

      // Listen for ready ping + height updates from the iframe.
      window.addEventListener("message", (event) => {
        const data = event.data;
        if (!data || typeof data !== "object") return;
        if (data.action === "startersCarouselReady") {
          sendInit();
        }
        if (data.action === "updateHeight" && this.startersCarouselIframe) {
          if (
            event.source &&
            event.source === this.startersCarouselIframe.contentWindow
          ) {
            this.startersCarouselIframe.style.height = data.height + "px";
          }
        }
        // sendMessage from the carousel iframe is intentionally NOT handled here —
        // the canonical handler in attachEventListeners (case "sendMessage") owns
        // that flow: handleOpenWidget, email-capture gate, conversation-started
        // event, honeycomb trace, then forwards to the widget iframe. Same path
        // the embedded-search iframe uses. Adding a shortcut here would bypass
        // mandatory email capture and produce "error sending your message".
      });
    }

    // Create and configure the embedded chat iframe
    createEmbeddedChat(productData) {
      // Check if there's a div with the configured element ID in the page
      const embeddedContainer = document.getElementById(this.embeddedElementId);
      if (!embeddedContainer) return;

      // Create the iframe
      this.embeddedIframe = document.createElement("iframe");
      this.embeddedIframe.classList.add("siena-embedded");

      // Get the origin from the main chat iframe URL
      const chatUrl = new URL(this.config.IFRAME_SRC);
      const embeddedUrl = new URL(
        `${chatUrl.origin}/dist/embedded-search.html`
      );
      // UTM attribution for the "Powered by Siena" link (utm_source / utm_content).
      const path = window.location.pathname;
      embeddedUrl.searchParams.set("host", window.location.hostname);
      embeddedUrl.searchParams.set(
        "placement",
        path.indexOf("/products/") !== -1
          ? "pdp_embedded"
          : path.indexOf("/collections/") !== -1
            ? "collection_embedded"
            : "page_embedded"
      );

      this.embeddedIframe.src = embeddedUrl.href;
      this.embeddedIframe.crossOrigin = "anonymous";
      this.embeddedIframe.id = "SIENA_EMBEDDED_IFRAME";
      this.embeddedIframe.title = "Siena AI Embedded Chat";
      // ECH-1244: productData is null on non-product pages without a pin.
      const product = productData || {};
      this.embeddedIframe.setAttribute("data-product-id", product.id || "");
      this.embeddedIframe.setAttribute("data-product-title", product.title || "");
      this.embeddedIframe.setAttribute("data-product-url", product.url || "");
      this.embeddedIframe.setAttribute(
        "data-product-handle",
        product.handle || ""
      );
      this.embeddedIframe.setAttribute(
        "data-shopify-domain",
        product.shopify_domain || window.location.hostname
      );

      // Clear the container and append the iframe
      embeddedContainer.innerHTML = "";
      embeddedContainer.appendChild(this.embeddedIframe);

      // Pass settings to the iframe when it's loaded
      this.embeddedIframe.addEventListener("load", () => {
        this.fetchProductSuggestions();
        // Send brand color
        if (this.chatSettings && this.chatSettings.brand_color) {
          this.embeddedIframe.contentWindow.postMessage(
            {
              action: "updateBrandColor",
              brandColor: this.chatSettings.brand_color
            },
            "*"
          );
        }

        // Send logo
        if (this.chatSettings && this.chatSettings.logo_url) {
          this.embeddedIframe.contentWindow.postMessage(
            {
              action: "updateLogo",
              logoUrl: this.chatSettings.logo_url
            },
            "*"
          );
        }

        // Send greeting message
        if (this.chatSettings && this.chatSettings.intro_message) {
          this.embeddedIframe.contentWindow.postMessage(
            {
              action: "updateGreeting",
              data: this.chatSettings.intro_message
            },
            "*"
          );
        }

        // Pass the isMobile state
        this.embeddedIframe.contentWindow.postMessage(
          {
            action: "updateIsMobile",
            isMobile: this.isMobile
          },
          "*"
        );

        // Pass settings
        if (this.chatSettings) {
          this.embeddedIframe.contentWindow.postMessage(
            {
              action: "updateSettings",
              settings: this.chatSettings
            },
            "*"
          );
          // EFE-1196: Live Chat brand styling (theme, font, brand color),
          // normalized here so every starters surface shares one reading.
          this.embeddedIframe.contentWindow.postMessage(
            {
              action: "updateTheme",
              theme: siena_normalizeStartersTheme(this.chatSettings)
            },
            "*"
          );
        }

        // Pass input placeholder if available
        if (this.chatSettings && this.chatSettings.input_placeholder) {
          this.embeddedIframe.contentWindow.postMessage(
            {
              action: "updateInputPlaceholder",
              placeholder: this.chatSettings.input_placeholder
            },
            "*"
          );
        }
      });
    }
  }

  const SIENA_STYLES_ID = "SIENA_WIDGET_STYLES";
  let SIENA_STYLES_URL = null;
  let stylesObserver = null;
  let stylesObserverInitialized = false;
  let styleInjectionCount = 0;
  let lastInjectionTime = 0;
  let injectionThrottleTimeout = null;

  // Style injection loop detection constants
  const STYLE_INJECTION_LOOP_RESET_MS = 5000;
  const STYLE_INJECTION_RAPID_THRESHOLD_MS = 1000;
  const STYLE_INJECTION_MAX_RAPID_COUNT = 10;
  const STYLE_INJECTION_THROTTLE_MS = 100;

  function getSienaStylesUrl() {
    if (SIENA_STYLES_URL) return SIENA_STYLES_URL;

    // Get the origin from the iframe source to construct the CSS URL
    const iframeSrc = DEFAULT_CONFIG.IFRAME_SRC;
    let cssUrl;

    try {
      const url = new URL(iframeSrc);
      cssUrl = `${url.origin}/dist/siena-chat.css`;
    } catch (error) {
      // Fallback if URL parsing fails - more robust approach
      const lastSlashIndex = iframeSrc.lastIndexOf("/");
      if (lastSlashIndex !== -1) {
        cssUrl = iframeSrc.substring(0, lastSlashIndex + 1) + "siena-chat.css";
      } else {
        // If no slash found, assume it's a relative path and prepend ./
        cssUrl = "./siena-chat.css";
      }
    }

    SIENA_STYLES_URL = cssUrl;
    return cssUrl;
  }

  function ensureSienaStyles() {
    if (document.getElementById(SIENA_STYLES_ID)) return;

    const now = Date.now();
    const timeSinceLastInjection = now - lastInjectionTime;

    // Reset counter if enough time has passed
    if (timeSinceLastInjection > STYLE_INJECTION_LOOP_RESET_MS) {
      styleInjectionCount = 0;
    }

    // Check for rapid re-injections (potential loop)
    if (timeSinceLastInjection < STYLE_INJECTION_RAPID_THRESHOLD_MS) {
      styleInjectionCount++;

      // If we've injected too many times recently, disconnect observer
      if (styleInjectionCount > STYLE_INJECTION_MAX_RAPID_COUNT) {
        if (isDevelopment()) {
          console.warn(
            "[Siena] Detected rapid style re-injection loop, disconnecting observer"
          );
        }
        if (stylesObserver) {
          stylesObserver.disconnect();
          stylesObserver = null;
          stylesObserverInitialized = false;
        }
        // Still inject styles one last time, but without observer
      }
    }

    const link = document.createElement("link");
    link.id = SIENA_STYLES_ID;
    link.rel = "stylesheet";
    link.href = getSienaStylesUrl();
    document.head.appendChild(link);

    lastInjectionTime = now;

    if (isDevelopment()) {
      console.log(
        `[Siena] Injected CSS styles (count: ${styleInjectionCount})`
      );
    }
  }

  function startSienaStylesObserver() {
    // Only start observer once - use atomic flag to prevent race conditions
    if (stylesObserverInitialized) return;
    stylesObserverInitialized = true;

    stylesObserver = new MutationObserver(() => {
      // Throttle observer callbacks to prevent excessive re-injection
      if (injectionThrottleTimeout) return;

      injectionThrottleTimeout = setTimeout(() => {
        ensureSienaStyles();
        injectionThrottleTimeout = null;
      }, STYLE_INJECTION_THROTTLE_MS);
    });

    stylesObserver.observe(document.head, {
      childList: true,
      subtree: true
    });

    // Ensure styles are injected immediately
    ensureSienaStyles();
  }

  function injectSienaStyles() {
    startSienaStylesObserver();
  }

  // Helper function to categorize errors for better monitoring
  function categorizeError(error, context) {
    const errorMessage = error.message || String(error);

    if (
      context.method === "fetchChatSettings" ||
      context.method === "fetchProductSuggestions"
    ) {
      return "api_error";
    }

    if (
      context.method === "handleIframeMessage" ||
      context.method === "handleAllMessages"
    ) {
      return "message_handling_error";
    }

    if (context.method === "init" || context.method === "initializeSienaChat") {
      return "initialization_error";
    }

    if (errorMessage.includes("JSON") || errorMessage.includes("parse")) {
      return "parsing_error";
    }

    if (
      errorMessage.includes("network") ||
      errorMessage.includes("fetch") ||
      context.status
    ) {
      return "network_error";
    }

    if (context.method === "createWebchat") {
      return "widget_creation_error";
    }

    return "general_error";
  }

  // Widget-scoped Sentry error reporting with enhanced context
  function sendErrorToSentry(error, context = {}) {
    const DSN = DEFAULT_CONFIG.SENTRY_DSN;

    if (!DSN) {
      return; // Silently fail if no DSN configured
    }

    // Filter out network noise and non-widget errors
    const errorMessage = error.message || String(error);
    const noisyNetworkErrors = [
      "Load Failed",
      "Failed to fetch",
      "Network Error",
      "NetworkError",
      "ERR_NETWORK",
      "ERR_INTERNET_DISCONNECTED",
      "ERR_CONNECTION_REFUSED",
      "ERR_NAME_NOT_RESOLVED",
      "ERR_TIMED_OUT",
      "Loading chunk",
      "Loading CSS chunk",
      "Script error",
      "chrome-extension://",
      "moz-extension://",
      "Extension context invalidated",
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      "Uncaught SyntaxError", // Often from customer's site
      "Cannot read property", // Generic errors often from customer's site
      "null is not an object" // Generic errors often from customer's site
    ];

    // Skip network noise
    if (noisyNetworkErrors.some((pattern) => errorMessage.includes(pattern))) {
      return;
    }

    // Only process widget-related errors
    if (!isWidgetError(error, context)) {
      return;
    }

    // Send to Honeycomb for comprehensive monitoring
    const honeycombTracker = window.SienaWidgetHoneycomb;
    if (honeycombTracker) {
      honeycombTracker.addEvent("error.occurred", {
        error_message: errorMessage,
        error_name: error.name || "Error",
        error_stack: error.stack ? error.stack.substring(0, 1000) : "no stack",
        context_method: context.method || "unknown",
        context_action: context.action || "unknown",
        context_url: context.url || window.location.href,
        context_data: context.data
          ? JSON.stringify(context.data).substring(0, 500)
          : "none",
        error_severity: context.severity || "error",
        error_category: categorizeError(error, context),
        user_agent: navigator.userAgent,
        timestamp: Date.now()
      });
    }

    try {
      const dsnUrl = new URL(DSN);
      const projectId = dsnUrl.pathname.substring(1);
      const publicKey = dsnUrl.username;
      const sentryContext = window.SienaWidgetSentryContext || {};

      // Enhanced widget-specific error context
      const errorData = {
        message: errorMessage,
        stack: error.stack || new Error().stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      };

      const payload = {
        platform: "javascript",
        level: context.severity || "error",
        timestamp: Date.now() / 1000,
        environment: sentryContext.widget_environment || "production",
        release: `siena-widget@${sentryContext.widget_version || "1.0.0"}`,

        // Message and exception
        message: { message: errorData.message },
        exception: {
          values: [
            {
              type: error.name || "Error",
              value: errorData.message,
              stacktrace: { frames: parseStackTrace(errorData.stack) }
            }
          ]
        },

        // User context for filtering
        user: {
          id: sentryContext.user_id || "anonymous",
          username: sentryContext.user_id || "anonymous"
        },

        // Tags for filtering and organization
        tags: {
          component: "siena-chat-widget",
          widget_version: sentryContext.widget_version,
          app_key: sentryContext.app_key,
          error_category: categorizeError(error, context),
          is_mobile: sentryContext.is_mobile,
          environment: sentryContext.widget_environment,
          page_hostname: sentryContext.page_hostname
        },

        // Contexts for detailed information
        contexts: {
          widget: {
            widget_id: sentryContext.widget_id,
            version: sentryContext.widget_version,
            app_key: sentryContext.app_key,
            is_mobile: sentryContext.is_mobile,
            page_hostname: sentryContext.page_hostname
          },
          session: {
            session_uuid: sentryContext.session_uuid,
            conversation_uuid: sentryContext.conversation_uuid
          },
          runtime: {
            name: "siena-chat-widget",
            version: sentryContext.widget_version
          }
        },

        // Extra context from the error
        extra: {
          ...context,
          widget_context: sentryContext,
          error_source: "siena-widget",
          api_method: context.method,
          api_endpoint: context.url,
          request_data: context.requestData,
          response_status: context.status,
          response_data: context.responseData,
          widget_state: {
            currentSessionUuid: sentryContext.session_uuid,
            currentConversationUuid: sentryContext.conversation_uuid,
            currentUserId: sentryContext.user_id
          }
        },

        // Request info
        request: {
          url: window.location.href,
          headers: { "User-Agent": navigator.userAgent }
        }
      };

      // Send to Sentry
      fetch(`${dsnUrl.protocol}//${dsnUrl.host}/api/${projectId}/store/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Sentry-Auth": `Sentry sentry_version=7, sentry_client=siena-chat-widget/2.0, sentry_key=${publicKey}`
        },
        body: JSON.stringify(payload)
      }).catch(() => {
        // Silently fail - don't log to avoid recursive errors
      });
    } catch (err) {
      // Silently fail - don't log to avoid recursive errors
    }
  }

  // Check if error is widget-related
  function isWidgetError(error, context) {
    const errorMessage = error.message || String(error);
    const stack = error.stack || "";

    // Always capture if context indicates widget method
    if (
      context.method &&
      [
        "init",
        "initializeSienaChat",
        "fetchChatSettings",
        "fetchProductSuggestions",
        "handleAllMessages",
        "handleIframeMessage",
        "handleOpenWidget",
        "createWebchat",
        "handleAddToCart",
        "handleTagShopifyCart",
        "fetchProductDataByHandle"
      ].includes(context.method)
    ) {
      return true;
    }

    // Check if error originates from widget code
    const widgetIndicators = [
      "siena-chat-web",
      "SienaAIChat",
      "HoneycombWidgetTracker",
      "handleOpenWidget",
      "handleIframeMessage",
      "siena-iframe",
      "SIENA_CHAT_IFRAME",
      "SienaWidgetHoneycomb"
    ];

    return widgetIndicators.some(
      (indicator) =>
        stack.includes(indicator) || errorMessage.includes(indicator)
    );
  }

  // Enhanced stack trace parser
  function parseStackTrace(stack) {
    if (!stack) return [];

    return stack
      .split("\n")
      .slice(1)
      .map((line, index) => {
        const match =
          line.match(/at (.+?) \((.+?):(\d+):(\d+)\)/) ||
          line.match(/(.+?)@(.+?):(\d+):(\d+)/) ||
          line.match(/at (.+):(\d+):(\d+)/);

        if (match) {
          return {
            filename: match[2] || match[1],
            function: match[1] || "unknown",
            lineno: parseInt(match[3] || match[2]) || 0,
            colno: parseInt(match[4] || match[3]) || 0
          };
        }

        return {
          filename: "unknown",
          function: line.trim(),
          lineno: 0,
          colno: 0
        };
      });
  }

  function initializeSienaChat() {
    try {
      // Prevent duplicate initialization - a copy of this script may already
      // own the page instance, the iframe may exist, or init may be in flight
      if (
        window.__siena.widgetInstance ||
        document.getElementById("SIENA_CHAT_IFRAME") ||
        isInitializing
      ) {
        console.warn(
          "Siena chat widget already initialized or initialization in progress"
        );
        return;
      }

      // Set flag immediately to prevent race condition
      isInitializing = true;

      const scriptSrc = DEFAULT_CONFIG.SCRIPT_SRC;
      const scripts = document.querySelectorAll("script");
      const script = Array.from(scripts).find(
        (s) =>
          s.src.includes(scriptSrc) ||
          s.src.includes("/dist/webchat.js?appKey=")
      );
      if (!script) {
        isInitializing = false; // Reset flag on error
        sendErrorToSentry(new Error("Script tag not found"), {
          scriptSrc,
          method: "initializeSienaChat",
          scripts: Array.from(scripts)
            .map((s) => s.src)
            .join(", ")
        });
        return;
      }

      const urlParams = new URLSearchParams(script.src.split("?")[1]);
      const appKey = urlParams.get("appKey");
      if (checkString(appKey)) {
        injectSienaStyles();
        window.__siena.widgetInstance = new SienaAIChat();
        window.__siena.widgetInstance.init({ token: appKey });
        // Keep flag set - successful initialization
      } else {
        isInitializing = false; // Reset flag on error
        sendErrorToSentry(new Error("Invalid app key or missing app key"), {
          appKey,
          token: appKey,
          scriptSrc: script.src
        });
      }
    } catch (error) {
      isInitializing = false; // Reset flag on error
      window.__siena.widgetInstance = null;
      sendErrorToSentry(error, { method: "initializeSienaChat" });
    }
  }

  const checkString = (str) => {
    if (
      str === "undefined" ||
      str === "null" ||
      str === "NaN" ||
      str === "" ||
      !str
    )
      return null;
    return str;
  };

  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    initializeSienaChat();
  } else {
    document.addEventListener("DOMContentLoaded", initializeSienaChat);
  }

  // UTILS

  /**
   * Find Shopify domain using multiple fallback methods
   * @param {Document} doc — document
   * @return {string|null} — "example.myshopify.com" || null
   */
  function findShopifyDomain(doc = document) {
    // 1. window.Shopify.shop
    if (
      typeof window !== "undefined" &&
      window.Shopify &&
      typeof window.Shopify.shop === "string"
    ) {
      return window.Shopify.shop;
    }
    // 2. window.__st.a
    if (
      typeof window !== "undefined" &&
      window.__st &&
      typeof window.__st.a === "string"
    ) {
      return window.__st.a;
    }
    // 3. <link rel="canonical">
    const canonical = doc.querySelector('link[rel="canonical"]');
    if (canonical && canonical.href) {
      try {
        const url = new URL(canonical.href);
        if (url.hostname.endsWith(".myshopify.com")) {
          return url.hostname;
        }
      } catch (e) {
        return null;
      }
    }
    // 4. script[src*=".myshopify.com"]
    const scripts = Array.from(doc.querySelectorAll("script[src]"));
    for (const s of scripts) {
      try {
        const scriptUrl = new URL(s.src, location.href);
        if (scriptUrl.hostname.endsWith(".myshopify.com")) {
          return scriptUrl.hostname;
        }
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  // Widget-scoped global error handlers (only capture widget errors)
  window.addEventListener("error", function (event) {
    // Only capture widget-related errors, ignore page errors
    if (!isWidgetRelatedGlobalError(event)) {
      return;
    }

    // Track in Honeycomb
    if (window.SienaWidgetHoneycomb) {
      window.SienaWidgetHoneycomb.addEvent("error.global_js", {
        error_message: event.message,
        error_filename: event.filename,
        error_lineno: event.lineno,
        error_colno: event.colno,
        error_stack: event.error ? event.error.stack : "no stack",
        error_source: "widget-global-handler",
        timestamp: Date.now()
      });
    }

    // Send to Sentry with widget context
    if (event.error) {
      sendErrorToSentry(event.error, {
        method: "global_error_handler",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        severity: "error"
      });
    }
  });

  window.addEventListener("unhandledrejection", function (event) {
    // Only capture widget-related promise rejections
    if (!isWidgetRelatedPromiseRejection(event)) {
      return;
    }

    // Track in Honeycomb
    if (window.SienaWidgetHoneycomb) {
      window.SienaWidgetHoneycomb.addEvent("error.unhandled_promise", {
        error_message: event.reason
          ? event.reason.toString()
          : "Unknown promise rejection",
        error_stack:
          event.reason && event.reason.stack ? event.reason.stack : "no stack",
        error_source: "widget-promise-handler",
        timestamp: Date.now()
      });
    }

    // Send to Sentry with widget context
    if (event.reason) {
      const error =
        event.reason instanceof Error ? event.reason : new Error(event.reason);
      sendErrorToSentry(error, {
        method: "unhandled_promise_rejection",
        severity: "error"
      });
    }
  });

  // Check if global error is widget-related
  function isWidgetRelatedGlobalError(event) {
    const filename = event.filename || "";
    const stack = (event.error && event.error.stack) || "";
    const message = event.message || "";

    // Check if error originates from widget files
    const widgetFileIndicators = [
      "siena-chat-web.js",
      "webchat.js",
      "dist/index.html",
      "search-bar.html",
      "embedded-search.html"
    ];

    if (
      widgetFileIndicators.some((indicator) => filename.includes(indicator))
    ) {
      return true;
    }

    // Check if error contains widget-specific terms
    const widgetIndicators = [
      "SienaAIChat",
      "HoneycombWidgetTracker",
      "siena-iframe",
      "SIENA_CHAT_IFRAME",
      "SIENA_SEARCH_BAR_IFRAME",
      "SIENA_EMBEDDED_IFRAME",
      "handleOpenWidget",
      "handleIframeMessage",
      "handleAllMessages",
      "SienaWidgetHoneycomb"
    ];

    return widgetIndicators.some(
      (indicator) => stack.includes(indicator) || message.includes(indicator)
    );
  }

  // Check if promise rejection is widget-related
  function isWidgetRelatedPromiseRejection(event) {
    if (!event.reason) return false;

    const reasonString = event.reason.toString();
    const stack = (event.reason && event.reason.stack) || "";

    // Check for widget-specific terms in the rejection
    const widgetIndicators = [
      "SienaAIChat",
      "HoneycombWidgetTracker",
      "siena-iframe",
      "fetchChatSettings",
      "fetchProductSuggestions",
      "handleAddToCart",
      "handleTagShopifyCart",
      "cart/add.js",
      "cart/update.js",
      "siena-chat-web"
    ];

    return widgetIndicators.some(
      (indicator) =>
        stack.includes(indicator) || reasonString.includes(indicator)
    );
  }

  // Expose to global scope for manual initialization and client API
  global.SienaLiveChatWidget = {
    init: function (apiKeys) {
      // Prevent duplicate initialization - a copy of this script may already
      // own the page instance, the iframe may exist, or init may be in flight
      if (
        window.__siena.widgetInstance ||
        document.getElementById("SIENA_CHAT_IFRAME") ||
        isInitializing
      ) {
        console.warn(
          "Siena chat widget already initialized via global init or initialization in progress"
        );
        return window.__siena.widgetInstance;
      }
      isInitializing = true;
      window.__siena.widgetInstance = new SienaAIChat();
      window.__siena.widgetInstance.init(apiKeys);
      return window.__siena.widgetInstance;
    },

    /**
     * Launch the widget programmatically
     * @param {string} [message] - Optional message to send when widget opens
     * @param {string} [email] - Optional email to pre-set for the user
     */
    launch: function (message = "", email = "", name = "") {
      if (!window.__siena.widgetInstance) {
        console.warn(
          "Siena widget not initialized. Please ensure the widget script is loaded."
        );
        return;
      }
      return window.__siena.widgetInstance.launchWidget(message, email, name);
    },

    /**
     * Launch the widget in fullscreen mode
     * @param {string} [message] - Optional message to send when widget opens
     * @param {string} [email] - Optional email to pre-set for the user
     */
    launchFullscreen: function (message = "", email = "", name = "") {
      if (!window.__siena.widgetInstance) {
        console.warn(
          "Siena widget not initialized. Please ensure the widget script is loaded."
        );
        return;
      }
      return window.__siena.widgetInstance.launchFullScreenWidget(message, email, name);
    },

    /**
     * Get the current widget state ("open" or "closed")
     */
    get state() {
      if (!window.__siena.widgetInstance) return "closed";
      return window.__siena.widgetInstance.widgetState || "closed";
    },

    /**
     * Get the number of unread agent messages (0 unless the unread badge is enabled)
     */
    get unreadCount() {
      if (!window.__siena.widgetInstance) return 0;
      return window.__siena.widgetInstance.unreadCount || 0;
    },

    /**
     * Close the widget programmatically
     */
    close: function () {
      if (!window.__siena.widgetInstance || !window.__siena.widgetInstance.iframe) {
        console.warn(
          "Siena widget not initialized. Please ensure the widget script is loaded."
        );
        return;
      }
      // Send a close message to the iframe which triggers the normal close flow
      if (window.__siena.widgetInstance.iframe.contentWindow) {
        window.__siena.widgetInstance.iframe.contentWindow.postMessage(
          { action: "closeWidget" },
          "*"
        );
      }
    },

    /**
     * Check if widget is available and initialized
     */
    isReady: function () {
      return !!window.__siena.widgetInstance && !!window.__siena.widgetInstance.iframe;
    },

    /**
     * Get widget instance (for advanced usage)
     */
    getInstance: function () {
      return window.__siena.widgetInstance;
    },

    /**
     * HEADLESS SHOPIFY SUPPORT
     * Check if the widget detected a headless store configuration
     */
    isHeadlessStore: function () {
      if (!window.__siena.widgetInstance) return false;
      return window.__siena.widgetInstance.isHeadlessStore();
    },

    /**
     * HEADLESS SHOPIFY SUPPORT
     * Get the list of events that headless stores should listen to
     */
    getHeadlessEvents: function () {
      return {
        "siena:cart:add":
          "Fired when user clicks Add to Cart. Detail: { variantId, quantity, productInfo, resolve, reject }. IMPORTANT: Call detail.resolve() on success or detail.reject(error) on failure.",
        "siena:cart:tag":
          "Fired to tag cart with session info. Detail: { sessionUuid, conversationUuid, attributes }",
        "siena:cart:updated":
          "Fired after successful cart operation (native Shopify only). Detail: { cart, source, action }",
        "siena:product:fetch":
          "Fired when product data is needed. Detail: { handle, _productData }",
        "siena:currency:request":
          "Fired to request store currency. Detail: { callback }",
        "siena:widget:opened":
          "Fired when the chat widget is opened. Detail: { timestamp }",
        "siena:widget:closed":
          "Fired when the chat widget is closed. Detail: { timestamp }",
        "siena:conversation:started":
          "Fired when the first user message is sent in a conversation. Detail: { timestamp }"
      };
    }
  };

  /**
   * HEADLESS SHOPIFY SUPPORT
   * Global helper to set up headless store event handlers
   * Usage: SienaHeadlessSetup({ onAddToCart: (detail) => {...}, ... })
   *
   * For onAddToCart, the handler receives a detail object with:
   * - variantId: The Shopify variant ID to add
   * - quantity: Number of items to add
   * - productInfo: Optional product information
   * - resolve(result): Call this when cart addition succeeds
   * - reject(error): Call this when cart addition fails
   *
   * If your handler returns a Promise, resolve/reject will be called automatically.
   * If your handler is synchronous and doesn't call resolve/reject, resolve() is called automatically.
   */
  global.SienaHeadlessSetup = function (handlers = {}) {
    if (handlers.onAddToCart) {
      document.addEventListener("siena:cart:add", (event) => {
        const detail = event.detail;
        try {
          const result = handlers.onAddToCart(detail);

          // If handler returns a Promise, wait for it and auto-resolve/reject
          if (result && typeof result.then === "function") {
            result
              .then((res) => {
                // Only auto-resolve if handler didn't already call resolve/reject
                if (detail.resolve) {
                  detail.resolve(res || { success: true });
                }
              })
              .catch((err) => {
                if (detail.reject) {
                  detail.reject(err);
                }
              });
          } else {
            // Synchronous handler - auto-resolve if it didn't throw
            // Give handler a chance to call resolve/reject manually
            setTimeout(() => {
              if (detail.resolve) {
                detail.resolve(result || { success: true });
              }
            }, 0);
          }
        } catch (err) {
          // Handler threw synchronously
          if (detail.reject) {
            detail.reject(err);
          }
        }
      });
    }
    if (handlers.onTagCart) {
      document.addEventListener("siena:cart:tag", (event) => {
        handlers.onTagCart(event.detail);
      });
    }
    if (handlers.onProductFetch) {
      document.addEventListener("siena:product:fetch", (event) => {
        const result = handlers.onProductFetch(event.detail);
        if (result) {
          event.detail._productData = result;
        }
      });
    }
    if (handlers.onCurrencyRequest) {
      document.addEventListener("siena:currency:request", (event) => {
        const currency = handlers.onCurrencyRequest();
        if (currency && event.detail.callback) {
          event.detail.callback(currency);
        }
      });
    }
    console.log("[Siena] Headless event handlers registered");
  };

  // Also expose simple global functions for easy client access
  global.SienaLaunchWidget = function (message = "", email = "", name = "") {
    if (!window.__siena.widgetInstance) {
      console.warn(
        "Siena widget not initialized. Please ensure the widget script is loaded."
      );
      return;
    }
    return window.__siena.widgetInstance.launchWidget(message, email, name);
  };

  // Alias for backward compatibility
  global.SienaLaunchChat = function (message = "", email = "", name = "") {
    if (!window.__siena.widgetInstance) {
      console.warn(
        "Siena widget not initialized. Please ensure the widget script is loaded."
      );
      return;
    }
    return window.__siena.widgetInstance.launchWidget(message, email, name);
  };

  global.SienaLaunchFullscreen = function (message = "", email = "", name = "") {
    if (!window.__siena.widgetInstance) {
      console.warn(
        "Siena widget not initialized. Please ensure the widget script is loaded."
      );
      return;
    }
    return window.__siena.widgetInstance.launchFullScreenWidget(message, email, name);
  };
})(window);
